import type {
  ApprovalListView,
  ApprovalRequestListItemReadModel,
  ApprovalRequestReadModel,
  ApprovalRequestStatus,
  ApprovalStep,
  CreateManualReviewResolutionApprovalCommand,
  GroupBuyReconciliationSignoffPackage,
  InventoryCountProgressReadModel,
  ManualReviewListFilter,
  ManualReviewReadItem,
  MemberRechargeReconciliationOutcome,
  MemberRechargeReconciliationPreview,
  MemberRechargeReconciliationPreviewRequest,
  MemberRefundWalletAllocation,
  OperatorIdentity,
  OrderCashRefundApprovalPreviewReadModel,
  OrderHistoryReadModel,
  PaidOrderItemReturnApprovalPreviewReadModel,
  PaidSaleReturnOriginalPayment,
  SaleReturnPhysicalDisposition,
  ServiceWorkItemListReadModel,
  ServiceWorkItemReadModel,
  TableCleaningWorkItemCompletionReceipt,
} from "@billiard/contracts";
import { inventoryMovementTerm } from "@billiard/contracts";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AlertTriangle,
  Banknote,
  BellRing,
  CheckCircle2,
  Clock3,
  RefreshCw,
  ShieldCheck,
  XCircle,
} from "lucide-react";
import { useEffect, useId, useMemo, useRef, useState } from "react";
import { NavLink, useLocation, useNavigate, useParams } from "react-router-dom";
import {
  ApiRequestError,
  cancelApproval,
  completeTableCleaningWorkItem,
  confirmMemberRechargeReconciliation,
  createManualReviewResolutionApproval,
  createGroupBuyDailyReconciliationApproval,
  createGroupBuyReconciliationResolutionApproval,
  createInventoryOperationApproval,
  createMemberBalanceRefundApproval,
  createOrderCashRefundApproval,
  createPaidOrderItemReturnApproval,
  createPurchaseOrderApproval,
  decideApproval,
  executeApproval,
  executePaidOrderItemReturn,
  findGroupBuyDailyReconciliationApprovalForRecovery,
  findGroupBuyReconciliationResolutionApprovalForRecovery,
  findManualReviewResolutionApprovalForRecovery,
  findPurchaseOrderApprovalForRecovery,
  getApproval,
  getApprovalSummary,
  getGroupBuyReconciliationSignoff,
  getInventoryCountProgress,
  listApprovals,
  listManualReview,
  listPaidOrderItemReturnCandidates,
  listServiceWorkItems,
  previewMemberRechargeReconciliation,
  previewManualReviewResolution,
  previewOrderCashRefundApproval,
  previewPaidOrderItemReturnApproval,
  recoverGroupBuyDailyReconciliationApproval,
  recoverGroupBuyReconciliationResolutionApproval,
  recoverPurchaseOrderApproval,
  requestSecondaryAuthorization,
  retryMemberReceivableManualReview,
} from "./api";
import {
  type ApprovalExecutionRecovery,
  type ApprovalExecutionRecoveryView,
  approvalExecutionCommand,
  approvalExecutionOutcomeIsUnknown,
  approvalExecutionResultIsTerminal,
  beginApprovalExecutionDispatch,
  claimApprovalExecutionRecovery,
  clearApprovalExecutionRecovery,
  finishApprovalExecutionDispatch,
  markApprovalExecutionOutcomeUnknown,
  readApprovalExecutionRecovery,
} from "./approvalExecutionRecovery";
import {
  type ApprovalMutationCommand,
  type ApprovalMutationRecovery,
  type ApprovalMutationRecoveryView,
  approvalMutationIdentityKey,
  approvalMutationOutcomeIsUnknown,
  beginApprovalMutationDispatch,
  claimApprovalMutationRecovery,
  clearApprovalMutationRecovery,
  finishApprovalMutationDispatch,
  markApprovalMutationOutcomeUnknown,
  readApprovalMutationRecovery,
} from "./approvalMutationRecovery";
import { AutofillSafeInput } from "./autofillGuard";
import {
  beginMemberReceivableRetryDispatch,
  checkoutIdentityKey,
  claimMemberReceivableRetryRecovery,
  clearMemberReceivableRetryAfterDefiniteRejection,
  completeMemberReceivableRetryRecovery,
  finishMemberReceivableRetryDispatch,
  type MemberReceivableRetryRecovery,
  type MemberReceivableRetryRecoveryView,
  markMemberReceivableRetryOutcomeUnknown,
  memberReceivableRetryCommand,
  memberReceivableRetryOutcomeIsUnknown,
  readMemberReceivableRetryRecovery,
} from "./checkoutRecovery";
import {
  ManualReviewResolutionPanel,
  type ManualReviewResolutionItem,
  type ManualReviewResolutionPreview,
} from "./ManualReviewResolutionPanel";
import {
  beginManualReviewResolutionDispatch,
  claimManualReviewResolutionRecovery,
  clearManualReviewResolutionRecovery,
  finishManualReviewResolutionDispatch,
  manualReviewResolutionIdentityKey,
  manualReviewResolutionRecoveryOutcomeIsUnknown,
  markManualReviewResolutionOutcomeUnknown,
  readManualReviewResolutionRecovery,
  type ManualReviewResolutionRecovery,
  type ManualReviewResolutionRecoveryView,
} from "./manualReviewResolutionRecovery";
import {
  advanceMemberRechargeRecoveryToConfirmation,
  beginMemberRechargeDispatch,
  claimMemberRechargeRecovery,
  clearMemberRechargeRecoveryAfterDefiniteRejection,
  completeMemberRechargeRecovery,
  discardUnconfirmedMemberRechargeReconciliation,
  finishMemberRechargeDispatch,
  type MemberRechargeRecovery,
  type MemberRechargeRecoveryView,
  markMemberRechargeOutcomeUnknown,
  memberRechargeOutcomeIsUnknown,
  memberRechargeReconciliationPreviewCommand,
  memberRechargeRecoveryContext,
  readMemberRechargeRecovery,
} from "./memberRechargeRecovery";
import { orderDisplayReference } from "./orderDisplayNumber";
import {
  beginPaidOrderItemReturnExecutionDispatch,
  claimPaidOrderItemReturnExecutionRecovery,
  clearPaidOrderItemReturnExecutionRecovery,
  finishPaidOrderItemReturnExecutionDispatch,
  markPaidOrderItemReturnExecutionOutcomeUnknown,
  type PaidOrderItemReturnExecutionRecovery,
  type PaidOrderItemReturnExecutionRecoveryView,
  paidOrderItemReturnExecutionOutcomeIsUnknown,
  readPaidOrderItemReturnExecutionRecovery,
  settlePaidOrderItemReturnExecutionRecovery,
} from "./paidOrderItemReturnExecutionRecovery";
import {
  KioskProductOrderWorkItemPanel,
  ServiceDutyWorkCenterPanel,
  serviceWorkItemsQueryKey,
} from "./ServiceDutyDesk";
import {
  beginTableCleaningWorkItemDispatch,
  claimTableCleaningWorkItemRecovery,
  clearTableCleaningWorkItemRecovery,
  finishTableCleaningWorkItemDispatch,
  markTableCleaningWorkItemOutcomeUnknown,
  readTableCleaningWorkItemRecovery,
  type TableCleaningWorkItemFrozenCommand,
  type TableCleaningWorkItemRecovery,
  type TableCleaningWorkItemRecoveryView,
  tableCleaningWorkItemIdentityKey,
  tableCleaningWorkItemOutcomeIsUnknown,
} from "./tableCleaningWorkItemRecovery";

type OperatorRole = OperatorIdentity["role"];
type WorkCenterView = ApprovalListView | "SERVICE" | "MANUAL";
type ManualReviewFilterSelection =
  | { status: "ALL"; filter: null }
  | { status: "FILTERED"; filter: ManualReviewListFilter }
  | { status: "INVALID"; filter: null };

function isRealBusinessDate(value: string | null): value is string {
  if (!value || !/^\d{4}-\d{2}-\d{2}$/u.test(value)) return false;
  const parsed = new Date(`${value}T00:00:00.000Z`);
  return Number.isFinite(parsed.getTime()) && parsed.toISOString().slice(0, 10) === value;
}

function manualReviewFilterFromSearch(search: string): ManualReviewFilterSelection {
  const params = new URLSearchParams(search);
  const categories = params.getAll("category");
  const businessDates = params.getAll("businessDate");
  if (categories.length === 0 && businessDates.length === 0) {
    return { status: "ALL", filter: null };
  }
  if ([...params.entries()].length !== 3) return { status: "INVALID", filter: null };
  if (
    params.getAll("view").length !== 1 ||
    params.get("view") !== "MANUAL" ||
    categories.length !== 1 ||
    params.get("category") !== "MEMBER_RECEIVABLE" ||
    businessDates.length !== 1
  ) {
    return { status: "INVALID", filter: null };
  }
  const businessDate = params.get("businessDate");
  return isRealBusinessDate(businessDate)
    ? {
        status: "FILTERED",
        filter: { category: "MEMBER_RECEIVABLE", businessDate },
      }
    : { status: "INVALID", filter: null };
}

const approvalStatusLabels: Record<ApprovalRequestStatus, string> = {
  PENDING_MANAGER_OR_OWNER: "等待店长或老板审批",
  PENDING_MANAGER: "等待店长审批",
  PENDING_OWNER: "等待老板审批",
  APPROVED: "已批准",
  REJECTED: "已驳回",
  CANCELLED: "已撤回",
};

const listTabs: ReadonlyArray<{
  id: WorkCenterView;
  label: string;
  summaryKey: "toDecideCount" | "myPendingCount" | "historyCount" | "serviceCount" | null;
}> = [
  { id: "TO_DECIDE", label: "现在要处理", summaryKey: "toDecideCount" },
  { id: "MINE", label: "我的申请", summaryKey: "myPendingCount" },
  { id: "SERVICE", label: "现场待办", summaryKey: "serviceCount" },
  { id: "MANUAL", label: "异常待核对", summaryKey: null },
  { id: "HISTORY", label: "全部记录", summaryKey: "historyCount" },
];

function fee(fen: number): string {
  const exactFen = BigInt(fen);
  const negative = exactFen < 0n;
  const absoluteFen = negative ? -exactFen : exactFen;
  const yuan = absoluteFen / 100n;
  const remainderFen = (absoluteFen % 100n).toString().padStart(2, "0");
  return `¥${negative ? "-" : ""}${yuan}.${remainderFen}`;
}

function memberRechargeYuanInputFromFen(fen: number | null): string {
  return fen === null ? "" : fee(fen).slice(1);
}

interface PaidReturnExecutionEvidenceInput {
  externalRefundReference: string | null;
  externalRefundReferences?: Array<{
    paymentId: string;
    externalRefundReference: string;
  }>;
}

function paidReturnPaymentMethodLabel(method: PaidSaleReturnOriginalPayment["method"]): string {
  switch (method) {
    case "CASH":
      return "现金";
    case "STATIC_WECHAT":
      return "微信静态码";
    case "STATIC_ALIPAY":
      return "支付宝静态码";
    case "MEMBER_BALANCE":
      return "会员余额";
    case "WECHAT_PAY":
      return "微信官方支付";
    case "ALIPAY":
      return "支付宝官方支付";
  }
}

function paidReturnAllocationConsequence(allocation: PaidSaleReturnOriginalPayment): string {
  switch (allocation.executionMode) {
    case "LOCAL_CASH_CONFIRMATION":
      return "收银员当面退还这一路现金并勾选确认，系统才会提交整笔商品退回。";
    case "MANUAL_STATIC_CODE_CONFIRMATION":
      return "先在原静态码渠道完成这一路退款，再填写该渠道自己的退款凭证。";
    case "MEMBER_BALANCE_CLOUD_REFUND":
      return "系统联网查询并退回原会员余额，不要求手工填写凭证。";
    case "OFFICIAL_PROVIDER_REFUND":
      return "系统查询原官方支付渠道并原路退款，不要求手工填写凭证。";
  }
}

function PaidReturnAllocationFacts({
  allocations,
  totalFen,
}: {
  allocations: readonly PaidSaleReturnOriginalPayment[];
  totalFen: number;
}): React.JSX.Element {
  return (
    <section className="approval-steps" aria-label="组合支付逐路退款分配">
      <h3>组合支付逐路退款</h3>
      <p className="field-help">
        退款金额由服务端按原收款累计比例和最大余数统一分配，页面不能修改。各路本次退款合计{" "}
        {fee(totalFen)}，与商品退款总额一致。
      </p>
      {allocations.map((allocation, index) => (
        <article className="approval-step" key={allocation.paymentId}>
          <span>{index + 1}</span>
          <div>
            <strong>{paidReturnPaymentMethodLabel(allocation.method)}</strong>
            <p>
              原收款 {fee(allocation.paymentAmountFen)} · 已退 {fee(allocation.alreadyRefundedFen)}{" "}
              · 尚可退 {fee(allocation.availableFen)} · 本次退 {fee(allocation.refundAmountFen)}
            </p>
            <small>{paidReturnAllocationConsequence(allocation)}</small>
          </div>
        </article>
      ))}
    </section>
  );
}

function memberRefundWalletUnits(allocation: MemberRefundWalletAllocation): string {
  if (allocation.unit === "CNY_FEN") return fee(allocation.refundUnits);
  const value = allocation.refundUnits / 10 ** allocation.precision;
  const formatted = value.toFixed(allocation.precision);
  const unit =
    allocation.unit === "POINT"
      ? "积分"
      : allocation.unit === "MINUTE"
        ? "分钟"
        : allocation.unit === "COUNT"
          ? "次"
          : (allocation.customUnitLabel ?? "份");
  return `${formatted} ${unit}`;
}

function MemberRefundAllocationFacts({
  allocations,
  totalFen,
}: {
  allocations: readonly MemberRefundWalletAllocation[];
  totalFen: number;
}): React.JSX.Element {
  return (
    <section className="approval-steps" aria-label="会员余额退款权益去向">
      <h3>按原消费路线退回</h3>
      <p className="field-help">
        这份分配已随审批固化，执行时服务端会再次核对；各项对应退款金额合计 {fee(totalFen)}。
      </p>
      {allocations.map((allocation, index) => (
        <article className="approval-step" key={allocation.walletDefinitionId}>
          <span>{index + 1}</span>
          <div>
            <strong>{allocation.walletName}</strong>
            <p>
              退回 {memberRefundWalletUnits(allocation)} · 对应退款{" "}
              {fee(allocation.refundAmountFen)}
            </p>
          </div>
        </article>
      ))}
    </section>
  );
}

type InventoryApprovalSnapshot = Extract<
  ApprovalRequestReadModel["snapshot"],
  { kind: "INVENTORY_OPERATION" }
>;

type PurchaseOrderApprovalSnapshot = Extract<
  ApprovalRequestReadModel["snapshot"],
  { kind: "PURCHASE_ORDER_APPROVAL" }
>;

type GroupBuyDailyReconciliationApprovalSnapshot = Extract<
  ApprovalRequestReadModel["snapshot"],
  { kind: "GROUP_BUY_DAILY_RECONCILIATION" }
>;

type GroupBuyReconciliationResolutionApprovalSnapshot = Extract<
  ApprovalRequestReadModel["snapshot"],
  { kind: "GROUP_BUY_RECONCILIATION_RESOLUTION" }
>;

type CashShiftDifferenceApprovalSnapshot = Extract<
  ApprovalRequestReadModel["snapshot"],
  { kind: "CASH_SHIFT_DIFFERENCE" }
>;

function groupBuyProviderLabel(
  provider: GroupBuyDailyReconciliationApprovalSnapshot["provider"],
): string {
  return provider === "MEITUAN" ? "美团" : "抖音";
}

function groupBuyReconciliationOutcomeLabel(
  outcome: GroupBuyDailyReconciliationApprovalSnapshot["matches"][number]["outcome"],
): string {
  switch (outcome) {
    case "CONFIRMED":
      return "金额一致";
    case "NOT_SETTLED":
      return "平台尚未结算";
    case "AMOUNT_MISMATCH":
      return "金额不一致";
    case "DUPLICATE_RECORD":
      return "平台存在重复记录";
  }
}

function inventoryOperationLabel(
  operationType: InventoryApprovalSnapshot["operationType"],
): string {
  switch (operationType) {
    case "PURCHASE_IN":
      return "采购入库";
    case "OTHER_IN":
      return "其他入库";
    case "RETURN_OUT":
      return "退货出库";
    case "LOSS_OUT":
      return "报损出库";
    case "INTERNAL_USE_OUT":
      return "领用出库";
    case "COUNT_SET":
      return "库存盘点";
    case "MANUAL_ADJUSTMENT":
      return "人工调整";
  }
}

function inventoryQuantity(quantity: number, unit: string): string {
  return `${quantity} ${unit}`;
}

function inventoryQuantityDelta(quantity: number, unit: string): string {
  return `${quantity > 0 ? "+" : ""}${quantity} ${unit}`;
}

function signedFee(fen: number): string {
  return fen > 0 ? `+${fee(fen)}` : fen < 0 ? `-${fee(-fen)}` : fee(0);
}

function cashShiftDifferenceRate(
  rateBasisPoints: CashShiftDifferenceApprovalSnapshot["assessment"]["differenceRateBasisPointsCeiling"],
): string {
  if (rateBasisPoints === null) return "应有现金为 0，比例不适用，按最高风险处理";
  const percentage = (rateBasisPoints / 100).toFixed(2);
  return `${percentage}%`;
}

function cashShiftDifferenceTierLabel(
  tier:
    | CashShiftDifferenceApprovalSnapshot["assessment"]["tier"]
    | Extract<
        ApprovalRequestListItemReadModel["summary"],
        { kind: "CASH_SHIFT_DIFFERENCE" }
      >["tier"],
): string {
  switch (tier) {
    case "BALANCED":
      return "账实一致，无需审批";
    case "MANAGER_NOTICE":
      return "小额差异，通知店长";
    case "MANAGER_OR_OWNER_APPROVAL":
      return "较大差异，另一位店长或老板审批";
    case "OWNER_APPROVAL":
      return "高风险差异，只能老板审批";
  }
}

function CashShiftDifferenceApprovalFacts({
  snapshot,
}: {
  snapshot: CashShiftDifferenceApprovalSnapshot;
}): React.JSX.Element {
  const assessment = snapshot.assessment;
  return (
    <>
      <div>
        <span>现金账实差异</span>
        <strong>{signedFee(assessment.cashDifferenceFen)}</strong>
      </div>
      <dl>
        <div>
          <dt>营业日</dt>
          <dd>{snapshot.businessDate}</dd>
        </div>
        <div>
          <dt>备用金</dt>
          <dd>{fee(snapshot.openingCashFen)}</dd>
        </div>
        <div>
          <dt>现金收入</dt>
          <dd>{fee(snapshot.cashReceiptsFen)}</dd>
        </div>
        <div>
          <dt>现金退款</dt>
          <dd>{fee(snapshot.cashRefundsFen)}</dd>
        </div>
        <div>
          <dt>应有现金</dt>
          <dd>{fee(assessment.expectedCashFen)}</dd>
        </div>
        <div>
          <dt>现场实盘</dt>
          <dd>{fee(assessment.countedCashFen)}</dd>
        </div>
        <div>
          <dt>账实差异</dt>
          <dd>{signedFee(assessment.cashDifferenceFen)}</dd>
        </div>
        <div>
          <dt>差异比例</dt>
          <dd>{cashShiftDifferenceRate(assessment.differenceRateBasisPointsCeiling)}</dd>
        </div>
        <div>
          <dt>风险档位</dt>
          <dd>{cashShiftDifferenceTierLabel(assessment.tier)}</dd>
        </div>
        <div>
          <dt>开班责任人</dt>
          <dd>{snapshot.openedBy.displayName}</dd>
        </div>
        <div>
          <dt>实际交班人</dt>
          <dd>
            {snapshot.handoverActor.displayName}（
            {snapshot.handoverMode === "OPENING_CASHIER" ? "开班收银员本人" : "店长代办"}）
          </dd>
        </div>
        <div>
          <dt>实际接班人</dt>
          <dd>
            {snapshot.receiver.displayName}（
            {snapshot.receiveMode === "NEXT_CASHIER" ? "下一位收银员" : "店长代办"}）
          </dd>
        </div>
        <div>
          <dt>交班说明</dt>
          <dd>{snapshot.handoverNote}</dd>
        </div>
        <div>
          <dt>接班说明</dt>
          <dd>{snapshot.receiveNote}</dd>
        </div>
        {snapshot.handoverSubstituteReason ? (
          <div>
            <dt>代办交班原因</dt>
            <dd>{snapshot.handoverSubstituteReason}</dd>
          </div>
        ) : null}
        {snapshot.receiveSubstituteReason ? (
          <div>
            <dt>代办接班原因</dt>
            <dd>{snapshot.receiveSubstituteReason}</dd>
          </div>
        ) : null}
        <div>
          <dt>原业务记录</dt>
          <dd>{snapshot.originalDocumentLabel}</dd>
        </div>
      </dl>
    </>
  );
}

function InventoryCountProgressPanel({
  progress,
  loading,
  error,
  onRetry,
}: {
  progress: InventoryCountProgressReadModel | null;
  loading: boolean;
  error: boolean;
  onRetry: () => void;
}): React.JSX.Element {
  if (error) {
    return (
      <section className="approval-warning" role="alert" aria-label="盘点期间变化读取失败">
        <strong>盘点期间变化暂时无法安全读取，旧进度已隐藏，本次不能执行</strong>
        <p>请重新读取；仍失败时保留审批编号，请店长核对期间库存流水。</p>
        <button className="button secondary compact" type="button" onClick={onRetry}>
          <RefreshCw size={15} aria-hidden="true" /> 重新读取盘点进度
        </button>
      </section>
    );
  }
  if (loading || !progress) {
    return <section className="empty-state">正在核对盘点后的销售、收货和其他库存变化</section>;
  }
  const movementsTruncated = progress.periodMovementCount === 501;
  return (
    <section className="approval-count-progress" aria-label="库存盘点期间变化与预计执行结果">
      <header>
        <div>
          <span>盘点单号</span>
          <strong>{progress.capture.countNumber}</strong>
        </div>
        <span>
          {movementsTruncated
            ? "库存变化超过 500 笔，不能执行"
            : progress.canExecute
              ? "期间变化已核对，可以执行"
              : "当前不能执行"}
        </span>
      </header>
      {movementsTruncated ? (
        <div className="approval-warning" role="alert">
          <strong>最早500笔，完整合计不可用，请重新盘点</strong>
          <p>
            页面只显示申请后的最早 500
            笔库存变化，后续变化未包含在下方小计中；当前库存、预计执行后数量和价值结果已隐藏，不能据此执行。
          </p>
        </div>
      ) : null}
      <dl className="approval-card-facts">
        <div>
          <dt>盘点时理论库存</dt>
          <dd>{inventoryQuantity(progress.capture.theoreticalQuantity, progress.unit)}</dd>
        </div>
        <div>
          <dt>现场实盘</dt>
          <dd>{inventoryQuantity(progress.capture.countedQuantity, progress.unit)}</dd>
        </div>
        <div>
          <dt>原账实差</dt>
          <dd>{inventoryQuantityDelta(progress.capture.differenceQuantity, progress.unit)}</dd>
        </div>
        <div>
          <dt>{movementsTruncated ? "最早500笔变化小计（非完整合计）" : "等待期间合计变化"}</dt>
          <dd>{inventoryQuantityDelta(progress.periodMovementDelta, progress.unit)}</dd>
        </div>
        <div>
          <dt>期间库存变化</dt>
          <dd>
            {progress.periodMovementCount === 501
              ? "超过 500 笔（页面只显示最早 500 笔）"
              : `期间共 ${progress.periodMovementCount} 笔`}
          </dd>
        </div>
        <div>
          <dt>当前理论库存</dt>
          <dd>
            {movementsTruncated
              ? "不显示：完整期间合计不可用"
              : inventoryQuantity(progress.currentTheoreticalQuantity, progress.unit)}
          </dd>
        </div>
        <div>
          <dt>预计执行后数量</dt>
          <dd>
            {movementsTruncated
              ? "不显示：完整期间合计不可用"
              : inventoryQuantity(progress.projectedQuantityAfterApply, progress.unit)}
          </dd>
        </div>
        <div>
          <dt>有效桌边预留</dt>
          <dd>{inventoryQuantity(progress.activeReservedQuantity, progress.unit)}</dd>
        </div>
        <div>
          <dt>当前库存价值</dt>
          <dd>
            {movementsTruncated
              ? "不显示：完整期间合计不可用"
              : progress.currentInventoryValueFen === null
                ? "暂时无法安全确认"
                : fee(progress.currentInventoryValueFen)}
          </dd>
        </div>
        <div>
          <dt>库存价值核对</dt>
          <dd>
            {movementsTruncated
              ? "不显示：完整期间合计不可用"
              : progress.currentInventoryValuationVersion === null
                ? "暂时无法安全确认"
                : `已按当前库存重新核对（记录第 ${progress.currentInventoryValuationVersion} 版）`}
          </dd>
        </div>
        <div>
          <dt>预计执行后价值</dt>
          <dd>
            {movementsTruncated
              ? "不显示：完整期间合计不可用"
              : progress.projectedInventoryValueFen === null
                ? "暂时无法安全确认"
                : fee(progress.projectedInventoryValueFen)}
          </dd>
        </div>
        <div>
          <dt>预计价值变化</dt>
          <dd>
            {movementsTruncated
              ? "不显示：完整期间合计不可用"
              : progress.inventoryValueDeltaFen === null
                ? "暂时无法安全确认"
                : fee(progress.inventoryValueDeltaFen)}
          </dd>
        </div>
      </dl>
      <p className="field-help">
        {movementsTruncated
          ? "下列仅为申请后的最早 500 笔变化，不是完整期间流水，也不能据此计算当前库存。"
          : "盘点申请保留的是当时账实差。等待审批期间的正常销售、收货和桌边商品送达不会丢失，执行时会把下列变化逐笔带入。"}
      </p>
      {progress.periodMovements.length ? (
        <ol
          className="approval-count-movements"
          aria-label={movementsTruncated ? "盘点等待期间最早500笔库存变化" : "盘点等待期间库存变化"}
        >
          {progress.periodMovements.map((movement) => {
            const movementTerm = inventoryMovementTerm(movement.movementType);
            return (
              <li key={movement.movementId}>
                <strong>
                  {movementTerm.label} ·{" "}
                  {inventoryQuantityDelta(movement.quantityDelta, progress.unit)}
                </strong>
                <span>
                  {inventoryQuantity(movement.quantityBefore, progress.unit)} →{" "}
                  {inventoryQuantity(movement.quantityAfter, progress.unit)} ·{" "}
                  {time(movement.occurredAt)}
                </span>
                <small>
                  {movement.reason}
                  {movement.referenceNumber ? ` · 单号 ${movement.referenceNumber}` : ""}
                </small>
                <small>
                  {movementTerm.explanation} {movementTerm.nextStep}
                </small>
              </li>
            );
          })}
        </ol>
      ) : (
        <p className="field-help">申请后尚无新的库存变化。</p>
      )}
      {progress.blocker ? (
        <div className="approval-warning" role="alert">
          <strong>当前不能执行盘点</strong>
          <p>{progress.blocker}</p>
          <p>
            {movementsTruncated
              ? "期间变化已超过可安全展示和执行的范围，请重新盘点；系统不会使用不完整合计覆盖库存。"
              : "请先按提示处理库存价值或有效桌边预留，再重新读取本页；系统不会强行覆盖。"}
          </p>
        </div>
      ) : null}
    </section>
  );
}

function approvalOperationName(snapshot: ApprovalRequestReadModel["snapshot"]): string {
  switch (snapshot.kind) {
    case "ORDER_CASH_REFUND":
      return "现金退款";
    case "MEMBER_BALANCE_REFUND":
      return "余额退款";
    case "ORDER_ITEM_PAID_RETURN":
      return snapshot.itemKind === "PACKAGE" ? "已结账套餐整包退回" : "已结账商品退回";
    case "INVENTORY_OPERATION":
      return inventoryOperationLabel(snapshot.operationType);
    case "PURCHASE_ORDER_APPROVAL":
      return "采购单审批";
    case "GROUP_BUY_DAILY_RECONCILIATION":
      return "团购日对账";
    case "GROUP_BUY_RECONCILIATION_RESOLUTION":
      return "团购差异处置";
    case "CASH_SHIFT_DIFFERENCE":
      return "关闭现金班次";
    case "MANUAL_REVIEW_RESOLUTION":
      return "待复核事项处置";
  }
}

function approvalListBusinessSummary(summary: ApprovalRequestListItemReadModel["summary"]): string {
  switch (summary.kind) {
    case "ORDER_CASH_REFUND":
    case "MEMBER_BALANCE_REFUND":
    case "ORDER_ITEM_PAID_RETURN":
      return fee(summary.amountFen);
    case "INVENTORY_OPERATION":
      return `${inventoryOperationLabel(summary.operationType)} · 变化 ${inventoryQuantityDelta(
        summary.quantityDelta,
        summary.unit,
      )} · 执行后 ${inventoryQuantity(summary.quantityAfter, summary.unit)}`;
    case "PURCHASE_ORDER_APPROVAL":
      return `${summary.supplierName} · ${summary.lineCount} 项 · 小计 ${fee(summary.subtotalFen)} · 税额 ${fee(summary.taxFen)} · 合计 ${fee(summary.totalFen)}`;
    case "GROUP_BUY_DAILY_RECONCILIATION":
      return `${groupBuyProviderLabel(summary.provider)} · ${summary.businessDate} · 核对 ${summary.fulfillmentCount} 笔 · 一致 ${summary.confirmedCount} 笔 · 差异 ${summary.exceptionCount} 笔 · 平台实结 ${fee(summary.actualSettlementFen)}`;
    case "GROUP_BUY_RECONCILIATION_RESOLUTION":
      return `${groupBuyProviderLabel(summary.provider)} · ${summary.businessDate} · 处置 ${summary.exceptionCount} 笔 · 平台应收 ${fee(summary.platformReceivableFen)} · 结算调整 ${fee(summary.settlementAdjustmentFen)}`;
    case "CASH_SHIFT_DIFFERENCE":
      return `${summary.businessDate} · 应有 ${fee(summary.expectedCashFen)} · 实盘 ${fee(summary.countedCashFen)} · 差异 ${signedFee(summary.cashDifferenceFen)} · ${cashShiftDifferenceTierLabel(summary.tier)}`;
    case "MANUAL_REVIEW_RESOLUTION":
      return `${manualReviewCategoryLabel(summary.category)} · ${manualReviewResolutionCodeLabel(summary.resolutionCode)}${summary.amountFen === null ? "" : ` · ${fee(summary.amountFen)}`}`;
  }
}

function invalidateCashShiftDifferenceReaders(
  queryClient: ReturnType<typeof useQueryClient>,
  operator: OperatorIdentity,
  approval: ApprovalRequestReadModel,
): Promise<unknown[]> {
  if (approval.snapshot.kind !== "CASH_SHIFT_DIFFERENCE") return Promise.resolve([]);
  const { businessDate, shiftId } = approval.snapshot;
  return Promise.all([
    queryClient.invalidateQueries({ queryKey: ["cash-shifts"] }),
    queryClient.invalidateQueries({ queryKey: ["cash-shift-handover-workflow", shiftId] }),
    queryClient.invalidateQueries({
      queryKey: ["store-closing-readiness", operator.tenantId, operator.storeId],
    }),
    queryClient.invalidateQueries({
      queryKey: ["owner-operating-dashboard", operator.tenantId, operator.storeId, businessDate],
    }),
    queryClient.invalidateQueries({
      queryKey: ["business-metric-report", operator.tenantId, operator.storeId, businessDate],
    }),
    queryClient.invalidateQueries({
      queryKey: [
        "business-metric-report-bundle",
        operator.tenantId,
        operator.storeId,
        businessDate,
      ],
    }),
    queryClient.invalidateQueries({
      queryKey: ["cash-difference-business-metric-detail", operator.tenantId, operator.storeId],
    }),
  ]);
}

function time(value: string): string {
  return new Date(value).toLocaleString("zh-CN", { hour12: false });
}

function failureMessage(error: unknown, fallback: string): string {
  return error instanceof Error ? error.message : fallback;
}

function manualReviewCategoryLabel(category: ManualReviewReadItem["category"]): string {
  switch (category) {
    case "PAYMENT_RECONCILIATION":
      return "付款核对";
    case "ORDER_SYNC_REVIEW":
      return "订单记录核对";
    case "GROUP_BUY_REDEMPTION_REVIEW":
      return "团购券核销核对";
    case "COACH_SERVICE_REVIEW":
      return "助教服务核对";
    case "MEMBER_RECHARGE_RECONCILIATION":
      return "会员充值核对";
    case "MEMBER_RECEIVABLE":
      return "会员余额待扣";
    case "CUSTOMER_STORAGE_EXCEPTION":
      return "顾客寄存异常";
    case "CUSTOMER_RENTAL_EXCEPTION":
      return "顾客租赁异常";
  }
}

function manualReviewResolutionCodeLabel(
  code: Extract<
    ApprovalRequestReadModel["snapshot"],
    { kind: "MANUAL_REVIEW_RESOLUTION" }
  >["resolutionCode"],
): string {
  switch (code) {
    case "MANUALLY_RECONCILED":
      return "人工确认已到账";
    case "NOT_RECEIVED":
      return "确认未到账";
    case "AMOUNT_MISMATCH":
      return "金额不一致";
    case "DUPLICATE_PAYMENT":
      return "重复付款凭证";
    case "KEEP_ISOLATED":
      return "继续隔离";
    case "TRANSFER_TO_FINANCE":
      return "转交财务复核";
    case "TRANSFER_TO_OPERATIONS":
      return "转交营运复核";
  }
}

function ManualReviewResolutionApprovalFacts({
  snapshot,
}: {
  snapshot: Extract<ApprovalRequestReadModel["snapshot"], { kind: "MANUAL_REVIEW_RESOLUTION" }>;
}): React.JSX.Element {
  return (
    <>
      <div>
        <span>申请处理方式</span>
        <strong>{manualReviewResolutionCodeLabel(snapshot.resolutionCode)}</strong>
      </div>
      <dl>
        <div>
          <dt>事项类型</dt>
          <dd>{manualReviewCategoryLabel(snapshot.category)}</dd>
        </div>
        <div>
          <dt>原业务记录</dt>
          <dd>{snapshot.originalDocumentLabel}</dd>
        </div>
        <div>
          <dt>当前原因</dt>
          <dd>{snapshot.currentReason}</dd>
        </div>
        <div>
          <dt>申请说明</dt>
          <dd>{snapshot.note}</dd>
        </div>
        <div>
          <dt>涉及金额</dt>
          <dd>{snapshot.amountFen === null ? "需要核对" : fee(snapshot.amountFen)}</dd>
        </div>
      </dl>
      <div className="approval-consequence-grid">
        <section>
          <h4>批准并安全执行后</h4>
          <ul>
            {snapshot.consequences.map((value) => (
              <li key={value}>{value}</li>
            ))}
          </ul>
        </section>
        <section>
          <h4>后续步骤</h4>
          <ol>
            {snapshot.nextSteps.map((value) => (
              <li key={value}>{value}</li>
            ))}
          </ol>
        </section>
      </div>
    </>
  );
}

function groupBuyManualReviewKindLabel(
  reviewKind: Extract<
    ManualReviewReadItem,
    { category: "GROUP_BUY_REDEMPTION_REVIEW" }
  >["reviewKind"],
): string {
  if (reviewKind === "DAILY_RECONCILIATION_DIFFERENCE") return "团购日结差异";
  return reviewKind === "OFFLINE_PRECHECK_REJECTED" ? "离线核销预检驳回" : "核销补偿结果待确认";
}

function customerRentalExceptionLabel(
  exceptionType: Extract<
    ManualReviewReadItem,
    { category: "CUSTOMER_RENTAL_EXCEPTION" }
  >["exceptionType"],
): string {
  switch (exceptionType) {
    case "LOST":
      return "丢失";
    case "DAMAGED":
      return "损坏";
    case "OVERDUE":
      return "逾期";
  }
}

function activeStep(approval: ApprovalRequestReadModel): ApprovalStep | null {
  return approval.steps.find((step) => step.status === "AVAILABLE") ?? null;
}

function executionLabel(approval: ApprovalRequestReadModel): string {
  const operationName = approvalOperationName(approval.snapshot);
  switch (approval.execution.status) {
    case "AWAITING_APPROVAL":
      return approval.snapshot.kind === "INVENTORY_OPERATION"
        ? "审批完成前不会变更库存"
        : approval.snapshot.kind === "PURCHASE_ORDER_APPROVAL"
          ? "采购单正在等待审批，当前不能收货"
          : approval.snapshot.kind === "GROUP_BUY_DAILY_RECONCILIATION"
            ? "团购日结单正在等待审批，当前不会更新任何核对结果"
            : approval.snapshot.kind === "GROUP_BUY_RECONCILIATION_RESOLUTION"
              ? "团购差异处置正在等待审批，当前不会新增平台应收或结算调整"
              : approval.snapshot.kind === "CASH_SHIFT_DIFFERENCE"
                ? "现金差异正在等待审批，班次保持未关闭"
                : approval.snapshot.kind === "ORDER_ITEM_PAID_RETURN"
                  ? approval.snapshot.itemKind === "PACKAGE"
                    ? "审批完成并安全执行前，不会整包退款、减少订单、恢复库存或撤销本单服务权益"
                    : "审批完成并安全执行前，不会退款、减少订单或处理退回实物"
                  : "审批完成前不会退款";
    case "NOT_APPLICABLE":
      return approval.snapshot.kind === "CASH_SHIFT_DIFFERENCE"
        ? approval.status === "REJECTED"
          ? "审批已驳回，班次已重新开放盘点，请回交接班工作台处理"
          : "申请已撤回，班次仍未关闭，请回交接班工作台处理"
        : "这笔申请不会执行";
    case "READY":
      return `${operationName}已批准，等待安全执行`;
    case "PROCESSING":
      return `${operationName}正在安全执行，请勿重复操作`;
    case "OUTCOME_UNKNOWN":
      return `${operationName}结果暂时未知，等待核对`;
    case "FAILED_RETRYABLE":
      return `${operationName}未完成，可由有权限人员核对后重试`;
    case "BLOCKED_PRECONDITION_CHANGED":
      return approval.snapshot.kind === "INVENTORY_OPERATION"
        ? "商品或库存数量已变化，已停止执行"
        : approval.snapshot.kind === "PURCHASE_ORDER_APPROVAL"
          ? "采购草稿内容或状态已变化，已停止执行"
          : approval.snapshot.kind === "GROUP_BUY_DAILY_RECONCILIATION"
            ? "平台日结单或团购履约事实已变化，已停止执行"
            : approval.snapshot.kind === "GROUP_BUY_RECONCILIATION_RESOLUTION"
              ? "原 GH 对账事实或逐笔处置依据已变化，已停止执行"
              : approval.snapshot.kind === "CASH_SHIFT_DIFFERENCE"
                ? "班次或现金交接事实已变化，已停止关闭班次"
                : approval.snapshot.kind === "MEMBER_BALANCE_REFUND"
                  ? "原会员消费情况已变化，已停止执行"
                  : "原单情况已变化，已停止执行";
    case "BLOCKED_INTEGRITY_REVIEW":
      return `${operationName}关联校验冲突，已停止自动执行`;
    case "FAILED_TERMINAL":
      return `${operationName}执行失败，需要人工核对`;
    case "SUCCEEDED":
      return approval.snapshot.kind === "INVENTORY_OPERATION"
        ? `${operationName}已完成并留下库存流水`
        : approval.snapshot.kind === "PURCHASE_ORDER_APPROVAL"
          ? "采购单审批已安全完成，可以进入后续收货"
          : approval.snapshot.kind === "GROUP_BUY_DAILY_RECONCILIATION"
            ? "团购日对账已完成，逐笔一致和差异结果已记录"
            : approval.snapshot.kind === "GROUP_BUY_RECONCILIATION_RESOLUTION"
              ? "团购差异处置已签结，平台应收和结算调整记录已追加"
              : approval.snapshot.kind === "CASH_SHIFT_DIFFERENCE"
                ? "现金班次已安全关闭并留下交接记录"
                : approval.snapshot.kind === "MEMBER_BALANCE_REFUND"
                  ? "余额退款已完成并留下会员资金记录"
                  : approval.snapshot.kind === "ORDER_ITEM_PAID_RETURN"
                    ? approval.snapshot.itemKind === "PACKAGE"
                      ? approval.snapshot.inventoryEffect === "SALEABLE_STOCK_RESTORED"
                        ? "套餐整包退回、原路退款、商品库存恢复和本单服务权益撤销均已完成"
                        : "套餐整包退回、原路退款和本单服务权益撤销均已完成；商品可售库存未增加"
                      : approval.snapshot.inventoryEffect === "SALEABLE_STOCK_RESTORED"
                        ? "商品退回、原路退款和可售库存增加均已完成"
                        : "商品退回和原路退款均已完成；不可售实物没有增加或再次扣减库存"
                    : "现金退款已完成并留下退款记录";
  }
}

async function runApprovalCommand(
  command: ApprovalMutationCommand,
  recoverUnknown = false,
): Promise<ApprovalRequestReadModel> {
  switch (command.kind) {
    case "CREATE_ORDER_CASH_REFUND":
      return createOrderCashRefundApproval(command.request);
    case "CREATE_MEMBER_BALANCE_REFUND":
      return createMemberBalanceRefundApproval(command.request);
    case "CREATE_PAID_ORDER_ITEM_RETURN":
      return createPaidOrderItemReturnApproval(command.request);
    case "CREATE_INVENTORY_OPERATION":
      return createInventoryOperationApproval(command.request);
    case "CREATE_PURCHASE_ORDER_APPROVAL":
      return recoverUnknown
        ? recoverPurchaseOrderApproval(command.request)
        : createPurchaseOrderApproval(command.request);
    case "CREATE_GROUP_BUY_DAILY_RECONCILIATION":
      return recoverUnknown
        ? recoverGroupBuyDailyReconciliationApproval(command.request)
        : createGroupBuyDailyReconciliationApproval(command.request);
    case "CREATE_GROUP_BUY_RECONCILIATION_RESOLUTION":
      return recoverUnknown
        ? recoverGroupBuyReconciliationResolutionApproval(command.request)
        : createGroupBuyReconciliationResolutionApproval(command.request);
    case "DECIDE":
      return decideApproval(command.request);
    case "CANCEL":
      return cancelApproval(command.request);
  }
}

function useApprovalMutationRecovery(
  operator: OperatorIdentity,
  onSuccess?: (
    approval: ApprovalRequestReadModel,
    command: ApprovalMutationCommand,
    recoveredExisting: boolean,
  ) => void,
): {
  recoveryView: ApprovalMutationRecoveryView;
  mutation: ReturnType<
    typeof useMutation<ApprovalRequestReadModel, Error, ApprovalMutationRecovery>
  >;
  submit: (command: ApprovalMutationCommand) => void;
  retry: () => void;
  refresh: () => void;
} {
  const queryClient = useQueryClient();
  const identityKey = approvalMutationIdentityKey(operator);
  const identityKeyRef = useRef(identityKey);
  identityKeyRef.current = identityKey;
  const [recoveryState, setRecoveryState] = useState<{
    identityKey: string;
    view: ApprovalMutationRecoveryView;
  }>(() => ({ identityKey, view: readApprovalMutationRecovery(identityKey) }));
  const recoveryView =
    recoveryState.identityKey === identityKey
      ? recoveryState.view
      : ({ status: "OTHER_OPERATOR" } as const);
  const mutation = useMutation<ApprovalRequestReadModel, Error, ApprovalMutationRecovery>({
    mutationFn: async (recovery) => {
      if (
        identityKeyRef.current !== identityKey ||
        !beginApprovalMutationDispatch(recovery, identityKey)
      ) {
        throw new ApiRequestError(
          "APPROVAL_RECOVERY_NOT_DURABLE",
          "原操作记录无法安全锁定，系统没有再次提交",
        );
      }
      try {
        const approval = await runApprovalCommand(
          recovery.command,
          recovery.state === "OUTCOME_UNKNOWN",
        );
        if (identityKeyRef.current !== identityKey) {
          throw new ApiRequestError(
            "OPERATOR_IDENTITY_CHANGED",
            "账号已切换；原账号的操作结果需要回到审批中心核对",
          );
        }
        if (!clearApprovalMutationRecovery(recovery, identityKey)) {
          throw new ApiRequestError(
            "APPROVAL_RECOVERY_CLEAR_FAILED",
            "服务已返回结果，但本机安全记录无法清除；请先查询状态，不要重新提交",
          );
        }
        return approval;
      } catch (error) {
        if (approvalMutationOutcomeIsUnknown(error)) {
          markApprovalMutationOutcomeUnknown(recovery, identityKey);
        } else {
          clearApprovalMutationRecovery(recovery, identityKey);
        }
        throw error;
      } finally {
        finishApprovalMutationDispatch(recovery, identityKey);
      }
    },
    onSuccess: async (approval, recovery) => {
      await Promise.all([
        invalidateCashShiftDifferenceReaders(queryClient, operator, approval),
        queryClient.invalidateQueries({ queryKey: ["approval-summary"] }),
        queryClient.invalidateQueries({ queryKey: ["approvals"] }),
        queryClient.invalidateQueries({ queryKey: ["approval", approval.approvalId] }),
        queryClient.invalidateQueries({ queryKey: ["orders"] }),
        queryClient.invalidateQueries({ queryKey: ["paid-order-item-return-candidates"] }),
        queryClient.invalidateQueries({ queryKey: ["purchase-order-drafts"] }),
      ]);
      onSuccess?.(approval, recovery.command, recovery.state === "OUTCOME_UNKNOWN");
    },
    onSettled: () => {
      setRecoveryState({ identityKey, view: readApprovalMutationRecovery(identityKey) });
    },
  });
  useEffect(() => {
    mutation.reset();
    setRecoveryState({ identityKey, view: readApprovalMutationRecovery(identityKey) });
  }, [identityKey, mutation.reset]);
  const dispatch = (recovery: ApprovalMutationRecovery): void => {
    if (!beginApprovalMutationDispatch(recovery, identityKey)) {
      setRecoveryState({ identityKey, view: readApprovalMutationRecovery(identityKey) });
      return;
    }
    finishApprovalMutationDispatch(recovery, identityKey);
    mutation.mutate(recovery);
  };
  const submit = (command: ApprovalMutationCommand): void => {
    mutation.reset();
    const claimed = claimApprovalMutationRecovery(identityKey, command);
    setRecoveryState({ identityKey, view: claimed });
    if (
      claimed.status === "OWNED" &&
      JSON.stringify(claimed.recovery.command) === JSON.stringify(command)
    ) {
      dispatch(claimed.recovery);
    }
  };
  const retry = (): void => {
    if (recoveryView.status === "OWNED" && !mutation.isPending) {
      dispatch(recoveryView.recovery);
    }
  };
  return {
    recoveryView,
    mutation,
    submit,
    retry,
    refresh: () =>
      setRecoveryState({ identityKey, view: readApprovalMutationRecovery(identityKey) }),
  };
}

function useApprovalExecutionRecovery(
  operator: OperatorIdentity,
  onResult?: (approval: ApprovalRequestReadModel) => void,
): {
  recoveryView: ApprovalExecutionRecoveryView;
  mutation: ReturnType<
    typeof useMutation<ApprovalRequestReadModel, Error, ApprovalExecutionRecovery>
  >;
  submit: (approval: ApprovalRequestReadModel) => void;
  restart: (approval: ApprovalRequestReadModel) => void;
  retry: () => void;
  refresh: () => void;
} {
  const queryClient = useQueryClient();
  const identityKey = approvalMutationIdentityKey(operator);
  const identityKeyRef = useRef(identityKey);
  identityKeyRef.current = identityKey;
  const [recoveryState, setRecoveryState] = useState<{
    identityKey: string;
    view: ApprovalExecutionRecoveryView;
  }>(() => ({ identityKey, view: readApprovalExecutionRecovery(operator) }));
  const recoveryView =
    recoveryState.identityKey === identityKey ? recoveryState.view : ({ status: "NONE" } as const);
  const mutation = useMutation<ApprovalRequestReadModel, Error, ApprovalExecutionRecovery>({
    mutationFn: async (recovery) => {
      if (
        identityKeyRef.current !== identityKey ||
        !beginApprovalExecutionDispatch(recovery, operator)
      ) {
        throw new ApiRequestError(
          "APPROVAL_EXECUTION_RECOVERY_NOT_DURABLE",
          "原审批执行请求无法安全锁定，系统没有再次提交",
        );
      }
      try {
        const approval = await executeApproval(approvalExecutionCommand(recovery));
        if (identityKeyRef.current !== identityKey) {
          throw new ApiRequestError(
            "OPERATOR_IDENTITY_CHANGED",
            "账号已切换；原账号的审批执行结果需要回到审批中心核对",
          );
        }
        if (
          approvalExecutionResultIsTerminal(approval) ||
          approval.execution.status === "FAILED_RETRYABLE"
        ) {
          if (!clearApprovalExecutionRecovery(recovery, operator)) {
            throw new ApiRequestError(
              "APPROVAL_EXECUTION_RECOVERY_CLEAR_FAILED",
              "服务已返回终局结果，但本机执行记录无法清除；请查询状态，不要再次执行",
            );
          }
        } else if (!markApprovalExecutionOutcomeUnknown(recovery, operator)) {
          throw new ApiRequestError(
            "APPROVAL_EXECUTION_RECOVERY_SAVE_FAILED",
            "业务执行结果尚未终结，但本机无法保留原执行请求；当前已暂停重试",
          );
        }
        return approval;
      } catch (error) {
        if (approvalExecutionOutcomeIsUnknown(error)) {
          markApprovalExecutionOutcomeUnknown(recovery, operator);
        }
        throw error;
      } finally {
        finishApprovalExecutionDispatch(recovery, operator);
      }
    },
    onSuccess: async (approval) => {
      if (
        approval.snapshot.kind === "INVENTORY_OPERATION" &&
        approval.snapshot.operationType === "COUNT_SET" &&
        approval.execution.status === "SUCCEEDED"
      ) {
        await queryClient.cancelQueries({
          queryKey: ["inventory-count-progress", approval.snapshot.skuId, approval.approvalId],
          exact: true,
        });
      }
      queryClient.setQueryData(["approval", approval.approvalId], approval);
      await Promise.all([
        invalidateCashShiftDifferenceReaders(queryClient, operator, approval),
        queryClient.invalidateQueries({ queryKey: ["approval-summary"] }),
        queryClient.invalidateQueries({ queryKey: ["approvals"] }),
        queryClient.invalidateQueries({ queryKey: ["orders"] }),
        ...(approval.snapshot.kind === "INVENTORY_OPERATION" &&
        approval.execution.status === "SUCCEEDED"
          ? [
              queryClient.invalidateQueries({ queryKey: ["inventory-products"] }),
              queryClient.invalidateQueries({ queryKey: ["inventory-movements"] }),
            ]
          : []),
      ]);
      onResult?.(approval);
    },
    onSettled: () => {
      setRecoveryState({ identityKey, view: readApprovalExecutionRecovery(operator) });
    },
  });
  useEffect(() => {
    mutation.reset();
    setRecoveryState({ identityKey, view: readApprovalExecutionRecovery(operator) });
  }, [identityKey, mutation.reset, operator]);
  const dispatch = (recovery: ApprovalExecutionRecovery): void => {
    if (!beginApprovalExecutionDispatch(recovery, operator)) {
      setRecoveryState({ identityKey, view: readApprovalExecutionRecovery(operator) });
      return;
    }
    finishApprovalExecutionDispatch(recovery, operator);
    mutation.mutate(recovery);
  };
  const submit = (approval: ApprovalRequestReadModel): void => {
    mutation.reset();
    const claimed = claimApprovalExecutionRecovery(operator, {
      approvalId: approval.approvalId,
      expectedCaseVersion: approval.version,
      idempotencyKey: crypto.randomUUID(),
    });
    setRecoveryState({ identityKey, view: claimed });
    if (claimed.status === "OWNED" && claimed.recovery.approvalId === approval.approvalId) {
      dispatch(claimed.recovery);
    }
  };
  const retry = (): void => {
    if (recoveryView.status === "OWNED" && !mutation.isPending) {
      dispatch(recoveryView.recovery);
    }
  };
  const restart = (approval: ApprovalRequestReadModel): void => {
    const current = readApprovalExecutionRecovery(operator);
    if (current.status === "OWNED") {
      if (
        current.recovery.approvalId !== approval.approvalId ||
        current.recovery.expectedCaseVersion >= approval.version ||
        !clearApprovalExecutionRecovery(current.recovery, operator)
      ) {
        setRecoveryState({ identityKey, view: readApprovalExecutionRecovery(operator) });
        return;
      }
    } else if (current.status !== "NONE") {
      setRecoveryState({ identityKey, view: current });
      return;
    }
    setRecoveryState({ identityKey, view: { status: "NONE" } });
    submit(approval);
  };
  return {
    recoveryView,
    mutation,
    submit,
    restart,
    retry,
    refresh: () => setRecoveryState({ identityKey, view: readApprovalExecutionRecovery(operator) }),
  };
}

function usePaidOrderItemReturnExecutionRecovery(
  operator: OperatorIdentity,
  onResult?: (
    result: ApprovalRequestReadModel,
    recovery: PaidOrderItemReturnExecutionRecovery,
  ) => void,
): {
  recoveryView: PaidOrderItemReturnExecutionRecoveryView;
  mutation: ReturnType<
    typeof useMutation<ApprovalRequestReadModel, Error, PaidOrderItemReturnExecutionRecovery>
  >;
  submit: (approval: ApprovalRequestReadModel, evidence: PaidReturnExecutionEvidenceInput) => void;
  retry: () => void;
  refresh: () => void;
} {
  const queryClient = useQueryClient();
  const identityKey = approvalMutationIdentityKey(operator);
  const identityKeyRef = useRef(identityKey);
  identityKeyRef.current = identityKey;
  const [recoveryState, setRecoveryState] = useState<{
    identityKey: string;
    view: PaidOrderItemReturnExecutionRecoveryView;
  }>(() => ({ identityKey, view: readPaidOrderItemReturnExecutionRecovery(operator) }));
  const recoveryView =
    recoveryState.identityKey === identityKey ? recoveryState.view : ({ status: "NONE" } as const);
  const mutation = useMutation<
    ApprovalRequestReadModel,
    Error,
    PaidOrderItemReturnExecutionRecovery
  >({
    mutationFn: async (recovery) => {
      if (
        identityKeyRef.current !== identityKey ||
        !beginPaidOrderItemReturnExecutionDispatch(recovery, operator)
      ) {
        throw new ApiRequestError(
          "PAID_RETURN_EXECUTION_RECOVERY_NOT_DURABLE",
          "原商品退回请求无法安全锁定，系统没有再次提交",
        );
      }
      try {
        const result = await executePaidOrderItemReturn(recovery.command, recovery.expected);
        if (identityKeyRef.current !== identityKey) {
          throw new ApiRequestError(
            "OPERATOR_IDENTITY_CHANGED",
            "账号已切换；原账号的商品退回结果需要回到审批中心核对",
          );
        }
        const settlement = settlePaidOrderItemReturnExecutionRecovery(
          recovery,
          operator,
          result.execution.status,
        );
        if (settlement === null) {
          throw new ApiRequestError(
            result.execution.status === "SUCCEEDED"
              ? "PAID_RETURN_EXECUTION_RECOVERY_CLEAR_FAILED"
              : "PAID_RETURN_EXECUTION_RECOVERY_SAVE_FAILED",
            result.execution.status === "SUCCEEDED"
              ? "商品退回成功结果已返回，但本机安全记录无法清除；请查询状态，不要再次执行"
              : "商品退回尚未成功，但本机无法保留原执行请求；当前已暂停重试",
          );
        }
        return result;
      } catch (error) {
        if (paidOrderItemReturnExecutionOutcomeIsUnknown(error)) {
          markPaidOrderItemReturnExecutionOutcomeUnknown(recovery, operator);
        } else {
          clearPaidOrderItemReturnExecutionRecovery(recovery, operator);
        }
        throw error;
      } finally {
        finishPaidOrderItemReturnExecutionDispatch(recovery, operator);
      }
    },
    onSuccess: async (result, recovery) => {
      queryClient.setQueryData(["approval", result.approvalId], result);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["approval", recovery.command.approvalId] }),
        queryClient.invalidateQueries({ queryKey: ["approval-summary"] }),
        queryClient.invalidateQueries({ queryKey: ["approvals"] }),
        queryClient.invalidateQueries({ queryKey: ["orders"] }),
        queryClient.invalidateQueries({ queryKey: ["paid-order-item-return-candidates"] }),
        queryClient.invalidateQueries({ queryKey: ["products"] }),
      ]);
      onResult?.(result, recovery);
    },
    onSettled: () => {
      setRecoveryState({
        identityKey,
        view: readPaidOrderItemReturnExecutionRecovery(operator),
      });
    },
  });
  useEffect(() => {
    mutation.reset();
    setRecoveryState({ identityKey, view: readPaidOrderItemReturnExecutionRecovery(operator) });
  }, [identityKey, mutation.reset, operator]);
  const dispatch = (recovery: PaidOrderItemReturnExecutionRecovery): void => {
    if (!beginPaidOrderItemReturnExecutionDispatch(recovery, operator)) {
      setRecoveryState({ identityKey, view: readPaidOrderItemReturnExecutionRecovery(operator) });
      return;
    }
    finishPaidOrderItemReturnExecutionDispatch(recovery, operator);
    mutation.mutate(recovery);
  };
  const submit = (
    approval: ApprovalRequestReadModel,
    evidence: PaidReturnExecutionEvidenceInput,
  ): void => {
    if (approval.snapshot.kind !== "ORDER_ITEM_PAID_RETURN") return;
    mutation.reset();
    const snapshot = approval.snapshot;
    const command = {
      approvalId: approval.approvalId,
      expectedCaseVersion: approval.version,
      externalRefundReference: evidence.externalRefundReference,
      ...(evidence.externalRefundReferences === undefined
        ? {}
        : { externalRefundReferences: evidence.externalRefundReferences }),
      idempotencyKey: crypto.randomUUID(),
    };
    const claimed = claimPaidOrderItemReturnExecutionRecovery(operator, command, {
      approvalSnapshotVersion: snapshot.snapshotVersion,
      itemId: snapshot.itemId,
      returnQuantity: snapshot.returnQuantity,
      amountFen: snapshot.amountFen,
      paymentMethod: snapshot.paymentMethod,
      refundExecutionMode: snapshot.refundExecutionMode,
      ...(snapshot.refundAllocations === undefined
        ? {}
        : { refundAllocations: snapshot.refundAllocations }),
      returnedQuantityAfter: snapshot.returnedQuantityAfter,
      physicalDisposition: snapshot.physicalDisposition,
      inventoryEffect: snapshot.inventoryEffect,
      inventoryQuantityBefore: snapshot.inventoryQuantityBefore,
      inventoryQuantityAfter: snapshot.inventoryQuantityAfter,
      inventorySnapshotKnown: true,
    });
    setRecoveryState({ identityKey, view: claimed });
    if (
      claimed.status === "OWNED" &&
      JSON.stringify(claimed.recovery.command) === JSON.stringify(command)
    ) {
      dispatch(claimed.recovery);
    }
  };
  return {
    recoveryView,
    mutation,
    submit,
    retry: () => {
      if (recoveryView.status === "OWNED" && !mutation.isPending) dispatch(recoveryView.recovery);
    },
    refresh: () =>
      setRecoveryState({
        identityKey,
        view: readPaidOrderItemReturnExecutionRecovery(operator),
      }),
  };
}

function ApprovalRecoveryNotice({
  recoveryView,
  pending,
  onRetry,
  onQuery,
}: {
  recoveryView: ApprovalMutationRecoveryView;
  pending: boolean;
  onRetry: () => void;
  onQuery: () => void;
}): React.JSX.Element | null {
  if (recoveryView.status === "NONE") return null;
  if (recoveryView.status === "OTHER_OPERATOR") {
    return (
      <div className="approval-warning" role="alert">
        另一个账号有一项结果尚未确认。请切回原账号处理；当前账号不会重复提交。
      </div>
    );
  }
  if (recoveryView.status === "RECOVERY_STORAGE_UNAVAILABLE") {
    return (
      <div className="approval-warning" role="alert">
        无法安全保存审批操作进度。为避免重复申请或重复决定，当前已暂停提交。
      </div>
    );
  }
  return (
    <section className="approval-recovery" role="alert">
      <strong>{pending ? "正在提交原操作，请勿重复点击" : "上次操作结果暂时未知"}</strong>
      <p>
        系统保留了原业务事实、原因和操作编号。你可以先查询最新状态；如果确需重试，也只会发送原操作。
      </p>
      {!pending ? (
        <div className="approval-actions">
          <button className="button secondary compact" type="button" onClick={onQuery}>
            <RefreshCw size={15} aria-hidden="true" /> 先查询状态
          </button>
          <button className="button primary compact" type="button" onClick={onRetry}>
            按原操作重试
          </button>
        </div>
      ) : null}
    </section>
  );
}

function ApprovalExecutionRecoveryNotice({
  recoveryView,
  pending,
  onQuery,
}: {
  recoveryView: ApprovalExecutionRecoveryView;
  pending: boolean;
  onQuery: () => void;
}): React.JSX.Element | null {
  if (recoveryView.status === "NONE") return null;
  if (recoveryView.status === "RECOVERY_STORAGE_UNAVAILABLE") {
    return (
      <div className="approval-warning" role="alert">
        无法安全保存审批执行进度。为避免重复执行，当前已暂停操作。
      </div>
    );
  }
  return (
    <section className="approval-recovery" role="alert">
      <strong>{pending ? "正在执行已批准的业务，请勿重复点击" : "原审批执行请求仍待终结"}</strong>
      <p>
        系统固定保留了原账号、申请版本和操作编号。先查询权威状态；详情允许重试时，也只会使用原请求。
      </p>
      {!pending ? (
        <button className="button secondary compact" type="button" onClick={onQuery}>
          <RefreshCw size={15} aria-hidden="true" /> 查询原执行结果
        </button>
      ) : null}
    </section>
  );
}

function PaidOrderItemReturnExecutionRecoveryNotice({
  recoveryView,
  pending,
  onQuery,
  onRetry,
}: {
  recoveryView: PaidOrderItemReturnExecutionRecoveryView;
  pending: boolean;
  onQuery: () => void;
  onRetry: () => void;
}): React.JSX.Element | null {
  if (recoveryView.status === "NONE") return null;
  if (recoveryView.status === "RECOVERY_STORAGE_UNAVAILABLE") {
    return (
      <div className="approval-warning" role="alert">
        无法安全保存商品退回执行进度。为避免重复退款或重复处理库存，当前已暂停执行。
      </div>
    );
  }
  const mode = recoveryView.recovery.expected.refundExecutionMode;
  const combinedRefund = recoveryView.recovery.expected.refundAllocations !== undefined;
  return (
    <section className="approval-recovery" role="alert">
      <strong>{pending ? "正在核对原商品退回请求" : "商品退回结果暂时未知"}</strong>
      <p>
        {combinedRefund
          ? "系统保留了全部逐路退款确认、各静态码凭证、申请版本和操作编号；任一路未知都会锁定整笔请求，重试不能修改任何一路。"
          : mode === "LOCAL_CASH_CONFIRMATION"
            ? "系统保留了原现金退款确认、申请版本和操作编号；请先查询，不能重新创建退款。"
            : mode === "MANUAL_STATIC_CODE_CONFIRMATION"
              ? "系统保留了原外部退款凭证、申请版本和操作编号；重试也只会使用这份原凭证。"
              : mode === "MEMBER_BALANCE_CLOUD_REFUND"
                ? "会员余额退款正在由联网服务核对；系统会使用原请求查询，不能再次扣减或增加余额。"
                : "官方支付退款正在查询原渠道结果；系统会复用原渠道请求，不能另起一笔退款。"}
      </p>
      {!pending ? (
        <div className="approval-actions">
          <button className="button secondary compact" type="button" onClick={onQuery}>
            <RefreshCw size={15} aria-hidden="true" /> 查询原执行结果
          </button>
          <button className="button primary compact" type="button" onClick={onRetry}>
            使用原请求安全重试
          </button>
        </div>
      ) : null}
    </section>
  );
}

type MemberRechargeManualReviewItem = Extract<
  ManualReviewReadItem,
  { category: "MEMBER_RECHARGE_RECONCILIATION" }
>;

type MemberReceivableManualReviewItem = Extract<
  ManualReviewReadItem,
  { category: "MEMBER_RECEIVABLE" }
>;

function memberRechargeMethodLabel(method: MemberRechargeManualReviewItem["method"]): string {
  return method === "STATIC_WECHAT" ? "微信收款码" : "支付宝收款码";
}

function memberRechargeStatusLabel(
  status: MemberRechargeManualReviewItem["rechargeStatus"],
): string {
  return status === "PENDING_RECONCILIATION" ? "等待渠道核对" : "已记录异常，仍可重新核对";
}

function memberRechargeRequestedAmountLabel(amountFen: number | null): string {
  return amountFen !== null && Number.isSafeInteger(amountFen) && amountFen > 0
    ? fee(amountFen)
    : "需要核对";
}

function memberRechargeConsequence(item: MemberRechargeManualReviewItem): string {
  return item.rechargeStatus === "PENDING_RECONCILIATION"
    ? "会员余额尚未增加。必须先生成确认预览；只有老板或店长核对并再次确认后，页面显示的钱包结果才会生效。"
    : "当前未增加会员余额；如果取得新的渠道证据，老板或店长可以在同一待办中重新核对。";
}

interface MemberRechargeReconciliationDraft {
  workItemId: string;
  outcome: MemberRechargeReconciliationOutcome;
  actualReceivedFen: string;
  paymentEvidenceReference: string;
  note: string;
}

const memberRechargeOutcomeOptions: ReadonlyArray<{
  value: MemberRechargeReconciliationOutcome;
  label: string;
  explanation: string;
}> = [
  {
    value: "CONFIRMED",
    label: "金额一致",
    explanation: "渠道实际到账与原申请相同；确认后按服务端预览显示的各钱包金额入账。",
  },
  {
    value: "NOT_RECEIVED",
    label: "未到账",
    explanation: "渠道没有查询到这笔收款；确认后关闭本次核对，会员余额保持不变。",
  },
  {
    value: "AMOUNT_MISMATCH",
    label: "金额不符",
    explanation: "渠道实际到账与原申请不同；赠送归零，实际到账只进入唯一的人民币储值钱包。",
  },
  {
    value: "DUPLICATE_PAYMENT",
    label: "付款凭证重复",
    explanation: "这个支付凭证已经用于另一笔处理；确认后不会重复入账，会员余额保持不变。",
  },
];

function memberRechargeOutcomeLabel(outcome: MemberRechargeReconciliationOutcome): string {
  return memberRechargeOutcomeOptions.find((option) => option.value === outcome)?.label ?? outcome;
}

function memberRechargeDefaultNote(outcome: MemberRechargeReconciliationOutcome): string {
  switch (outcome) {
    case "CONFIRMED":
      return "渠道到账金额与原申请一致";
    case "NOT_RECEIVED":
      return "渠道记录未查询到这笔到账";
    case "AMOUNT_MISMATCH":
      return "渠道显示实际到账金额与原申请不同";
    case "DUPLICATE_PAYMENT":
      return "支付凭证已在另一笔充值中处理";
  }
}

function initialMemberRechargeDraft(
  item: MemberRechargeManualReviewItem,
): MemberRechargeReconciliationDraft {
  return {
    workItemId: item.workItemId,
    outcome: "CONFIRMED",
    actualReceivedFen: memberRechargeYuanInputFromFen(item.amountFen),
    paymentEvidenceReference: "",
    note: memberRechargeDefaultNote("CONFIRMED"),
  };
}

function memberRechargePreviewRequest(
  item: MemberRechargeManualReviewItem,
  draft: MemberRechargeReconciliationDraft,
):
  | { request: MemberRechargeReconciliationPreviewRequest; error: null }
  | { request: null; error: string } {
  if (item.amountFen === null || !Number.isSafeInteger(item.amountFen) || item.amountFen <= 0) {
    return {
      request: null,
      error: "原充值金额尚未确认。请刷新本页；仍无法取得时保留待核对，不要关闭或入账。",
    };
  }
  const note = draft.note.trim();
  if ([...note].length < 2 || [...note].length > 500) {
    return { request: null, error: "核对说明请填写 2 至 500 个字。" };
  }
  if (draft.outcome === "NOT_RECEIVED") {
    return {
      request: {
        outcome: draft.outcome,
        actualReceivedFen: null,
        paymentEvidenceReference: null,
        note,
        idempotencyKey: `member-recharge-preview-${crypto.randomUUID()}`,
      },
      error: null,
    };
  }
  const paymentEvidenceReference = draft.paymentEvidenceReference.trim();
  if (!paymentEvidenceReference) {
    return {
      request: null,
      error: "请填写支付凭证号，例如渠道订单号；页面只会在预览和结果中显示尾号。",
    };
  }
  if (!/^[A-Za-z0-9][A-Za-z0-9._:/-]{5,127}$/u.test(paymentEvidenceReference)) {
    return {
      request: null,
      error:
        "支付凭证号需填写 6 至 128 个字符，只能使用英文字母、数字、点、横线、下划线、冒号或斜线。",
    };
  }
  if (draft.outcome === "DUPLICATE_PAYMENT") {
    return {
      request: {
        outcome: draft.outcome,
        actualReceivedFen: null,
        paymentEvidenceReference,
        note,
        idempotencyKey: `member-recharge-preview-${crypto.randomUUID()}`,
      },
      error: null,
    };
  }
  const actualReceivedYuan = draft.actualReceivedFen.trim();
  if (!/^(0|[1-9]\d*)(?:\.\d{1,2})?$/u.test(actualReceivedYuan)) {
    return {
      request: null,
      error:
        "实际到账金额请按“元”填写，最多两位小数；不能使用负数、指数写法或三位以上小数，例如请填写 80.01。",
    };
  }
  const actualReceivedFen = yuanInputToFen(actualReceivedYuan);
  if (actualReceivedFen === null || actualReceivedFen > 100_000_000) {
    return {
      request: null,
      error: `实际到账金额必须大于 ¥0.00，且不能超过 ${fee(100_000_000)}。`,
    };
  }
  const requestedPaidAmountFen = item.amountFen;
  if (draft.outcome === "CONFIRMED" && actualReceivedFen !== requestedPaidAmountFen) {
    return {
      request: null,
      error: `选择“金额一致”时，实际到账必须等于原申请 ${fee(requestedPaidAmountFen)}。`,
    };
  }
  if (draft.outcome === "AMOUNT_MISMATCH" && actualReceivedFen === requestedPaidAmountFen) {
    return {
      request: null,
      error: "选择“金额不符”时，实际到账不能等于原申请；金额相同请改选“金额一致”。",
    };
  }
  return {
    request: {
      outcome: draft.outcome,
      actualReceivedFen,
      paymentEvidenceReference,
      note,
      idempotencyKey: `member-recharge-preview-${crypto.randomUUID()}`,
    },
    error: null,
  };
}

function memberRechargeWalletBusinessLabel(businessType: string): string {
  switch (businessType) {
    case "STORED_VALUE":
      return "人民币储值钱包";
    case "PROMOTIONAL":
      return "赠送钱包";
    case "TIME_CREDIT":
      return "时长钱包";
    case "LOYALTY":
      return "积分权益";
    case "COUNT_CREDIT":
      return "次数钱包";
    case "CUSTOM":
      return "其他业务钱包";
    default:
      return "业务钱包";
  }
}

function memberRechargeWalletAmount(
  credit: MemberRechargeReconciliationPreview["walletCredits"][number],
): string {
  if (credit.unit === "CNY_FEN") return fee(credit.amount);
  const value = (credit.amount / 10 ** credit.precision).toFixed(credit.precision);
  if (credit.unit === "MINUTE") {
    const minutes = Number(value);
    if (!Number.isInteger(minutes)) return `${value} 分钟`;
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    const parts = [
      hours > 0 ? `${hours} 小时` : "",
      remainingMinutes > 0 || hours === 0 ? `${remainingMinutes} 分钟` : "",
    ].filter(Boolean);
    return parts.join(" ");
  }
  const unit =
    credit.unit === "POINT"
      ? "积分"
      : credit.unit === "COUNT"
        ? "次"
        : (credit.customUnitLabel ?? "单位");
  return `${value} ${unit}`;
}

function MemberRechargeReconciliationPreviewCard({
  preview,
}: {
  preview: MemberRechargeReconciliationPreview;
}): React.JSX.Element {
  const credited = preview.balanceEffectOnConfirmation === "CREDITED";
  return (
    <section className="approval-steps" aria-label="不可变充值核对确认预览">
      <h4>不可变确认预览</h4>
      <p className="field-help">
        下方内容由服务端按原申请和渠道核对结果生成。只有按此预览再次确认后才会生效，页面不会自行改算钱包金额。
      </p>
      <dl className="approval-card-facts">
        <div>
          <dt>处理选择</dt>
          <dd>{memberRechargeOutcomeLabel(preview.outcome)}</dd>
        </div>
        <div>
          <dt>原申请金额</dt>
          <dd>{fee(preview.requestedPaidAmountFen)}</dd>
        </div>
        <div>
          <dt>实际到账金额</dt>
          <dd>
            {preview.actualReceivedFen === null ? "没有到账" : fee(preview.actualReceivedFen)}
          </dd>
        </div>
        <div>
          <dt>人民币钱包入账合计</dt>
          <dd>{fee(preview.cnyWalletCreditTotalFen)}</dd>
        </div>
        <div>
          <dt>会员余额与权益是否变化</dt>
          <dd>
            {credited
              ? preview.cnyWalletCreditTotalFen > 0
                ? `人民币余额会增加 ${fee(preview.cnyWalletCreditTotalFen)}；其他权益按下方明细增加`
                : "人民币余额不变；其他会员权益按下方明细增加"
              : "所有会员钱包和权益保持不变"}
          </dd>
        </div>
        <div>
          <dt>支付凭证</dt>
          <dd>
            {preview.paymentEvidenceEnding ? `凭证尾号${preview.paymentEvidenceEnding}` : "不需要"}
          </dd>
        </div>
      </dl>
      {preview.walletCredits.length ? (
        <section className="approval-list" aria-label="各钱包入账结果">
          {preview.walletCredits.map((wallet, index) => (
            <article className="approval-list-card" key={`${wallet.walletName}:${index}`}>
              <strong>{wallet.walletName}</strong>
              <p>
                {memberRechargeWalletBusinessLabel(wallet.businessType)} · 入账{" "}
                {memberRechargeWalletAmount(wallet)}
                {preview.outcome === "AMOUNT_MISMATCH" && index === 0 ? " · 唯一入账钱包" : ""}
              </p>
            </article>
          ))}
        </section>
      ) : (
        <p className="field-help">本次没有任何钱包入账。</p>
      )}
      <div className="approval-info">
        <ShieldCheck size={17} aria-hidden="true" />
        <div>
          <strong>确认后果</strong>
          <p>
            {preview.outcome === "AMOUNT_MISMATCH"
              ? `确认后，赠送金额归零；实际到账 ${fee(preview.actualReceivedFen ?? 0)} 只进入上方唯一的人民币储值钱包。`
              : credited
                ? `确认后，人民币钱包增加 ${fee(preview.cnyWalletCreditTotalFen)}，时长、次数或其他权益按上方各自单位增加。`
                : "确认后，这笔待核对事项会关闭，所有会员钱包和权益保持不变，也不会重复入账。"}
          </p>
        </div>
      </div>
    </section>
  );
}

function MemberRechargeReconciliationEditor({
  item,
  draft,
  preview,
  credentialId,
  credential,
  draftError,
  locked,
  pending,
  onOutcomeChange,
  onDraftChange,
  onPreview,
  onCredentialChange,
  onConfirm,
}: {
  item: MemberRechargeManualReviewItem;
  draft: MemberRechargeReconciliationDraft;
  preview: MemberRechargeReconciliationPreview | null;
  credentialId: string;
  credential: string;
  draftError: string | null;
  locked: boolean;
  pending: boolean;
  onOutcomeChange: (outcome: MemberRechargeReconciliationOutcome) => void;
  onDraftChange: (update: Partial<MemberRechargeReconciliationDraft>) => void;
  onPreview: () => void;
  onCredentialChange: (value: string) => void;
  onConfirm: () => void;
}): React.JSX.Element {
  const requiresAmount = draft.outcome === "CONFIRMED" || draft.outcome === "AMOUNT_MISMATCH";
  const requiresEvidence = draft.outcome !== "NOT_RECEIVED";
  const parsedActualReceivedFen = requiresAmount ? yuanInputToFen(draft.actualReceivedFen) : null;
  const displayActualReceivedFen =
    parsedActualReceivedFen !== null && parsedActualReceivedFen <= 100_000_000
      ? parsedActualReceivedFen
      : null;
  return (
    <section className="approval-member-recharge-actions" aria-label="会员充值核对处理">
      <fieldset className="sale-return-disposition-options">
        <legend>请选择渠道核对结果</legend>
        {memberRechargeOutcomeOptions.map((option) => (
          <label key={option.value}>
            <input
              type="radio"
              name={`member-recharge-outcome-${item.workItemId}`}
              value={option.value}
              checked={draft.outcome === option.value}
              disabled={locked || pending}
              onChange={() => onOutcomeChange(option.value)}
            />
            <span>
              <strong>{option.label}</strong>
              <small>{option.explanation}</small>
            </span>
          </label>
        ))}
      </fieldset>
      {requiresAmount ? (
        <label>
          实际到账金额（元）
          <input
            type="text"
            inputMode="decimal"
            value={draft.actualReceivedFen}
            disabled={locked || pending}
            placeholder="例如：80.01"
            onChange={(event) => onDraftChange({ actualReceivedFen: event.target.value })}
          />
          <small>
            原申请金额为 {memberRechargeRequestedAmountLabel(item.amountFen)}。
            {displayActualReceivedFen === null
              ? " 请输入大于 0 且最多两位小数的人民币金额。"
              : ` 本次按 ${fee(displayActualReceivedFen)} 核对。`}
          </small>
        </label>
      ) : null}
      {requiresEvidence ? (
        <label>
          支付凭证号
          <input
            type="text"
            autoComplete="off"
            value={draft.paymentEvidenceReference}
            disabled={locked || pending}
            placeholder="填写渠道订单号或支付凭证号"
            onChange={(event) => onDraftChange({ paymentEvidenceReference: event.target.value })}
          />
          <small>用于防止重复入账；确认预览和结果只显示尾号，不显示完整凭证。</small>
        </label>
      ) : null}
      <label>
        核对说明（2–500 字）
        <textarea
          rows={3}
          maxLength={500}
          value={draft.note}
          disabled={locked || pending}
          onChange={(event) => onDraftChange({ note: event.target.value })}
        />
        <small>请写明核对了哪个渠道记录，以及为什么选择当前结果。</small>
      </label>
      {draftError ? (
        <div className="approval-warning" role="alert">
          还需修改：{draftError}
        </div>
      ) : null}
      {preview ? <MemberRechargeReconciliationPreviewCard preview={preview} /> : null}
      {!preview ? (
        <button
          className="button primary"
          type="button"
          disabled={locked || pending}
          onClick={onPreview}
        >
          {pending ? "正在生成预览…" : "生成确认预览"}
        </button>
      ) : (
        <>
          {locked ? (
            <div className="approval-warning" role="alert">
              确认结果暂时未知，原处理选择、金额、支付凭证、说明和预览已经锁定，只能按原预览安全重试。
            </div>
          ) : (
            <p className="field-help">
              如果切换处理选择，或修改金额、支付凭证、说明，当前预览会立即失效，必须重新生成。
            </p>
          )}
          <label htmlFor={credentialId}>
            当前账号密码
            <AutofillSafeInput
              id={credentialId}
              fieldName="approval-member-recharge-confirm-authorization"
              type="password"
              sensitive
              value={credential}
              onUserChange={onCredentialChange}
            />
            <small>密码只用于本次二次确认，不会和预览或支付凭证一起保存。</small>
          </label>
          <button
            className="button primary"
            type="button"
            disabled={!credential || pending}
            onClick={onConfirm}
          >
            {pending ? "正在确认…" : locked ? "使用原预览安全重试" : "按此预览确认处理"}
          </button>
        </>
      )}
    </section>
  );
}

function ApprovalManualReviewPanel({
  operator,
  effectiveRoles,
  filter,
  filterInvalid,
  onClearFilter,
}: {
  operator: OperatorIdentity;
  effectiveRoles: readonly OperatorRole[];
  filter: ManualReviewListFilter | null;
  filterInvalid: boolean;
  onClearFilter: () => void;
}): React.JSX.Element {
  const queryClient = useQueryClient();
  const credentialId = useId();
  const feedbackRef = useRef<HTMLDivElement>(null);
  const [credential, setCredential] = useState("");
  const [credentialWorkItemId, setCredentialWorkItemId] = useState<string | null>(null);
  const [reconciliationDraft, setReconciliationDraft] =
    useState<MemberRechargeReconciliationDraft | null>(null);
  const [reconciliationDraftError, setReconciliationDraftError] = useState<string | null>(null);
  const [feedback, setFeedback] = useState<{ kind: "SUCCESS" | "ERROR"; message: string } | null>(
    null,
  );
  const { id: operatorId, storeId, tenantId } = operator;
  const recoveryContext = useMemo(
    () => memberRechargeRecoveryContext({ id: operatorId, storeId, tenantId }),
    [operatorId, storeId, tenantId],
  );
  const [recoveryView, setRecoveryView] = useState<MemberRechargeRecoveryView>(() =>
    readMemberRechargeRecovery(recoveryContext),
  );
  const memberReceivableIdentityKey = checkoutIdentityKey(operator);
  const [memberReceivableRecoveryView, setMemberReceivableRecoveryView] =
    useState<MemberReceivableRetryRecoveryView>(() =>
      readMemberReceivableRetryRecovery(memberReceivableIdentityKey),
    );
  const manualResolutionIdentityKey = useMemo(
    () => manualReviewResolutionIdentityKey({ id: operatorId, storeId, tenantId }),
    [operatorId, storeId, tenantId],
  );
  const [manualResolutionRecoveryView, setManualResolutionRecoveryView] =
    useState<ManualReviewResolutionRecoveryView>(() =>
      readManualReviewResolutionRecovery(manualResolutionIdentityKey),
    );
  const canReconcile = effectiveRoles.some((role) => role === "OWNER" || role === "MANAGER");
  const canRetryMemberReceivable = effectiveRoles.some(
    (role) => role === "CASHIER" || role === "MANAGER",
  );
  const query = useQuery({
    queryKey: [
      "manual-review",
      operator.tenantId,
      operator.storeId,
      operator.id,
      filter?.category ?? "ALL",
      filter?.businessDate ?? "ALL",
    ],
    queryFn: () => (filter ? listManualReview(filter) : listManualReview()),
    enabled: !filterInvalid,
    retry: false,
  });
  const previewReconciliation = useMutation({
    mutationFn: async ({
      item,
      request: frozenRequest,
      recovery,
    }: {
      item: MemberRechargeManualReviewItem;
      request: MemberRechargeReconciliationPreviewRequest;
      recovery: MemberRechargeRecovery;
    }) => {
      if (recovery.command.kind !== "MEMBER_RECHARGE_RECONCILIATION_PREVIEW") {
        throw new ApiRequestError(
          "MEMBER_RECHARGE_RECOVERY_CONFLICT",
          "当前保留的不是充值核对预览请求，请先处理原请求",
          409,
        );
      }
      if (!beginMemberRechargeDispatch(recovery, recoveryContext)) {
        throw new ApiRequestError(
          "MEMBER_RECHARGE_RECOVERY_LOCKED",
          "原预览请求正在处理，或当前账号已发生变化，请稍后重新读取",
          409,
        );
      }
      let frozenPreview: MemberRechargeReconciliationPreview;
      try {
        if (item.amountFen === null) {
          throw new ApiRequestError(
            "MEMBER_RECHARGE_AMOUNT_UNAVAILABLE",
            "原申请金额无法安全确认，系统没有生成预览",
            400,
          );
        }
        frozenPreview = await previewMemberRechargeReconciliation(
          recovery.command.workItemId,
          frozenRequest,
          {
            workItemId: item.workItemId,
            memberName: item.memberName,
            method: item.method,
            amountFen: item.amountFen,
            createdAt: item.createdAt,
          },
        );
      } catch (error) {
        finishMemberRechargeDispatch(recovery, recoveryContext);
        discardUnconfirmedMemberRechargeReconciliation(recovery, recoveryContext);
        throw error;
      }
      const confirmationRequest = {
        previewId: frozenPreview.previewId,
        confirmationToken: frozenPreview.confirmationToken,
        idempotencyKey: `member-recharge-confirm-${crypto.randomUUID()}`,
      };
      const advanced = advanceMemberRechargeRecoveryToConfirmation(
        recovery,
        recoveryContext,
        frozenPreview,
        confirmationRequest,
      );
      if (!advanced) {
        finishMemberRechargeDispatch(recovery, recoveryContext);
        discardUnconfirmedMemberRechargeReconciliation(recovery, recoveryContext);
        throw new ApiRequestError(
          "MEMBER_RECHARGE_PREVIEW_CANNOT_BE_FROZEN",
          "确认预览无法安全锁定，页面不会继续确认，请重新读取这张待办",
          409,
        );
      }
      return advanced;
    },
    onMutate: () => {
      setCredential("");
      setCredentialWorkItemId(null);
      setFeedback(null);
      setReconciliationDraftError(null);
    },
    onSuccess: () => setRecoveryView(readMemberRechargeRecovery(recoveryContext)),
    onError: (error) => {
      setRecoveryView(readMemberRechargeRecovery(recoveryContext));
      setFeedback({
        kind: "ERROR",
        message: `${failureMessage(error, "这张充值待办暂时无法生成确认预览")}。这只影响当前待办，不表示整套 ERP 离线。`,
      });
    },
  });
  const confirmReconciliation = useMutation({
    mutationFn: async ({
      recovery,
      credential: frozenCredential,
    }: {
      recovery: MemberRechargeRecovery;
      credential: string;
    }) => {
      if (recovery.command.kind !== "MEMBER_RECHARGE_RECONCILIATION_CONFIRM") {
        throw new ApiRequestError(
          "MEMBER_RECHARGE_RECOVERY_CONFLICT",
          "当前保留的不是可确认的充值核对预览，请先处理原请求",
          409,
        );
      }
      if (!beginMemberRechargeDispatch(recovery, recoveryContext)) {
        throw new ApiRequestError(
          "MEMBER_RECHARGE_RECOVERY_LOCKED",
          "原核对请求正在处理，或当前账号已发生变化，请稍后重新读取",
          409,
        );
      }
      try {
        const grant = await requestSecondaryAuthorization(
          "RECONCILE_MEMBER_RECHARGE",
          frozenCredential,
        );
        const result = await confirmMemberRechargeReconciliation(
          recovery.command.workItemId,
          recovery.command.request,
          recovery.command.preview,
          grant.token,
        );
        if (!completeMemberRechargeRecovery(recovery, recoveryContext, result)) {
          markMemberRechargeOutcomeUnknown(recovery, recoveryContext);
          throw new ApiRequestError(
            "MEMBER_RECHARGE_RESULT_UNKNOWN",
            "核对服务返回的结果与原请求不一致，结果仍待确认；请使用原请求安全重试",
          );
        }
        return result;
      } catch (error) {
        if (memberRechargeOutcomeIsUnknown(error)) {
          markMemberRechargeOutcomeUnknown(recovery, recoveryContext);
        } else {
          clearMemberRechargeRecoveryAfterDefiniteRejection(recovery, recoveryContext, error);
        }
        throw error;
      } finally {
        finishMemberRechargeDispatch(recovery, recoveryContext);
      }
    },
    onMutate: () => {
      setCredential("");
      setCredentialWorkItemId(null);
      setFeedback(null);
    },
    onSuccess: async (result) => {
      await Promise.allSettled([
        queryClient.invalidateQueries({ queryKey: ["members"] }),
        queryClient.invalidateQueries({ queryKey: ["member-dashboard"] }),
      ]);
      const refreshed = await query.refetch();
      setFeedback(
        refreshed.isError
          ? {
              kind: "ERROR",
              message:
                "核对结果已经返回，但统一待办暂时无法重新读取；旧内容已隐藏，请重新读取后确认当前状态。",
            }
          : {
              kind: "SUCCESS",
              message:
                result.balanceEffect === "CREDITED"
                  ? result.cnyWalletCreditTotalFen > 0
                    ? `充值核对已完成，人民币余额增加 ${fee(result.cnyWalletCreditTotalFen)}；其他权益按结果明细增加。`
                    : "充值核对已完成，人民币余额不变；其他会员权益已按结果明细增加。"
                  : "充值核对已完成，所有会员钱包和权益保持不变，也没有重复入账。",
            },
      );
    },
    onError: (error) => {
      setFeedback({
        kind: "ERROR",
        message: memberRechargeOutcomeIsUnknown(error)
          ? "核对结果暂时无法确认。原处理选择、实际到账、支付凭证、核对说明、不可变预览和确认操作编号已经锁定；请重新输入当前账号密码，再使用原预览安全重试。"
          : failureMessage(error, "充值核对没有完成，请按提示检查后重试"),
      });
    },
    onSettled: () => setRecoveryView(readMemberRechargeRecovery(recoveryContext)),
  });
  const retryMemberReceivable = useMutation({
    mutationFn: async ({ recovery }: { recovery: MemberReceivableRetryRecovery }) => {
      if (!beginMemberReceivableRetryDispatch(recovery, memberReceivableIdentityKey)) {
        throw new ApiRequestError(
          "MEMBER_RECEIVABLE_RETRY_LOCKED",
          "原重新扣款请求正在处理，或当前账号已经变化，请稍后重新读取",
          409,
        );
      }
      try {
        const amountFen = recovery.command.baselineReview.amountFen;
        if (amountFen === null) {
          throw new ApiRequestError(
            "MEMBER_RECEIVABLE_AMOUNT_UNAVAILABLE",
            "待扣金额无法安全确认，系统没有发出重新扣款请求",
            400,
          );
        }
        const result = await retryMemberReceivableManualReview(
          recovery.command.workItemId,
          recovery.command.request,
          {
            amountFen,
            lastAttemptAt: recovery.command.baselineReview.lastAttemptAt,
          },
        );
        if (!completeMemberReceivableRetryRecovery(recovery, memberReceivableIdentityKey, result)) {
          markMemberReceivableRetryOutcomeUnknown(recovery, memberReceivableIdentityKey);
          throw new ApiRequestError(
            "MEMBER_RECEIVABLE_RETRY_RESULT_UNKNOWN",
            "重新扣款结果与原待扣事项无法安全对应，原请求已经保留",
          );
        }
        return result;
      } catch (error) {
        if (memberReceivableRetryOutcomeIsUnknown(error)) {
          markMemberReceivableRetryOutcomeUnknown(recovery, memberReceivableIdentityKey);
        } else {
          clearMemberReceivableRetryAfterDefiniteRejection(
            recovery,
            memberReceivableIdentityKey,
            error,
          );
        }
        throw error;
      } finally {
        finishMemberReceivableRetryDispatch(recovery, memberReceivableIdentityKey);
      }
    },
    onMutate: () => setFeedback(null),
    onSuccess: async (result) => {
      const refreshed = await query.refetch();
      setFeedback(
        refreshed.isError
          ? {
              kind: "ERROR",
              message:
                "重新扣款结果已经返回，但统一待办暂时无法重新读取；旧内容已隐藏，请重新读取后确认。",
            }
          : {
              kind: "SUCCESS",
              message:
                result.status === "RESOLVED"
                  ? `会员余额已成功扣除 ${fee(result.amountFen)}，这笔待扣事项已解决。${result.nextStep}`
                  : `重新扣款后仍未完成：${result.message}。${result.nextStep}`,
            },
      );
    },
    onError: (error) => {
      setFeedback({
        kind: "ERROR",
        message: memberReceivableRetryOutcomeIsUnknown(error)
          ? "重新扣款结果暂时无法确认。原应收业务号、原订单、会员、待扣金额和操作编号已经锁定；请使用原请求安全重试。"
          : failureMessage(error, "重新扣款没有完成，请按提示处理后重试"),
      });
    },
    onSettled: () =>
      setMemberReceivableRecoveryView(
        readMemberReceivableRetryRecovery(memberReceivableIdentityKey),
      ),
  });
  const submitManualResolutionApproval = useMutation({
    mutationFn: async ({ recovery }: { recovery: ManualReviewResolutionRecovery }) => {
      if (!beginManualReviewResolutionDispatch(recovery, manualResolutionIdentityKey)) {
        throw new ApiRequestError(
          "MANUAL_REVIEW_RESOLUTION_RECOVERY_LOCKED",
          "原待复核处置申请正在提交，或当前账号已经变化，请稍后重新读取",
          409,
        );
      }
      try {
        const recovered =
          recovery.state === "OUTCOME_UNKNOWN"
            ? await findManualReviewResolutionApprovalForRecovery(recovery.command)
            : null;
        const approval =
          recovered ?? (await createManualReviewResolutionApproval(recovery.command));
        if (!clearManualReviewResolutionRecovery(recovery, manualResolutionIdentityKey)) {
          throw new ApiRequestError(
            "MANUAL_REVIEW_RESOLUTION_APPROVAL_RESULT_UNKNOWN",
            "审批已返回，但浏览器无法安全解除原申请锁；请勿重复提交",
          );
        }
        return approval;
      } catch (error) {
        if (manualReviewResolutionRecoveryOutcomeIsUnknown(error)) {
          markManualReviewResolutionOutcomeUnknown(recovery, manualResolutionIdentityKey);
        } else {
          clearManualReviewResolutionRecovery(recovery, manualResolutionIdentityKey);
        }
        throw error;
      } finally {
        finishManualReviewResolutionDispatch(recovery, manualResolutionIdentityKey);
      }
    },
    onMutate: () => setFeedback(null),
    onSuccess: async (approval) => {
      await Promise.allSettled([
        queryClient.invalidateQueries({ queryKey: ["approval-summary"] }),
        queryClient.invalidateQueries({ queryKey: ["approvals"] }),
      ]);
      const refreshed = await query.refetch();
      setFeedback(
        refreshed.isError
          ? {
              kind: "ERROR",
              message:
                "审批申请已建立，但异常清单暂时无法重新读取；旧内容已隐藏，请在统一审批中心核对申请。",
            }
          : {
              kind: "SUCCESS",
              message:
                "审批申请已建立。当前业务尚未改变，请由另一位老板或店长在统一审批中心批准并执行。",
            },
      );
    },
    onError: (error) => {
      setFeedback({
        kind: "ERROR",
        message: manualReviewResolutionRecoveryOutcomeIsUnknown(error)
          ? "审批申请结果暂时无法确认。原事项、处理方式、说明和操作编号已经锁定；请使用下方原请求安全恢复，不能另建一笔。"
          : failureMessage(error, "待复核事项审批申请没有建立，请按提示处理后重试"),
      });
    },
    onSettled: () =>
      setManualResolutionRecoveryView(
        readManualReviewResolutionRecovery(manualResolutionIdentityKey),
      ),
  });
  useEffect(() => {
    setCredential("");
    setCredentialWorkItemId(null);
    setReconciliationDraft(null);
    setReconciliationDraftError(null);
    setFeedback(null);
    setRecoveryView(readMemberRechargeRecovery(recoveryContext));
  }, [recoveryContext]);
  useEffect(() => {
    setFeedback(null);
    setMemberReceivableRecoveryView(readMemberReceivableRetryRecovery(memberReceivableIdentityKey));
  }, [memberReceivableIdentityKey]);
  useEffect(() => {
    setFeedback(null);
    setManualResolutionRecoveryView(
      readManualReviewResolutionRecovery(manualResolutionIdentityKey),
    );
  }, [manualResolutionIdentityKey]);
  useEffect(() => {
    if (feedback) feedbackRef.current?.focus();
  }, [feedback]);

  const ownedRecovery = recoveryView.status === "OWNED" ? recoveryView.recovery : null;
  const reconciliationCommand =
    ownedRecovery?.command.kind === "MEMBER_RECHARGE_RECONCILIATION_PREVIEW" ||
    ownedRecovery?.command.kind === "MEMBER_RECHARGE_RECONCILIATION_CONFIRM"
      ? ownedRecovery.command
      : null;
  const reconciliationRecovery = reconciliationCommand ? ownedRecovery : null;
  const reconciliationPreview =
    reconciliationCommand?.kind === "MEMBER_RECHARGE_RECONCILIATION_CONFIRM"
      ? reconciliationCommand.preview
      : null;
  const createRecovery = ownedRecovery?.command.kind === "MEMBER_RECHARGE" ? ownedRecovery : null;
  const ownedMemberReceivableRecovery =
    memberReceivableRecoveryView.status === "OWNED" ? memberReceivableRecoveryView.recovery : null;
  const memberReceivableCommand = ownedMemberReceivableRecovery?.command ?? null;
  const ownedManualResolutionRecovery =
    manualResolutionRecoveryView.status === "OWNED" ? manualResolutionRecoveryView.recovery : null;
  const manualResolutionCommand = ownedManualResolutionRecovery?.command ?? null;
  const queryItems =
    query.isError || query.isFetching || recoveryView.status === "CONTEXT_MISMATCH"
      ? []
      : (query.data?.items ?? []);
  const itemsAfterRechargeRecovery = reconciliationCommand
    ? queryItems.filter(
        (item) =>
          item.category !== "MEMBER_RECHARGE_RECONCILIATION" ||
          item.workItemId !== reconciliationCommand.workItemId,
      )
    : queryItems;
  const itemsAfterMemberReceivableRecovery = memberReceivableCommand
    ? itemsAfterRechargeRecovery.filter(
        (item) =>
          item.category !== "MEMBER_RECEIVABLE" ||
          item.workItemId !== memberReceivableCommand.workItemId,
      )
    : itemsAfterRechargeRecovery;
  const itemsAfterManualResolutionRecovery = manualResolutionCommand
    ? itemsAfterMemberReceivableRecovery.filter(
        (item) => item.workItemId !== manualResolutionCommand.workItemId,
      )
    : itemsAfterMemberReceivableRecovery;
  const itemsAfterOtherMemberReceivable =
    memberReceivableRecoveryView.status === "OTHER_OPERATOR"
      ? itemsAfterManualResolutionRecovery.filter((item) => item.category !== "MEMBER_RECEIVABLE")
      : itemsAfterManualResolutionRecovery;
  const items =
    manualResolutionRecoveryView.status === "OTHER_OPERATOR"
      ? itemsAfterOtherMemberReceivable.filter(
          (item) =>
            item.category !== "PAYMENT_RECONCILIATION" &&
            item.category !== "ORDER_SYNC_REVIEW" &&
            item.category !== "COACH_SERVICE_REVIEW",
        )
      : itemsAfterOtherMemberReceivable;
  const memberReceivableWriteLocked =
    memberReceivableRecoveryView.status !== "NONE" || retryMemberReceivable.isPending;
  const setCredentialFor = (workItemId: string, value: string) => {
    setCredentialWorkItemId(workItemId);
    setCredential(value);
    setFeedback(null);
  };
  const createManualResolutionApproval = async (preview: ManualReviewResolutionPreview) => {
    const command: CreateManualReviewResolutionApprovalCommand = {
      workItemId: preview.workItemId,
      category: preview.category,
      resolutionCode: preview.resolutionCode,
      note: preview.note,
      sourceReferenceSha256: preview.sourceReferenceSha256,
      businessFactSha256: preview.businessFactSha256,
      commandSha256: preview.commandSha256,
      idempotencyKey: `manual-review-resolution-${crypto.randomUUID()}`,
    };
    const claimed = claimManualReviewResolutionRecovery(manualResolutionIdentityKey, command);
    setManualResolutionRecoveryView(claimed);
    if (
      claimed.status !== "OWNED" ||
      JSON.stringify(claimed.recovery.command) !== JSON.stringify(command)
    ) {
      throw new ApiRequestError(
        "MANUAL_REVIEW_RESOLUTION_RECOVERY_CONFLICT",
        claimed.status === "OTHER_OPERATOR"
          ? "另一个账号有一笔待复核处置申请结果待确认，当前账号不能覆盖"
          : "已有一笔待复核处置申请需要先恢复，当前请求没有提交",
        409,
      );
    }
    await submitManualResolutionApproval.mutateAsync({ recovery: claimed.recovery });
  };
  const draftFor = (item: MemberRechargeManualReviewItem): MemberRechargeReconciliationDraft =>
    reconciliationDraft?.workItemId === item.workItemId
      ? reconciliationDraft
      : initialMemberRechargeDraft(item);
  const changeReconciliationDraft = (
    item: MemberRechargeManualReviewItem,
    update: (current: MemberRechargeReconciliationDraft) => MemberRechargeReconciliationDraft,
  ) => {
    if (reconciliationRecovery) {
      if (
        reconciliationRecovery.state === "OUTCOME_UNKNOWN" ||
        !discardUnconfirmedMemberRechargeReconciliation(reconciliationRecovery, recoveryContext)
      ) {
        setFeedback({
          kind: "ERROR",
          message: "确认结果暂时未知，原预览已经锁定，不能修改处理选择、金额、支付凭证或说明。",
        });
        return;
      }
      setRecoveryView(readMemberRechargeRecovery(recoveryContext));
    }
    setReconciliationDraft((current) =>
      update(current?.workItemId === item.workItemId ? current : initialMemberRechargeDraft(item)),
    );
    setCredential("");
    setCredentialWorkItemId(null);
    setReconciliationDraftError(null);
    setFeedback(null);
    previewReconciliation.reset();
  };
  const selectReconciliationOutcome = (
    item: MemberRechargeManualReviewItem,
    outcome: MemberRechargeReconciliationOutcome,
  ) =>
    changeReconciliationDraft(item, (current) => ({
      ...current,
      outcome,
      actualReceivedFen:
        outcome === "CONFIRMED" ? memberRechargeYuanInputFromFen(item.amountFen) : "",
      paymentEvidenceReference: "",
      note: memberRechargeDefaultNote(outcome),
    }));
  const submitReconciliationPreview = (item: MemberRechargeManualReviewItem) => {
    if (!canReconcile || recoveryView.status !== "NONE") return;
    const draft = draftFor(item);
    const validation = memberRechargePreviewRequest(item, draft);
    if (validation.error || !validation.request) {
      setReconciliationDraftError(validation.error);
      return;
    }
    setReconciliationDraft(draft);
    const command = memberRechargeReconciliationPreviewCommand(item, validation.request);
    const claimed = claimMemberRechargeRecovery(recoveryContext, command);
    setRecoveryView(claimed);
    if (claimed.status !== "OWNED") {
      setFeedback({
        kind: "ERROR",
        message: "当前已有一笔充值或核对结果待确认，请先按原请求处理，不能改成另一笔。",
      });
      return;
    }
    previewReconciliation.mutate({ item, request: validation.request, recovery: claimed.recovery });
  };
  const retryReconciliation = () => {
    const frozenCredential =
      reconciliationCommand && credentialWorkItemId === reconciliationCommand.workItemId
        ? credential
        : "";
    if (!reconciliationRecovery || !frozenCredential) {
      setFeedback({ kind: "ERROR", message: "请重新输入当前账号密码，再使用原请求安全重试。" });
      return;
    }
    confirmReconciliation.mutate({
      recovery: reconciliationRecovery,
      credential: frozenCredential,
    });
  };
  const submitMemberReceivableRetry = (item: MemberReceivableManualReviewItem) => {
    if (!canRetryMemberReceivable) return;
    const command = memberReceivableRetryCommand(item, {
      idempotencyKey: `member-receivable-retry-${crypto.randomUUID()}`,
    });
    const claimed = claimMemberReceivableRetryRecovery(memberReceivableIdentityKey, command);
    if (claimed.status !== "OWNED") {
      setMemberReceivableRecoveryView(
        readMemberReceivableRetryRecovery(memberReceivableIdentityKey),
      );
      setFeedback({
        kind: "ERROR",
        message: "当前已有一笔重新扣款结果待确认，请先按原请求处理，不能改成另一笔。",
      });
      return;
    }
    setMemberReceivableRecoveryView(claimed);
    retryMemberReceivable.mutate({ recovery: claimed.recovery });
  };
  const retryOriginalMemberReceivableRequest = () => {
    if (!canRetryMemberReceivable || !ownedMemberReceivableRecovery) return;
    retryMemberReceivable.mutate({ recovery: ownedMemberReceivableRecovery });
  };
  if (filterInvalid) {
    return (
      <section className="approval-manual-panel" aria-label="异常待核对统一清单">
        <div className="approval-warning" role="alert">
          <strong>筛选范围无法安全确认</strong>
          <p>
            会员余额待补扣筛选必须同时带有正确分类和真实营业日。页面没有读取或显示全部异常，也没有沿用旧事项。
          </p>
          <button className="button secondary compact" type="button" onClick={onClearFilter}>
            清除筛选，查看全部异常
          </button>
        </div>
      </section>
    );
  }
  return (
    <section className="approval-manual-panel" aria-label="异常待核对统一清单">
      {filter ? (
        <div className="approval-info">
          <RefreshCw size={18} aria-hidden="true" />
          <div>
            <strong>仅显示营业日 {filter.businessDate} 形成的会员余额待补扣</strong>
            <p>这里只核对该营业日形成的待补扣事项；清除筛选后可查看其他异常，不会改变任何余额。</p>
            <button className="button secondary compact" type="button" onClick={onClearFilter}>
              清除筛选，查看全部异常
            </button>
          </div>
        </div>
      ) : null}
      <div className="approval-info">
        <AlertTriangle size={18} aria-hidden="true" />
        <div>
          <strong>
            {filter ? "这里仅处理会员余额待补扣" : "这里集中处理不能自动确认的经营记录"}
          </strong>
          <p>
            {filter
              ? "老板可查看待扣原因和进度；会员充值完成后，由店长或收银员在原事项上重新扣款。"
              : "普通异常保持只读；待核对充值由老板或店长选择金额一致、未到账、金额不符或付款凭证重复，先看确认预览，再用当前账号密码确认。"}
          </p>
        </div>
      </div>
      {recoveryView.status === "CONTEXT_MISMATCH" ? (
        <div className="approval-warning" role="alert">
          另一个账号有一笔充值或核对结果待确认。当前账号不能查看旧内容，也不能覆盖原请求。
        </div>
      ) : recoveryView.status === "RECOVERY_STORAGE_UNAVAILABLE" ? (
        <div className="approval-warning" role="alert">
          浏览器暂时无法安全保存核对请求，充值核对已锁定；请恢复浏览器存储后重新进入。
        </div>
      ) : createRecovery ? (
        <div className="approval-info">
          <ShieldCheck size={17} aria-hidden="true" />
          <p>当前账号还有一笔会员充值请求待确认，请先返回会员页按原请求处理。</p>
        </div>
      ) : null}
      {reconciliationRecovery && reconciliationCommand ? (
        <article className="approval-list-card approval-recovery" role="alert">
          <div>
            <span className="approval-kicker">会员充值核对</span>
            <h3>
              {reconciliationCommand.kind === "MEMBER_RECHARGE_RECONCILIATION_PREVIEW"
                ? "这笔充值正在生成确认预览"
                : recoveryView.canRetry
                  ? "这笔充值确认结果暂时未知"
                  : "请核对不可变预览后再次确认"}
            </h3>
            <p>
              {reconciliationCommand.kind === "MEMBER_RECHARGE_RECONCILIATION_PREVIEW"
                ? "预览不会改变会员余额；如果页面曾意外关闭，可以放弃这份未确认预览后重新生成。"
                : recoveryView.canRetry
                  ? "原处理选择、实际到账、支付凭证和预览已经锁定，只能按完全相同的预览重试。"
                  : "下方预览尚未生效；修改任何核对内容都会清除旧预览，必须重新生成。"}
            </p>
          </div>
          <dl className="approval-card-facts">
            <div>
              <dt>会员</dt>
              <dd>{reconciliationCommand.baselineReview.memberName}</dd>
            </div>
            <div>
              <dt>原申请金额</dt>
              <dd>
                {memberRechargeRequestedAmountLabel(reconciliationCommand.baselineReview.amountFen)}
              </dd>
            </div>
            <div>
              <dt>收款渠道</dt>
              <dd>{memberRechargeMethodLabel(reconciliationCommand.baselineReview.method)}</dd>
            </div>
          </dl>
          {canReconcile &&
          reconciliationCommand.kind === "MEMBER_RECHARGE_RECONCILIATION_PREVIEW" ? (
            <div className="approval-actions">
              <button
                className="button secondary compact"
                type="button"
                disabled={previewReconciliation.isPending}
                onClick={() => {
                  if (
                    discardUnconfirmedMemberRechargeReconciliation(
                      reconciliationRecovery,
                      recoveryContext,
                    )
                  ) {
                    setRecoveryView(readMemberRechargeRecovery(recoveryContext));
                  }
                }}
              >
                放弃未确认预览，重新填写
              </button>
            </div>
          ) : canReconcile && reconciliationPreview ? (
            <MemberRechargeReconciliationEditor
              item={reconciliationCommand.baselineReview}
              draft={
                reconciliationDraft?.workItemId === reconciliationCommand.workItemId
                  ? reconciliationDraft
                  : {
                      workItemId: reconciliationCommand.workItemId,
                      outcome: reconciliationPreview.outcome,
                      actualReceivedFen:
                        reconciliationPreview.actualReceivedFen === null
                          ? ""
                          : memberRechargeYuanInputFromFen(reconciliationPreview.actualReceivedFen),
                      paymentEvidenceReference: "",
                      note: reconciliationPreview.note,
                    }
              }
              preview={reconciliationPreview}
              credentialId={`${credentialId}-recovery`}
              credential={
                credentialWorkItemId === reconciliationCommand.workItemId ? credential : ""
              }
              draftError={reconciliationDraftError}
              locked={reconciliationRecovery.state === "OUTCOME_UNKNOWN"}
              pending={confirmReconciliation.isPending}
              onOutcomeChange={(outcome) =>
                selectReconciliationOutcome(reconciliationCommand.baselineReview, outcome)
              }
              onDraftChange={(update) =>
                changeReconciliationDraft(reconciliationCommand.baselineReview, (current) => ({
                  ...current,
                  ...update,
                }))
              }
              onPreview={() => undefined}
              onCredentialChange={(value) =>
                setCredentialFor(reconciliationCommand.workItemId, value)
              }
              onConfirm={retryReconciliation}
            />
          ) : null}
        </article>
      ) : null}
      {memberReceivableRecoveryView.status === "OTHER_OPERATOR" ? (
        <div className="approval-warning" role="alert">
          另一个账号有一笔会员余额重新扣款结果待确认。当前账号不能查看旧内容，也不能覆盖原请求。
        </div>
      ) : memberReceivableRecoveryView.status === "RECOVERY_STORAGE_UNAVAILABLE" ? (
        <div className="approval-warning" role="alert">
          浏览器暂时无法安全保存重新扣款请求，会员余额待扣操作已锁定；请恢复浏览器存储后重新进入。
        </div>
      ) : null}
      {ownedMemberReceivableRecovery && memberReceivableCommand ? (
        <article className="approval-list-card approval-recovery" role="alert">
          <div>
            <span className="approval-kicker">会员余额待扣</span>
            <h3>
              {retryMemberReceivable.isPending ? "正在按原金额重新扣款" : "重新扣款结果暂时未知"}
            </h3>
            <p>原应收业务号、原订单、会员、待扣金额和操作编号已经锁定，不能改成另一笔。</p>
          </div>
          <dl className="approval-card-facts">
            <div>
              <dt>应收业务号</dt>
              <dd>{memberReceivableCommand.baselineReview.receivableNumber}</dd>
            </div>
            <div>
              <dt>原订单</dt>
              <dd>{memberReceivableCommand.baselineReview.orderNumber}</dd>
            </div>
            <div>
              <dt>会员</dt>
              <dd>
                {memberReceivableCommand.baselineReview.memberName} ·{" "}
                {memberReceivableCommand.baselineReview.memberNumber}
              </dd>
            </div>
            <div>
              <dt>待扣金额</dt>
              <dd>{fee(memberReceivableCommand.baselineReview.amountFen ?? 0)}</dd>
            </div>
            <div>
              <dt>原失败原因</dt>
              <dd>{memberReceivableCommand.baselineReview.reason}</dd>
            </div>
          </dl>
          {canRetryMemberReceivable ? (
            <button
              className="button primary compact"
              type="button"
              disabled={retryMemberReceivable.isPending || !memberReceivableRecoveryView.canRetry}
              onClick={retryOriginalMemberReceivableRequest}
            >
              使用原请求安全重试
            </button>
          ) : (
            <div className="approval-info">
              <ShieldCheck size={17} aria-hidden="true" />
              <p>老板可以查看待扣原因和处理进度；重新扣款需由收银员或店长完成。</p>
            </div>
          )}
        </article>
      ) : null}
      {manualResolutionRecoveryView.status === "OTHER_OPERATOR" ? (
        <div className="approval-warning" role="alert">
          另一个账号有一笔待复核处置审批结果待确认。当前账号不能查看旧请求，也不能覆盖或新建同类申请。
        </div>
      ) : manualResolutionRecoveryView.status === "RECOVERY_STORAGE_UNAVAILABLE" ? (
        <div className="approval-warning" role="alert">
          浏览器暂时无法安全保存待复核处置请求，付款、订单同步和助教服务处置申请已锁定；请恢复浏览器存储后重新进入。
        </div>
      ) : null}
      {ownedManualResolutionRecovery && manualResolutionCommand ? (
        <article className="approval-list-card approval-recovery" role="alert">
          <div>
            <span className="approval-kicker">
              {manualReviewCategoryLabel(manualResolutionCommand.category)}
            </span>
            <h3>
              {submitManualResolutionApproval.isPending
                ? "正在恢复原审批申请"
                : "这笔审批申请结果暂时未知"}
            </h3>
            <p>
              原事项、处理方式、核对说明、业务快照和操作编号已经锁定；恢复只查询或重放原请求，不会另建一笔。
            </p>
          </div>
          <dl className="approval-card-facts">
            <div>
              <dt>原事项</dt>
              <dd>{manualReviewCategoryLabel(manualResolutionCommand.category)}（系统已锁定）</dd>
            </div>
            <div>
              <dt>处理方式</dt>
              <dd>{manualReviewResolutionCodeLabel(manualResolutionCommand.resolutionCode)}</dd>
            </div>
            <div>
              <dt>核对说明</dt>
              <dd>{manualResolutionCommand.note}</dd>
            </div>
          </dl>
          {canReconcile ? (
            <button
              className="button primary compact"
              type="button"
              disabled={submitManualResolutionApproval.isPending}
              onClick={() =>
                submitManualResolutionApproval.mutate({
                  recovery: ownedManualResolutionRecovery,
                })
              }
            >
              使用原请求安全恢复
            </button>
          ) : null}
        </article>
      ) : null}
      {feedback ? (
        <div
          className={feedback.kind === "SUCCESS" ? "success-band" : "approval-warning"}
          ref={feedbackRef}
          role={feedback.kind === "ERROR" ? "alert" : undefined}
          tabIndex={-1}
        >
          {feedback.message}
        </div>
      ) : null}
      {!query.isError && query.data?.memberRechargeCoverage === "LOCAL_ONLY" ? (
        <output className="approval-info">
          <RefreshCw size={17} aria-hidden="true" />
          <p>
            本机已有异常事项仍可查看；会员充值待核对清单暂时无法全部更新，请稍后重新读取。这只影响会员充值待核对清单，不影响其他经营页面的在线状态判断。
          </p>
        </output>
      ) : null}
      {query.isError ? (
        <div className="approval-warning" role="alert">
          {filter
            ? `营业日 ${filter.businessDate} 形成的会员余额待补扣暂时无法安全读取；旧筛选结果已隐藏。这只影响当前局部清单，不表示整套 ERP 离线。`
            : "异常事项暂时无法安全读取，旧内容已隐藏。"}
          <button
            className="button secondary compact"
            type="button"
            onClick={() => query.refetch()}
          >
            {filter ? "重新读取本日待补扣" : "重新读取"}
          </button>
        </div>
      ) : query.isFetching ? (
        <div className="empty-state">
          {filter
            ? `正在读取营业日 ${filter.businessDate} 形成的会员余额待补扣`
            : "正在读取异常事项"}
        </div>
      ) : recoveryView.status === "CONTEXT_MISMATCH" ? null : items.length ? (
        <div className="approval-list">
          {items.map((item) => (
            <article className="approval-list-card" key={item.itemKey}>
              <div>
                <span className="approval-kicker">{manualReviewCategoryLabel(item.category)}</span>
                <h3>{item.title}</h3>
                <p>{item.summary}</p>
              </div>
              <dl className="approval-card-facts">
                <div>
                  <dt>金额</dt>
                  <dd>
                    {item.category === "CUSTOMER_STORAGE_EXCEPTION" ||
                    item.category === "CUSTOMER_RENTAL_EXCEPTION"
                      ? "不涉及资金"
                      : item.category === "COACH_SERVICE_REVIEW" || item.amountFen === null
                        ? "需要核对"
                        : fee(item.amountFen)}
                  </dd>
                </div>
                <div>
                  <dt>发生时间</dt>
                  <dd>{time(item.occurredAt ?? item.createdAt)}</dd>
                </div>
                <div>
                  <dt>下一步</dt>
                  <dd>{item.nextStep}</dd>
                </div>
                {item.category === "MEMBER_RECHARGE_RECONCILIATION" ? (
                  <>
                    <div>
                      <dt>会员</dt>
                      <dd>{item.memberName}</dd>
                    </div>
                    <div>
                      <dt>收款渠道</dt>
                      <dd>{memberRechargeMethodLabel(item.method)}</dd>
                    </div>
                    <div>
                      <dt>当前状态</dt>
                      <dd>{memberRechargeStatusLabel(item.rechargeStatus)}</dd>
                    </div>
                    <div>
                      <dt>原登记说明</dt>
                      <dd>{item.originalRequestNote || "未填写"}</dd>
                    </div>
                    <div>
                      <dt>最新核对说明</dt>
                      <dd>{item.latestReconciliationNote ?? "尚未核对"}</dd>
                    </div>
                    <div>
                      <dt>余额后果</dt>
                      <dd>{memberRechargeConsequence(item)}</dd>
                    </div>
                  </>
                ) : item.category === "MEMBER_RECEIVABLE" ? (
                  <>
                    <div>
                      <dt>应收业务号</dt>
                      <dd>{item.receivableNumber}</dd>
                    </div>
                    <div>
                      <dt>原订单</dt>
                      <dd>{item.orderNumber}</dd>
                    </div>
                    <div>
                      <dt>会员</dt>
                      <dd>
                        {item.memberName} · {item.memberNumber}
                      </dd>
                    </div>
                    <div>
                      <dt>未扣金额</dt>
                      <dd>{fee(item.amountFen ?? 0)}</dd>
                    </div>
                    <div>
                      <dt>未扣原因</dt>
                      <dd>{item.reason}</dd>
                    </div>
                    <div>
                      <dt>最近尝试</dt>
                      <dd>
                        {time(item.lastAttemptAt)} · 已尝试 {item.attemptCount} 次
                      </dd>
                    </div>
                  </>
                ) : item.category === "GROUP_BUY_REDEMPTION_REVIEW" ? (
                  <>
                    <div>
                      <dt>复核类型</dt>
                      <dd>{groupBuyManualReviewKindLabel(item.reviewKind)}</dd>
                    </div>
                    <div>
                      <dt>营业日</dt>
                      <dd>{item.businessDate ?? "需要核对"}</dd>
                    </div>
                    <div>
                      <dt>记录来源</dt>
                      <dd>{item.offlineOrigin ? "离线核销记录" : "在线团购记录"}</dd>
                    </div>
                  </>
                ) : item.category === "CUSTOMER_STORAGE_EXCEPTION" ? (
                  <>
                    <div>
                      <dt>寄存编号</dt>
                      <dd>{item.storageNumber}</dd>
                    </div>
                    <div>
                      <dt>会员</dt>
                      <dd>
                        {item.memberName} · {item.memberNumber}
                      </dd>
                    </div>
                    <div>
                      <dt>异常商品</dt>
                      <dd>
                        {item.productName} · {item.remainingQuantity}
                        {item.unit}
                      </dd>
                    </div>
                    <div>
                      <dt>处理边界</dt>
                      <dd>确认本待办不会自动赔付、退款、报损或改变库存</dd>
                    </div>
                  </>
                ) : item.category === "CUSTOMER_RENTAL_EXCEPTION" ? (
                  <>
                    <div>
                      <dt>租借编号</dt>
                      <dd>{item.rentalNumber}</dd>
                    </div>
                    <div>
                      <dt>台号</dt>
                      <dd>{item.tableNumber}</dd>
                    </div>
                    <div>
                      <dt>订单</dt>
                      <dd>{item.orderNumber}</dd>
                    </div>
                    <div>
                      <dt>物品</dt>
                      <dd>{item.productName}</dd>
                    </div>
                    <div>
                      <dt>剩余</dt>
                      <dd>
                        {item.remainingQuantity}
                        {item.unit}
                      </dd>
                    </div>
                    <div>
                      <dt>异常</dt>
                      <dd>{customerRentalExceptionLabel(item.exceptionType)}</dd>
                    </div>
                    <div>
                      <dt>预计归还</dt>
                      <dd>{time(item.expectedReturnAt)}</dd>
                    </div>
                  </>
                ) : null}
              </dl>
              {item.category === "MEMBER_RECHARGE_RECONCILIATION" ? (
                canReconcile && recoveryView.status === "NONE" ? (
                  <MemberRechargeReconciliationEditor
                    item={item}
                    draft={draftFor(item)}
                    preview={null}
                    credentialId={`${credentialId}-${item.workItemId}`}
                    credential=""
                    draftError={
                      reconciliationDraft?.workItemId === item.workItemId
                        ? reconciliationDraftError
                        : null
                    }
                    locked={false}
                    pending={previewReconciliation.isPending}
                    onOutcomeChange={(outcome) => selectReconciliationOutcome(item, outcome)}
                    onDraftChange={(update) =>
                      changeReconciliationDraft(item, (current) => ({ ...current, ...update }))
                    }
                    onPreview={() => submitReconciliationPreview(item)}
                    onCredentialChange={() => undefined}
                    onConfirm={() => undefined}
                  />
                ) : (
                  <div className="approval-info">
                    <ShieldCheck size={17} aria-hidden="true" />
                    <p>
                      {canReconcile
                        ? "当前有原充值或核对请求待确认，这张待办暂时只读。"
                        : "收银员可以查看原申请、状态和余额后果，只有老板或店长可以处理。"}
                    </p>
                  </div>
                )
              ) : item.category === "MEMBER_RECEIVABLE" ? (
                canRetryMemberReceivable && memberReceivableRecoveryView.status === "NONE" ? (
                  <div className="approval-actions">
                    <button
                      className="button primary"
                      type="button"
                      disabled={memberReceivableWriteLocked}
                      onClick={() => submitMemberReceivableRetry(item)}
                    >
                      充值后重新扣款
                    </button>
                  </div>
                ) : (
                  <div className="approval-info">
                    <ShieldCheck size={17} aria-hidden="true" />
                    <p>
                      {canRetryMemberReceivable
                        ? "当前有原重新扣款请求待确认，这张待办暂时只读。"
                        : "老板可以查看待扣金额、失败原因和处理进度；重新扣款需由收银员或店长完成。"}
                    </p>
                  </div>
                )
              ) : item.category === "PAYMENT_RECONCILIATION" ||
                item.category === "ORDER_SYNC_REVIEW" ||
                item.category === "COACH_SERVICE_REVIEW" ? (
                <ManualReviewResolutionPanel
                  item={item as ManualReviewResolutionItem}
                  operator={operator}
                  effectiveRoles={effectiveRoles}
                  disabled={
                    manualResolutionRecoveryView.status !== "NONE" ||
                    submitManualResolutionApproval.isPending
                  }
                  onPreview={(workItemId, request) =>
                    previewManualReviewResolution(workItemId, request)
                  }
                  onSubmitApproval={createManualResolutionApproval}
                />
              ) : item.category === "GROUP_BUY_REDEMPTION_REVIEW" ? (
                item.reviewKind === "DAILY_RECONCILIATION_DIFFERENCE" ? (
                  canReconcile ? (
                    <div className="approval-actions">
                      <NavLink
                        className="button primary"
                        to={`/group-buy?mode=RECONCILE&workItemId=${encodeURIComponent(item.workItemId)}`}
                      >
                        打开团购差异并处理
                      </NavLink>
                      <p>
                        店长主责，老板同步；请回到原团购日结差异核对完整渠道账单后处理，工作中心不重复建立第二套处理口径。
                      </p>
                    </div>
                  ) : (
                    <div className="approval-info">
                      <ShieldCheck size={17} aria-hidden="true" />
                      <p>
                        收银员可以查看差异金额、营业日和处理进度；请由老板或店长处理，工作中心不提供写入口。
                      </p>
                    </div>
                  )
                ) : (
                  <div className="approval-info">
                    <ShieldCheck size={17} aria-hidden="true" />
                    <p>
                      {item.reviewKind === "OFFLINE_PRECHECK_REJECTED"
                        ? "离线核销预检驳回只读事项"
                        : "补偿结果待确认只读事项"}
                      ：请按当前待办的下一步恢复或核对原核销结果，不能在团购日结差异页处置。
                    </p>
                  </div>
                )
              ) : item.category === "CUSTOMER_STORAGE_EXCEPTION" ? (
                <div className="approval-actions">
                  <NavLink
                    className="button primary"
                    to={`/storage?workItemId=${encodeURIComponent(item.workItemId)}`}
                  >
                    {canReconcile ? "打开寄存记录并处理" : "打开寄存记录查看"}
                  </NavLink>
                  <p>
                    {canReconcile
                      ? "请回到原寄存记录核对完整历史后确认或驳回，工作中心不重复建立第二套处理口径。"
                      : "收银员可查看寄存和异常历史；只有老板或店长可以确认或驳回。"}
                  </p>
                </div>
              ) : item.category === "CUSTOMER_RENTAL_EXCEPTION" ? (
                <div className="approval-actions">
                  <NavLink
                    className="button primary"
                    to={`/rentals?workItemId=${encodeURIComponent(item.workItemId)}`}
                  >
                    {canReconcile ? "打开租借记录并处理" : "打开租借记录查看"}
                  </NavLink>
                  <p>
                    {canReconcile
                      ? "请回到原租借记录核对完整历史后确认或驳回，工作中心不重复建立第二套处理口径。"
                      : "收银员可查看租借和异常历史；只有老板或店长可以确认或驳回。"}
                  </p>
                </div>
              ) : null}
            </article>
          ))}
        </div>
      ) : (
        <div className="empty-state">
          {filter
            ? `营业日 ${filter.businessDate} 当前没有形成会员余额待补扣事项；这里只表示本筛选范围为空。`
            : "当前没有异常待核对事项"}
        </div>
      )}
    </section>
  );
}

function serviceRequestTypeLabel(type: ServiceWorkItemReadModel["source"]["type"]): string {
  if (type === "SERVICE") return "呼叫服务";
  if (type === "CHECKOUT") return "请求结账";
  return type === "PRODUCT_DELIVERY" ? "桌边商品送达" : "结账后清洁";
}

function serviceRequestDestination(eventId: string): string {
  const search = new URLSearchParams({ eventId });
  return `/staff?${search.toString()}`;
}

function serviceSlaLabel(item: ServiceWorkItemReadModel): string {
  switch (item.slaStatus) {
    case "NOT_CONFIGURED":
      return "尚未设置处理时限";
    case "ON_TIME":
      return "处理时限内";
    case "DUE_SOON":
      return "即将到期";
    case "OVERDUE":
      return "已逾期";
    case "COMPLETED_ON_TIME":
      return "按时完成";
    case "COMPLETED_LATE":
      return "逾期后完成";
    case "FAILED":
      return "处理失败待核对";
  }
}

function serviceEscalationLabel(item: ServiceWorkItemReadModel): string {
  if (item.escalationStatus === "OWNER_NOTIFIED") return "已提醒老板";
  if (item.escalationStatus === "MANAGER_NOTIFIED") return "已提醒店长";
  return "尚未升级提醒";
}

function serviceCoverageLabel(item: ServiceWorkItemReadModel): string {
  if (item.coverageStatus === "UNATTENDED") return "当前无人当班";
  if (item.coverageStatus === "UNKNOWN") return "当班情况无法确认";
  return "当前有人可处理";
}

function servicePriorityLabel(priority: ServiceWorkItemReadModel["priority"]): string {
  if (priority === "URGENT") return "紧急";
  if (priority === "HIGH") return "较紧急";
  if (priority === "LOW") return "普通靠后";
  return "普通";
}

const serviceRoutingRoleLabels: Record<ServiceWorkItemReadModel["primaryRole"], string> = {
  MANAGER: "店长",
  CASHIER: "收银员",
  WAITER: "服务员",
};

export type TableCleaningWorkItem = ServiceWorkItemReadModel & {
  source: Extract<ServiceWorkItemReadModel["source"], { kind: "TABLE_CLEANING" }>;
};

type NonKioskProductOrderWorkItem = ServiceWorkItemReadModel & {
  source: Exclude<ServiceWorkItemReadModel["source"], { kind: "KIOSK_PRODUCT_ORDER" }>;
};

export function tableCleaningLightOffLabel(item: TableCleaningWorkItem): string {
  const lightOff = item.source.lightOff;
  switch (lightOff.status) {
    case "NOT_REQUIRED":
      return "本桌无需关灯。";
    case "PENDING":
      return lightOff.evidence === "MOCK"
        ? "模拟设备：模拟关灯指令处理中（不代表真实灯具已关）。"
        : "关灯指令处理中，暂不能当成已关灯。";
    case "CONFIRMED":
      return lightOff.evidence === "MOCK"
        ? "模拟设备：模拟关灯指令已完成（不代表真实灯具已关）。"
        : "设备结果：已确认关灯。";
    case "FAILED":
      return lightOff.evidence === "MOCK"
        ? "模拟设备：模拟关灯失败，请按现场情况检查；不影响清洁完成记录。"
        : "设备关灯失败，请现场检查；不影响清洁完成记录。";
    case "UNKNOWN":
      return lightOff.evidence === "MOCK"
        ? "模拟设备：模拟关灯结果未知（不代表真实灯具状态）。"
        : "设备关灯结果未知，请现场检查；系统不会当成已关灯。";
  }
}

export interface TableCleaningWorkItemCompletionController {
  view: TableCleaningWorkItemRecoveryView;
  pending: boolean;
  error: Error | null;
  message: string;
  confirmedWorkItemId: string | null;
  submit: (item: TableCleaningWorkItem) => void;
  retry: () => void;
}

export function useTableCleaningWorkItemCompletion(
  operator: OperatorIdentity,
): TableCleaningWorkItemCompletionController {
  const queryClient = useQueryClient();
  const identityKey = tableCleaningWorkItemIdentityKey(operator);
  const identityKeyRef = useRef(identityKey);
  identityKeyRef.current = identityKey;
  const [viewState, setViewState] = useState<{
    identityKey: string;
    view: TableCleaningWorkItemRecoveryView;
  }>(() => ({ identityKey, view: readTableCleaningWorkItemRecovery(identityKey) }));
  const [message, setMessage] = useState("");
  const [confirmedWorkItemId, setConfirmedWorkItemId] = useState<string | null>(null);
  const view =
    viewState.identityKey === identityKey
      ? viewState.view
      : ({ status: "OTHER_OPERATOR" } as const);
  const mutation = useMutation<
    TableCleaningWorkItemCompletionReceipt,
    Error,
    TableCleaningWorkItemRecovery
  >({
    mutationFn: async (recovery) => {
      if (
        identityKeyRef.current !== identityKey ||
        !beginTableCleaningWorkItemDispatch(recovery, identityKey)
      ) {
        throw new ApiRequestError(
          "TABLE_CLEANING_RECOVERY_NOT_DURABLE",
          "原清洁操作无法安全锁定，系统没有再次提交",
        );
      }
      try {
        const receipt = await completeTableCleaningWorkItem(recovery.command.request);
        if (identityKeyRef.current !== identityKey) {
          throw new ApiRequestError(
            "OPERATOR_IDENTITY_CHANGED",
            "账号已切换；原账号的清洁结果需要回到工作中心核对",
          );
        }
        if (!clearTableCleaningWorkItemRecovery(recovery, identityKey)) {
          throw new ApiRequestError(
            "TABLE_CLEANING_RECOVERY_CLEAR_FAILED",
            "服务已返回清洁结果，但本机安全记录无法清除；请先刷新状态，不要再次操作",
          );
        }
        return receipt;
      } catch (error) {
        if (tableCleaningWorkItemOutcomeIsUnknown(error, recovery.state)) {
          markTableCleaningWorkItemOutcomeUnknown(recovery, identityKey);
        } else {
          clearTableCleaningWorkItemRecovery(recovery, identityKey);
        }
        throw error;
      } finally {
        finishTableCleaningWorkItemDispatch(recovery, identityKey);
      }
    },
    onSuccess: async (_receipt, recovery) => {
      setConfirmedWorkItemId(recovery.command.request.workItemId);
      setMessage("清洁完成已确认；下一步由店长或收银员最终释放球台。");
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["service-work-items"] }),
        queryClient.invalidateQueries({ queryKey: ["frontdesk-operating-facts"] }),
        queryClient.invalidateQueries({ queryKey: ["tables"] }),
      ]);
    },
    onSettled: () => {
      setViewState({ identityKey, view: readTableCleaningWorkItemRecovery(identityKey) });
    },
  });

  useEffect(() => {
    mutation.reset();
    setMessage("");
    setConfirmedWorkItemId(null);
    setViewState({ identityKey, view: readTableCleaningWorkItemRecovery(identityKey) });
  }, [identityKey, mutation.reset]);

  const dispatch = (recovery: TableCleaningWorkItemRecovery): void => {
    if (mutation.isPending) return;
    mutation.mutate(recovery);
  };

  const submit = (item: TableCleaningWorkItem): void => {
    setMessage("");
    mutation.reset();
    const command: TableCleaningWorkItemFrozenCommand = {
      tableNumber: item.tableNumber,
      request: {
        workItemId: item.workItemId,
        expectedWorkItemVersion: item.workItemVersion,
        tableId: item.source.tableId,
        orderId: item.source.orderId,
        request: {
          expectedTableRevision: item.source.tableRevision,
          expectedSessionId: item.source.sessionId,
          expectedAggregateVersion: item.source.aggregateVersion,
          idempotencyKey: crypto.randomUUID(),
        },
      },
    };
    const claimed = claimTableCleaningWorkItemRecovery(identityKey, command);
    setViewState({ identityKey, view: claimed });
    if (
      claimed.status === "OWNED" &&
      claimed.recovery.command.request.request.idempotencyKey ===
        command.request.request.idempotencyKey
    ) {
      dispatch(claimed.recovery);
    }
  };

  return {
    view,
    pending: mutation.isPending,
    error: mutation.error instanceof Error ? mutation.error : null,
    message,
    confirmedWorkItemId,
    submit,
    retry: () => {
      if (view.status === "OWNED") dispatch(view.recovery);
    },
  };
}

export function TableCleaningCompletionFeedback({
  completion,
}: {
  completion: TableCleaningWorkItemCompletionController;
}): React.JSX.Element | null {
  return (
    <>
      {completion.message ? (
        <output className="success-band">
          <CheckCircle2 size={17} aria-hidden="true" />
          <span>{completion.message}</span>
        </output>
      ) : null}
      {completion.view.status === "OWNED" ? (
        <div className="approval-warning" role="alert">
          <span>
            {completion.view.recovery.state === "OUTCOME_UNKNOWN"
              ? `${completion.view.recovery.command.tableNumber} 号台清洁结果尚未确认，已保留操作时看到的状态和原请求；安全重试不会重复确认。`
              : `${completion.view.recovery.command.tableNumber} 号台正在确认清洁，请勿重复操作。`}
          </span>
          {completion.view.recovery.state === "OUTCOME_UNKNOWN" ? (
            <button
              className="button secondary compact"
              type="button"
              disabled={completion.pending}
              onClick={completion.retry}
            >
              <RefreshCw size={15} aria-hidden="true" />
              {completion.pending ? "正在核对" : "使用原请求安全重试"}
            </button>
          ) : null}
        </div>
      ) : completion.view.status === "OTHER_OPERATOR" ? (
        <div className="approval-warning" role="alert">
          另一个登录账号有一笔清洁结果待确认。请切回原账号处理，本账号不会重复提交。
        </div>
      ) : completion.view.status === "RECOVERY_STORAGE_UNAVAILABLE" ? (
        <div className="error-band" role="alert">
          本机暂时不能安全保存清洁操作，已暂停确认。请保留本页并联系店长核对。
        </div>
      ) : null}
      {completion.error ? (
        <div className="approval-warning" role="alert">
          {completion.view.status === "OWNED"
            ? "清洁结果尚未确认，请使用保留的原请求安全重试。"
            : failureMessage(completion.error, "清洁操作未完成，请刷新后重新核对。")}
        </div>
      ) : null}
    </>
  );
}

export function TableCleaningWorkItemAction({
  item,
  effectiveRoles,
  completion,
}: {
  item: TableCleaningWorkItem;
  effectiveRoles: readonly OperatorRole[];
  completion: TableCleaningWorkItemCompletionController;
}): React.JSX.Element {
  const roleCanConfirm = effectiveRoles.some(
    (role) => role === "WAITER" || role === "MANAGER" || role === "CASHIER",
  );
  if (completion.confirmedWorkItemId === item.workItemId) {
    return <p className="field-help">清洁已确认，正在刷新当前球台状态。</p>;
  }
  if (!item.viewerActions.canComplete || !roleCanConfirm) {
    return (
      <p className="field-help">
        {roleCanConfirm ? item.viewerActions.unavailableReason : "当前账号只能查看清洁状态。"}
      </p>
    );
  }
  return (
    <button
      className="button primary compact"
      type="button"
      disabled={completion.pending || completion.view.status !== "NONE"}
      onClick={() => completion.submit(item)}
    >
      <CheckCircle2 size={15} aria-hidden="true" />
      {completion.pending ? "正在确认清洁" : "确认清洁完成"}
    </button>
  );
}

export function WorkCenterServicePanel({
  operator,
  effectiveRoles,
  personalSession,
  simulated,
  data,
  isLoading,
  isError,
  isFetching,
  onRetry,
}: {
  operator: OperatorIdentity;
  effectiveRoles: readonly OperatorRole[];
  personalSession: boolean;
  simulated: boolean;
  data: ServiceWorkItemListReadModel | undefined;
  isLoading: boolean;
  isError: boolean;
  isFetching: boolean;
  onRetry: () => void;
}): React.JSX.Element {
  const items = data?.items ?? [];
  const otherItems = items.filter(
    (item): item is NonKioskProductOrderWorkItem => item.source.kind !== "KIOSK_PRODUCT_ORDER",
  );
  const hasTrustedData = data !== undefined;
  const cleaningCompletion = useTableCleaningWorkItemCompletion(operator);
  return (
    <section className="approval-manual-panel" aria-label="现场服务待办清单">
      <div className="approval-info">
        <BellRing size={18} aria-hidden="true" />
        <div>
          <strong>现场请求处理仍回到原球台；结账后清洁可在这里确认</strong>
          <p>本页不会收款、退款或释放球台。顾客呼叫和结账请求仍要打开原球台重新核对。</p>
        </div>
      </div>
      <ServiceDutyWorkCenterPanel
        operator={operator}
        effectiveRoles={effectiveRoles}
        personalSession={personalSession}
        simulated={simulated}
      />
      <TableCleaningCompletionFeedback completion={cleaningCompletion} />
      <KioskProductOrderWorkItemPanel
        operator={operator}
        effectiveRoles={effectiveRoles}
        items={items}
        disabled={isError}
      />
      {isError ? (
        <div className="approval-warning" role="alert">
          <span>
            {hasTrustedData
              ? "现场待办更新失败，正在显示上次读取结果；现场状态可能已经变化。"
              : "现场待办暂时无法安全读取，旧内容已隐藏。"}
          </span>
          <button className="button secondary compact" type="button" onClick={onRetry}>
            <RefreshCw size={15} aria-hidden="true" /> {isFetching ? "重新读取中" : "重新读取"}
          </button>
        </div>
      ) : null}
      {isLoading && !hasTrustedData ? (
        <div className="empty-state">正在读取现场待办</div>
      ) : !hasTrustedData ? null : items.length === 0 ? (
        <div className="empty-state">当前没有现场待办</div>
      ) : (
        <div className="approval-list">
          {otherItems.map((item) => (
            <article className="approval-list-card" key={item.workItemId}>
              <div>
                <span className="approval-kicker">
                  {item.source.kind === "TABLE_CLEANING" ? (
                    <CheckCircle2 size={14} aria-hidden="true" />
                  ) : item.source.type === "SERVICE" ? (
                    <BellRing size={14} aria-hidden="true" />
                  ) : (
                    <Banknote size={14} aria-hidden="true" />
                  )}
                  {item.tableNumber} 号台 · {serviceRequestTypeLabel(item.source.type)}
                </span>
                <h3>{item.statusLabel}</h3>
                <p>{item.statusExplanation}</p>
                <p>
                  负责人：
                  {item.source.kind === "TABLE_CLEANING"
                    ? (item.assigneeDisplayName ?? "可执行清洁的服务员")
                    : (item.assigneeDisplayName ?? "尚未接单")}
                </p>
                {item.source.kind === "TABLE_CLEANING" ? (
                  <output className="field-help">
                    灯光关闭：{tableCleaningLightOffLabel(item as TableCleaningWorkItem)}
                  </output>
                ) : null}
                {item.coverageStatus === "UNATTENDED" ? (
                  <div className="approval-warning service-coverage-warning" role="alert">
                    <strong>当前无人当班</strong>
                    <span>{item.coverageReason}</span>
                    {item.escalationReason ? <span>升级原因：{item.escalationReason}</span> : null}
                    <span>下一步：{item.nextStepLabel}</span>
                  </div>
                ) : item.coverageStatus === "UNKNOWN" ? (
                  <div className="error-band service-coverage-warning" role="alert">
                    <strong>当班情况暂时无法确认</strong>
                    <span>{item.coverageReason}</span>
                    <span>下一步：{item.nextStepLabel}</span>
                  </div>
                ) : null}
              </div>
              <dl className="approval-card-facts">
                <div>
                  <dt>主责</dt>
                  <dd>{serviceRoutingRoleLabels[item.primaryRole]}</dd>
                </div>
                <div>
                  <dt>同步提醒</dt>
                  <dd>
                    {item.copiedRoles.length > 0
                      ? item.copiedRoles.map((role) => serviceRoutingRoleLabels[role]).join("、")
                      : "不额外提醒"}
                  </dd>
                </div>
                <div>
                  <dt>紧急程度</dt>
                  <dd>{servicePriorityLabel(item.priority)}</dd>
                </div>
                <div>
                  <dt>发起时间</dt>
                  <dd>{time(item.requestedAt)}</dd>
                </div>
                <div>
                  <dt>当前状态</dt>
                  <dd>{serviceSlaLabel(item)}</dd>
                </div>
                <div>
                  <dt>处理期限</dt>
                  <dd>{item.dueAt === null ? "尚未设置" : time(item.dueAt)}</dd>
                </div>
                <div>
                  <dt>逾期提醒</dt>
                  <dd>{serviceEscalationLabel(item)}</dd>
                </div>
                <div>
                  <dt>现场覆盖</dt>
                  <dd>
                    {serviceCoverageLabel(item)}：{item.coverageReason}
                  </dd>
                </div>
                {item.escalationReason ? (
                  <div>
                    <dt>升级原因</dt>
                    <dd>{item.escalationReason}</dd>
                  </div>
                ) : null}
                <div>
                  <dt>下一步</dt>
                  <dd>{item.nextStepLabel}</dd>
                </div>
              </dl>
              {item.source.kind === "TABLE_CLEANING" ? (
                <TableCleaningWorkItemAction
                  item={item as TableCleaningWorkItem}
                  effectiveRoles={isError ? [] : effectiveRoles}
                  completion={cleaningCompletion}
                />
              ) : item.viewerActions.canAcknowledge ||
                item.viewerActions.canComplete ||
                item.viewerActions.canTransfer ? (
                <NavLink
                  className="button secondary compact"
                  to={serviceRequestDestination(item.source.eventId)}
                >
                  去现场工作台处理
                </NavLink>
              ) : (
                <p className="field-help">{item.viewerActions.unavailableReason}</p>
              )}
            </article>
          ))}
        </div>
      )}
    </section>
  );
}

function PurchaseOrderApprovalFacts({
  snapshot,
}: {
  snapshot: PurchaseOrderApprovalSnapshot;
}): React.JSX.Element {
  return (
    <>
      <div>
        <span>采购单合计</span>
        <strong>{fee(snapshot.totalFen)}</strong>
      </div>
      <dl>
        <div>
          <dt>采购单号</dt>
          <dd>{snapshot.purchaseOrderNumber}</dd>
        </div>
        <div>
          <dt>供应商</dt>
          <dd>
            {snapshot.supplier.supplierCode} · {snapshot.supplier.name}
          </dd>
        </div>
        <div>
          <dt>供应商资料版本</dt>
          <dd>第 {snapshot.supplier.version} 版</dd>
        </div>
        <div>
          <dt>业务日期</dt>
          <dd>{snapshot.businessDate}</dd>
        </div>
        <div>
          <dt>预计到货日期</dt>
          <dd>{snapshot.expectedDeliveryDate ?? "未填写"}</dd>
        </div>
        <div>
          <dt>采购草稿版本</dt>
          <dd>第 {snapshot.purchaseOrderVersion} 版</dd>
        </div>
        <div>
          <dt>商品行数</dt>
          <dd>{snapshot.lines.length} 项</dd>
        </div>
        <div>
          <dt>小计</dt>
          <dd>{fee(snapshot.subtotalFen)}</dd>
        </div>
        <div>
          <dt>税额</dt>
          <dd>{fee(snapshot.taxFen)}</dd>
        </div>
        <div>
          <dt>合计</dt>
          <dd>{fee(snapshot.totalFen)}</dd>
        </div>
        <div>
          <dt>申请原因</dt>
          <dd>{snapshot.reason}</dd>
        </div>
        <div>
          <dt>采购备注</dt>
          <dd>{snapshot.notes ?? "未填写"}</dd>
        </div>
      </dl>
      <section className="purchase-draft-lines" aria-label="采购商品明细">
        {snapshot.lines.map((line, index) => (
          <article key={line.lineId} aria-label={`第 ${index + 1} 行 ${line.productName}`}>
            <strong>
              第 {index + 1} 行 · {line.productName}
            </strong>
            <span>商品资料版本：第 {line.productVersion} 版</span>
            <span>
              采购数量：{line.inputUnitQuantity} {line.inputUnitName}
            </span>
            <span>
              基础数量：{line.orderedBaseQuantity} {line.baseUnitName}
            </span>
            <span>
              {line.conversionId === null
                ? `本行直接按基础库存单位“${line.baseUnitName}”采购`
                : `单位换算：1 ${line.inputUnitName} = ${line.baseUnitQuantityPerInput} ${line.baseUnitName}（第 ${line.conversionVersion} 版）`}
            </span>
            <small>
              采购单价 {fee(line.inputUnitPriceFen)} / {line.inputUnitName} · 小计{" "}
              {fee(line.subtotalFen)} · 税额 {fee(line.taxFen)} · 合计 {fee(line.totalFen)}
            </small>
          </article>
        ))}
      </section>
      <p className="field-help">
        审批只是核对并决定这张采购单是否可以进入后续收货，不代表已经收货；本页不会增加库存、形成应付或执行付款。
      </p>
    </>
  );
}

function GroupBuyDailyReconciliationFacts({
  snapshot,
}: {
  snapshot: GroupBuyDailyReconciliationApprovalSnapshot;
}): React.JSX.Element {
  return (
    <>
      <div>
        <span>平台实际结算</span>
        <strong>{fee(snapshot.actualSettlementFen)}</strong>
      </div>
      <dl>
        <div>
          <dt>团购对账单号</dt>
          <dd>{snapshot.reconciliationNumber}</dd>
        </div>
        <div>
          <dt>平台</dt>
          <dd>{groupBuyProviderLabel(snapshot.provider)}</dd>
        </div>
        <div>
          <dt>营业日</dt>
          <dd>{snapshot.businessDate}</dd>
        </div>
        <div>
          <dt>平台日结单</dt>
          <dd>{snapshot.statementReference}</dd>
        </div>
        <div>
          <dt>本地团购履约</dt>
          <dd>{snapshot.fulfillmentCount} 笔</dd>
        </div>
        <div>
          <dt>金额一致</dt>
          <dd>{snapshot.confirmedCount} 笔</dd>
        </div>
        <div>
          <dt>需要核对</dt>
          <dd>{snapshot.exceptionCount} 笔</dd>
        </div>
        <div>
          <dt>预计结算</dt>
          <dd>{fee(snapshot.expectedSettlementFen)}</dd>
        </div>
        <div>
          <dt>平台实际结算</dt>
          <dd>{fee(snapshot.actualSettlementFen)}</dd>
        </div>
        <div>
          <dt>预计平台费用</dt>
          <dd>{fee(snapshot.expectedFeeFen)}</dd>
        </div>
        <div>
          <dt>平台实际费用</dt>
          <dd>{fee(snapshot.actualFeeFen)}</dd>
        </div>
        <div>
          <dt>预计平台补贴</dt>
          <dd>{fee(snapshot.expectedSubsidyFen)}</dd>
        </div>
        <div>
          <dt>平台实际补贴</dt>
          <dd>{fee(snapshot.actualSubsidyFen)}</dd>
        </div>
        <div>
          <dt>核对说明</dt>
          <dd>{snapshot.note}</dd>
        </div>
      </dl>
      <section className="purchase-draft-lines" aria-label="团购日结单逐笔核对结果">
        {snapshot.matches.map((match) => (
          <article
            key={`${match.platformReference}:${match.tableNumber}`}
            aria-label={`${match.tableNumber} 号台 ${match.packageName}`}
          >
            <strong>
              {match.tableNumber} 号台 · {match.packageName}
            </strong>
            <span>平台凭证：{match.platformReference}</span>
            <span>核对结果：{groupBuyReconciliationOutcomeLabel(match.outcome)}</span>
            <span>
              预计结算 {fee(match.expectedSettlementFen)} · 平台实际结算{" "}
              {fee(match.actualSettlementFen)}
            </span>
            <small>
              平台费用 {fee(match.actualFeeFen)} · 平台补贴 {fee(match.actualSubsidyFen)} · 日结单第{" "}
              {match.statementLineNumbers.join("、") || "无"} 行
            </small>
          </article>
        ))}
        {snapshot.unmatchedStatementLines.map((line) => (
          <article
            key={`unmatched:${line.lineNumber}`}
            aria-label={`未匹配第 ${line.lineNumber} 行`}
          >
            <strong>平台日结单第 {line.lineNumber} 行 · 本地未找到对应履约</strong>
            <span>平台凭证：{line.platformReference}</span>
            <small>
              实际结算 {fee(line.actualSettlementFen)} · 平台费用 {fee(line.actualFeeFen)} ·
              平台补贴 {fee(line.actualSubsidyFen)}
            </small>
          </article>
        ))}
      </section>
      <p className="field-help">
        执行只会把这些逐笔核对结果写回团购履约记录；不会修改订单金额、库存或会员余额，也不会新增收款、付款或退款。
      </p>
    </>
  );
}

function groupBuyResolutionDispositionLabel(
  disposition: GroupBuyReconciliationResolutionApprovalSnapshot["lines"][number]["disposition"],
): string {
  switch (disposition) {
    case "PLATFORM_RECEIVABLE":
      return "向平台追收";
    case "ACCEPT_PLATFORM_ADJUSTMENT":
      return "接受平台结算调整";
    case "EXCLUDE_DUPLICATE_OR_UNRELATED":
      return "排除重复或无关记录";
  }
}

function GroupBuyReconciliationResolutionFacts({
  snapshot,
  signoff,
  signoffPending,
  signoffFailed,
}: {
  snapshot: GroupBuyReconciliationResolutionApprovalSnapshot;
  signoff: GroupBuyReconciliationSignoffPackage | null;
  signoffPending: boolean;
  signoffFailed: boolean;
}): React.JSX.Element {
  return (
    <>
      <div>
        <span>向平台追收</span>
        <strong>{fee(snapshot.platformReceivableFen)}</strong>
      </div>
      <dl>
        <div>
          <dt>团购对账单号</dt>
          <dd>{snapshot.reconciliationNumber}</dd>
        </div>
        <div>
          <dt>平台</dt>
          <dd>{groupBuyProviderLabel(snapshot.provider)}</dd>
        </div>
        <div>
          <dt>营业日</dt>
          <dd>{snapshot.businessDate}</dd>
        </div>
        <div>
          <dt>平台日结单</dt>
          <dd>{snapshot.statementReference}</dd>
        </div>
        <div>
          <dt>需要处置</dt>
          <dd>{snapshot.exceptionCount} 笔</dd>
        </div>
        <div>
          <dt>平台应收</dt>
          <dd>{fee(snapshot.platformReceivableFen)}</dd>
        </div>
        <div>
          <dt>结算调整（正数增加、负数减少）</dt>
          <dd>{fee(snapshot.settlementAdjustmentFen)}</dd>
        </div>
        <div>
          <dt>排除金额</dt>
          <dd>{fee(snapshot.excludedNetSettlementFen)}</dd>
        </div>
      </dl>
      <section className="purchase-draft-lines" aria-label="团购差异逐笔处置">
        {snapshot.lines.map((line) => (
          <article key={line.target.targetId}>
            <strong>
              {line.target.kind === "FULFILLMENT"
                ? `${line.target.tableNumber} 号台 · ${line.target.packageName}`
                : `平台日结单第 ${line.target.statementLineNumber} 行`}
            </strong>
            <span>平台凭证：{line.target.platformReference}</span>
            <span>处置：{groupBuyResolutionDispositionLabel(line.disposition)}</span>
            <span>
              预计净结算 {fee(line.target.expectedNetSettlementFen)} · 平台实际净结算{" "}
              {fee(line.target.actualNetSettlementFen)} · 差额 {fee(line.target.differenceFen)}
            </span>
            <span>
              本次账簿结果：
              {line.ledgerEntryType === "PLATFORM_RECEIVABLE"
                ? `平台应收 ${fee(line.ledgerAmountFen ?? 0)}`
                : line.ledgerEntryType === "SETTLEMENT_ADJUSTMENT"
                  ? `结算调整 ${fee(line.ledgerAmountFen ?? 0)}`
                  : `排除净额 ${fee(line.target.actualNetSettlementFen)}`}
            </span>
            <small>
              处置凭证：{line.evidenceReference} · 说明：{line.note}
            </small>
          </article>
        ))}
      </section>
      <p className="field-help">
        这次处置不会改写原团购履约、订单、库存、会员余额或既有收付款；批准并执行后，只会追加平台应收或结算调整记录，并保留排除依据。
      </p>
      {signoff ? (
        <section className="approval-execution is-positive" aria-label="团购差异签结结果">
          <strong>已签结 · {time(signoff.signedOffAt)}</strong>
          <p>已生成 {signoff.ledgerEntries.length} 笔不可变结算记录；原日结异常工作项已完成。</p>
          {signoff.ledgerEntries.map((entry) => (
            <p key={entry.entryId}>
              {entry.entryType === "PLATFORM_RECEIVABLE" ? "平台应收" : "结算调整"}：
              {fee(entry.amountFen)} · {entry.evidenceReference}
            </p>
          ))}
        </section>
      ) : signoffPending ? (
        <p className="field-help">正在读取门店已签结记录…</p>
      ) : signoffFailed ? (
        <div className="approval-warning" role="alert">
          执行状态已完成，但签结明细暂时无法读取。请保留 GH 单号并重新查询，不要重复执行。
        </div>
      ) : null}
    </>
  );
}

export function ApprovalDetail({
  approval,
  effectiveRoles,
  onSubmit,
  pending,
  executionRecoveryView,
  executionPending,
  onExecute,
  paidReturnExecutionRecoveryView,
  paidReturnExecutionPending,
  onExecutePaidReturn,
}: {
  approval: ApprovalRequestReadModel;
  effectiveRoles: readonly OperatorRole[];
  onSubmit: (command: ApprovalMutationCommand) => void;
  pending: boolean;
  executionRecoveryView: ApprovalExecutionRecoveryView;
  executionPending: boolean;
  onExecute: (approval: ApprovalRequestReadModel) => void;
  paidReturnExecutionRecoveryView: PaidOrderItemReturnExecutionRecoveryView;
  paidReturnExecutionPending: boolean;
  onExecutePaidReturn: (
    approval: ApprovalRequestReadModel,
    evidence: PaidReturnExecutionEvidenceInput,
  ) => void;
}): React.JSX.Element {
  const [decisionReason, setDecisionReason] = useState("");
  const [cancelReason, setCancelReason] = useState("");
  const [externalRefundReference, setExternalRefundReference] = useState("");
  const [cashRefundConfirmed, setCashRefundConfirmed] = useState(false);
  const [allocationExternalReferences, setAllocationExternalReferences] = useState<
    Record<string, string>
  >({});
  const [allocationCashConfirmations, setAllocationCashConfirmations] = useState<
    Record<string, boolean>
  >({});
  const snapshot = approval.snapshot;
  useEffect(() => {
    if (approval.approvalId.length === 0) return;
    setExternalRefundReference("");
    setCashRefundConfirmed(false);
    setAllocationExternalReferences({});
    setAllocationCashConfirmations({});
  }, [approval.approvalId]);
  const countProgressRequired =
    snapshot.kind === "INVENTORY_OPERATION" &&
    snapshot.operationType === "COUNT_SET" &&
    snapshot.snapshotVersion === 2;
  const legacyCountReadOnly =
    snapshot.kind === "INVENTORY_OPERATION" &&
    snapshot.operationType === "COUNT_SET" &&
    snapshot.snapshotVersion === 1;
  const currentLossCapture =
    snapshot.kind === "INVENTORY_OPERATION" &&
    snapshot.operationType === "LOSS_OUT" &&
    snapshot.snapshotVersion === 2
      ? snapshot.lossCapture
      : undefined;
  const legacyLossReadOnly =
    snapshot.kind === "INVENTORY_OPERATION" &&
    snapshot.operationType === "LOSS_OUT" &&
    snapshot.snapshotVersion === 1;
  const countProgressShouldPoll =
    countProgressRequired &&
    approval.status !== "REJECTED" &&
    approval.status !== "CANCELLED" &&
    approval.execution.status !== "NOT_APPLICABLE" &&
    !approvalExecutionResultIsTerminal(approval);
  const countProgressQuery = useQuery({
    queryKey: [
      "inventory-count-progress",
      snapshot.kind === "INVENTORY_OPERATION" ? snapshot.skuId : "",
      approval.approvalId,
    ],
    queryFn: () =>
      getInventoryCountProgress(
        snapshot.kind === "INVENTORY_OPERATION" ? snapshot.skuId : "",
        approval.approvalId,
      ),
    enabled: countProgressShouldPoll,
    refetchInterval: countProgressShouldPoll ? 2_000 : false,
    retry: false,
  });
  const resolutionSignoffQuery = useQuery({
    queryKey: [
      "group-buy-reconciliation-signoff",
      snapshot.kind === "GROUP_BUY_RECONCILIATION_RESOLUTION" ? snapshot.resolutionId : "",
    ],
    queryFn: () =>
      getGroupBuyReconciliationSignoff(
        snapshot.kind === "GROUP_BUY_RECONCILIATION_RESOLUTION" ? snapshot.resolutionId : "",
      ),
    enabled:
      snapshot.kind === "GROUP_BUY_RECONCILIATION_RESOLUTION" &&
      approval.execution.status === "SUCCEEDED",
    retry: false,
  });
  const trustedCountProgress =
    countProgressRequired && countProgressQuery.isSuccess && !countProgressQuery.isError
      ? countProgressQuery.data
      : null;
  const step = activeStep(approval);
  const privileged = effectiveRoles.includes("OWNER") || effectiveRoles.includes("MANAGER");
  const canDecide =
    privileged &&
    !legacyLossReadOnly &&
    step !== null &&
    (approval.actions.canApprove || approval.actions.canReject);
  const executionRecoveryForThisApproval =
    executionRecoveryView.status === "OWNED" &&
    executionRecoveryView.recovery.approvalId === approval.approvalId;
  const executionRecoveryAvailable =
    executionRecoveryView.status === "NONE" || executionRecoveryForThisApproval;
  const mustReuseUnknownAttempt =
    executionRecoveryForThisApproval &&
    executionRecoveryView.recovery.expectedCaseVersion >= approval.version;
  const canExecute =
    snapshot.kind !== "ORDER_ITEM_PAID_RETURN" &&
    privileged &&
    approval.actions.canRetryExecution &&
    executionRecoveryAvailable &&
    !legacyCountReadOnly &&
    !legacyLossReadOnly &&
    (!countProgressRequired || trustedCountProgress?.canExecute === true) &&
    (approval.execution.status === "READY" ||
      approval.execution.status === "PROCESSING" ||
      approval.execution.status === "FAILED_RETRYABLE" ||
      approval.execution.status === "OUTCOME_UNKNOWN");
  const paidReturnRecoveryForThisApproval =
    paidReturnExecutionRecoveryView.status === "OWNED" &&
    paidReturnExecutionRecoveryView.recovery.command.approvalId === approval.approvalId;
  const paidReturnExecutionAvailable =
    paidReturnExecutionRecoveryView.status === "NONE" || paidReturnRecoveryForThisApproval;
  const canExecutePaidReturn =
    snapshot.kind === "ORDER_ITEM_PAID_RETURN" &&
    effectiveRoles.includes("CASHIER") &&
    !effectiveRoles.includes("OWNER") &&
    !effectiveRoles.includes("MANAGER") &&
    approval.actions.canRetryExecution &&
    paidReturnExecutionAvailable &&
    !paidReturnRecoveryForThisApproval &&
    (approval.execution.status === "READY" || approval.execution.status === "FAILED_RETRYABLE");
  const staticRefundReferenceRequired =
    snapshot.kind === "ORDER_ITEM_PAID_RETURN" &&
    snapshot.refundExecutionMode === "MANUAL_STATIC_CODE_CONFIRMATION";
  const refundAllocations =
    snapshot.kind === "ORDER_ITEM_PAID_RETURN" ? snapshot.refundAllocations : undefined;
  const combinedRefund = refundAllocations !== undefined;
  const staticRefundAllocations =
    refundAllocations?.filter(
      (allocation) =>
        allocation.method === "STATIC_WECHAT" || allocation.method === "STATIC_ALIPAY",
    ) ?? [];
  const cashRefundAllocations =
    refundAllocations?.filter((allocation) => allocation.method === "CASH") ?? [];
  const normalizedExternalRefundReference = externalRefundReference.trim();
  const staticRefundReferenceValid =
    !staticRefundReferenceRequired ||
    /^[A-Za-z0-9][A-Za-z0-9._:-]{1,127}$/.test(normalizedExternalRefundReference);
  const combinedStaticReferencesValid = staticRefundAllocations.every((allocation) =>
    /^[A-Za-z0-9][A-Za-z0-9._:-]{1,127}$/.test(
      allocationExternalReferences[allocation.paymentId]?.trim() ?? "",
    ),
  );
  const combinedCashConfirmationsValid = cashRefundAllocations.every(
    (allocation) => allocationCashConfirmations[allocation.paymentId] === true,
  );
  return (
    <article className="approval-detail">
      <header className="approval-detail-header">
        <div>
          <span className="approval-kicker">
            {currentLossCapture
              ? `报损单号 ${currentLossCapture.lossNumber}`
              : legacyLossReadOnly
                ? "历史报损记录"
                : snapshot.kind === "GROUP_BUY_DAILY_RECONCILIATION"
                  ? `团购对账单号 ${snapshot.reconciliationNumber}`
                  : snapshot.kind === "GROUP_BUY_RECONCILIATION_RESOLUTION"
                    ? `团购对账单号 ${snapshot.reconciliationNumber}`
                    : `审批编号 ${approval.approvalId}`}
          </span>
          <h2>{approval.title}</h2>
          <p>
            申请人 {approval.applicant.displayName} · {time(approval.requestedAt)}
          </p>
        </div>
        <span className={"approval-status status-" + approval.status.toLowerCase()}>
          {approvalStatusLabels[approval.status]}
        </span>
      </header>

      <section
        className="approval-money-card"
        aria-label={
          snapshot.kind === "INVENTORY_OPERATION"
            ? "库存操作和数量变化"
            : snapshot.kind === "PURCHASE_ORDER_APPROVAL"
              ? "采购单审批快照"
              : snapshot.kind === "GROUP_BUY_DAILY_RECONCILIATION"
                ? "团购日对账审批快照"
                : snapshot.kind === "GROUP_BUY_RECONCILIATION_RESOLUTION"
                  ? "团购差异处置审批快照"
                  : snapshot.kind === "CASH_SHIFT_DIFFERENCE"
                    ? "现金交接差异审批快照"
                    : snapshot.kind === "MANUAL_REVIEW_RESOLUTION"
                      ? "待复核事项处置审批快照"
                      : "申请金额和原业务记录"
        }
      >
        {snapshot.kind === "INVENTORY_OPERATION" ? (
          <>
            <div>
              <span>库存操作</span>
              <strong>{inventoryOperationLabel(snapshot.operationType)}</strong>
            </div>
            <dl>
              <div>
                <dt>商品</dt>
                <dd>{snapshot.productName}</dd>
              </div>
              <div>
                <dt>操作类型</dt>
                <dd>{inventoryOperationLabel(snapshot.operationType)}</dd>
              </div>
              <div>
                <dt>申请时库存</dt>
                <dd>{inventoryQuantity(snapshot.quantityBefore, snapshot.unit)}</dd>
              </div>
              <div>
                <dt>库存变化</dt>
                <dd>{inventoryQuantityDelta(snapshot.quantityDelta, snapshot.unit)}</dd>
              </div>
              <div>
                <dt>按申请快照预计执行后数量</dt>
                <dd>{inventoryQuantity(snapshot.quantityAfter, snapshot.unit)}</dd>
              </div>
              {currentLossCapture ? (
                <>
                  <div>
                    <dt>报损单号</dt>
                    <dd>{currentLossCapture.lossNumber}</dd>
                  </div>
                  <div>
                    <dt>申请时已预留</dt>
                    <dd>
                      {inventoryQuantity(currentLossCapture.activeReservedQuantity, snapshot.unit)}
                    </dd>
                  </div>
                  <div>
                    <dt>将减少的库存价值</dt>
                    <dd>{fee(currentLossCapture.inventoryValueDecreaseFen)}</dd>
                  </div>
                  <div>
                    <dt>报损后库存价值</dt>
                    <dd>{fee(currentLossCapture.inventoryValueAfterFen)}</dd>
                  </div>
                </>
              ) : null}
              <div>
                <dt>单位</dt>
                <dd>{snapshot.unit}</dd>
              </div>
              <div>
                <dt>业务记录</dt>
                <dd>{snapshot.originalDocumentLabel}</dd>
              </div>
              {snapshot.referenceNumber ? (
                <div>
                  <dt>参考单号</dt>
                  <dd>{snapshot.referenceNumber}</dd>
                </div>
              ) : null}
              <div>
                <dt>本次原因</dt>
                <dd>{snapshot.reason}</dd>
              </div>
            </dl>
          </>
        ) : snapshot.kind === "PURCHASE_ORDER_APPROVAL" ? (
          <PurchaseOrderApprovalFacts snapshot={snapshot} />
        ) : snapshot.kind === "GROUP_BUY_DAILY_RECONCILIATION" ? (
          <GroupBuyDailyReconciliationFacts snapshot={snapshot} />
        ) : snapshot.kind === "GROUP_BUY_RECONCILIATION_RESOLUTION" ? (
          <GroupBuyReconciliationResolutionFacts
            snapshot={snapshot}
            signoff={resolutionSignoffQuery.data ?? null}
            signoffPending={
              resolutionSignoffQuery.isPending && resolutionSignoffQuery.fetchStatus !== "idle"
            }
            signoffFailed={resolutionSignoffQuery.isError}
          />
        ) : snapshot.kind === "CASH_SHIFT_DIFFERENCE" ? (
          <CashShiftDifferenceApprovalFacts snapshot={snapshot} />
        ) : snapshot.kind === "MANUAL_REVIEW_RESOLUTION" ? (
          <ManualReviewResolutionApprovalFacts snapshot={snapshot} />
        ) : (
          <>
            <div>
              <span>申请退款金额</span>
              <strong>{fee(snapshot.amountFen)}</strong>
            </div>
            {snapshot.kind === "ORDER_ITEM_PAID_RETURN" ? (
              <>
                <dl>
                  <div>
                    <dt>{snapshot.itemKind === "PACKAGE" ? "门店套餐" : "商品"}</dt>
                    <dd>{snapshot.productName}</dd>
                  </div>
                  {snapshot.itemKind === "PACKAGE" && snapshot.lineNumber ? (
                    <div>
                      <dt>套餐业务单号</dt>
                      <dd>{snapshot.lineNumber}</dd>
                    </div>
                  ) : null}
                  {snapshot.itemKind === "PACKAGE" && snapshot.componentNames ? (
                    <div>
                      <dt>整包内容</dt>
                      <dd>{snapshot.componentNames.join("、")}</dd>
                    </div>
                  ) : null}
                  <div>
                    <dt>原订单</dt>
                    <dd>{snapshot.originalDocumentLabel}</dd>
                  </div>
                  <div>
                    <dt>业务单号</dt>
                    <dd>{snapshot.orderNumber}</dd>
                  </div>
                  <div>
                    <dt>{snapshot.itemKind === "PACKAGE" ? "退回范围" : "退回数量"}</dt>
                    <dd>
                      {snapshot.itemKind === "PACKAGE"
                        ? "整包 1 份（全部商品与本单服务权益一次处理）"
                        : `${snapshot.returnQuantity} 件（原售 ${snapshot.soldQuantity} 件，此前已退 ${snapshot.returnedQuantityBefore} 件）`}
                    </dd>
                  </div>
                  <div>
                    <dt>原收款方式</dt>
                    <dd>{snapshot.paymentMethodLabel}</dd>
                  </div>
                  <div>
                    <dt>退款金额</dt>
                    <dd>{fee(snapshot.amountFen)}</dd>
                  </div>
                  <div>
                    <dt>{snapshot.itemKind === "PACKAGE" ? "套餐内商品状态" : "退回实物状态"}</dt>
                    <dd>
                      {snapshot.physicalDisposition === "SALEABLE_RETURNED"
                        ? "完好可再次销售"
                        : "已开封或破损，不可再次销售"}
                    </dd>
                  </div>
                  <div>
                    <dt>{snapshot.itemKind === "PACKAGE" ? "库存和服务权益" : "可售库存"}</dt>
                    <dd>
                      {snapshot.itemKind === "PACKAGE"
                        ? snapshot.inventoryEffect === "SALEABLE_STOCK_RESTORED"
                          ? "全部受管商品按原销售成本恢复可售库存，本单服务权益同时撤销"
                          : "商品可售库存不增加且不再扣一次，本单服务权益同时撤销"
                        : snapshot.inventoryEffect === "SALEABLE_STOCK_RESTORED"
                          ? `${snapshot.inventoryQuantityBefore} → ${snapshot.inventoryQuantityAfter}（增加 ${snapshot.returnQuantity} 件）`
                          : "不增加，也不会再扣一次；系统记录为不可再次销售"}
                    </dd>
                  </div>
                  <div>
                    <dt>本次原因</dt>
                    <dd>{snapshot.reason}</dd>
                  </div>
                  {snapshot.refundAllocations ? (
                    <div>
                      <dt>逐路退款</dt>
                      <dd>见下方组合支付逐路退款明细</dd>
                    </div>
                  ) : null}
                </dl>
                {snapshot.refundAllocations ? (
                  <PaidReturnAllocationFacts
                    allocations={snapshot.refundAllocations}
                    totalFen={snapshot.amountFen}
                  />
                ) : null}
              </>
            ) : snapshot.kind === "MEMBER_BALANCE_REFUND" ? (
              <>
                <dl>
                  <div>
                    <dt>会员</dt>
                    <dd>
                      {snapshot.memberName}（{snapshot.memberNumber}）
                    </dd>
                  </div>
                  <div>
                    <dt>原消费记录</dt>
                    <dd>{snapshot.originalDocumentLabel}</dd>
                  </div>
                  <div>
                    <dt>公开订单号</dt>
                    <dd>{snapshot.orderNumber}</dd>
                  </div>
                  <div>
                    <dt>原消费使用余额</dt>
                    <dd>{fee(snapshot.originalWalletAmountFen)}</dd>
                  </div>
                  <div>
                    <dt>此前已退回</dt>
                    <dd>{fee(snapshot.alreadyRefundedFen)}</dd>
                  </div>
                  <div>
                    <dt>申请时可退</dt>
                    <dd>{fee(snapshot.refundableRemainingFen)}</dd>
                  </div>
                  <div>
                    <dt>本次原因</dt>
                    <dd>{snapshot.reason}</dd>
                  </div>
                </dl>
                {snapshot.refundAllocations ? (
                  <MemberRefundAllocationFacts
                    allocations={snapshot.refundAllocations}
                    totalFen={snapshot.amountFen}
                  />
                ) : null}
              </>
            ) : (
              <dl>
                <div>
                  <dt>原单</dt>
                  <dd>{snapshot.originalDocumentLabel}</dd>
                </div>
                <div>
                  <dt>业务单号</dt>
                  <dd>{snapshot.orderNumber}</dd>
                </div>
                <div>
                  <dt>原单已收</dt>
                  <dd>{fee(snapshot.orderPaidFen)}</dd>
                </div>
                <div>
                  <dt>此前已退</dt>
                  <dd>{fee(snapshot.orderAlreadyRefundedFen)}</dd>
                </div>
                <div>
                  <dt>本次原因</dt>
                  <dd>{snapshot.reason}</dd>
                </div>
              </dl>
            )}
          </>
        )}
      </section>

      {legacyLossReadOnly ? (
        <div className="approval-warning" role="alert">
          这是旧版报损记录，没有保存服务端权威库存价值和预留快照，只能查看历史，不能审批、撤回或执行。请按当前库存重新发起报损申请。
        </div>
      ) : null}

      {snapshot.kind === "CASH_SHIFT_DIFFERENCE" ? (
        <div className="approval-consequence-grid">
          <section>
            <h3>批准后会发生什么</h3>
            <ul>
              <li>批准只允许系统继续安全关闭这次现金班次，不会修改冻结的应有、实盘或差异金额。</li>
              <li>只有执行成功并取得交接记录后，班次才算关闭；处理中或结果未知时不能重复发起。</li>
            </ul>
          </section>
          <section>
            <h3>谁可以审批</h3>
            <ul>
              <li>
                {snapshot.assessment.approvalRequirement === "OWNER"
                  ? "这是高风险差异，只能由老板审批。"
                  : "这笔现金差异可由另一位店长或老板审批。"}
              </li>
              <li>发起人、代办交班人和接班人都不能审批自己的现金差异，最终以服务端核验为准。</li>
              <li>如果驳回，原审批永久保留，班次会重新开放盘点，可按意见重新交接。</li>
            </ul>
          </section>
        </div>
      ) : (
        <div className="approval-consequence-grid">
          <section>
            <h3>批准后会发生什么</h3>
            <ul>
              {snapshot.consequences.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          </section>
          <section>
            <h3>接下来怎么走</h3>
            <ol>
              {snapshot.nextSteps.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ol>
          </section>
        </div>
      )}

      {countProgressRequired && countProgressShouldPoll ? (
        <InventoryCountProgressPanel
          progress={trustedCountProgress}
          loading={
            !trustedCountProgress && (countProgressQuery.isLoading || countProgressQuery.isFetching)
          }
          error={countProgressQuery.isError}
          onRetry={() => {
            void countProgressQuery.refetch();
          }}
        />
      ) : countProgressRequired ? (
        <section className="approval-info" aria-label="盘点终态进度说明">
          <strong>这笔盘点已经结束，不再读取期间进度</strong>
          <p>
            {approval.execution.status === "SUCCEEDED"
              ? "请以库存流水和刷新后的权威库存为准；页面不会再把本次盘点执行记录当成等待期间变化。"
              : "这笔申请不会继续执行；请以审批处理记录为准，需要盘点时重新打开当前库存发起申请。"}
          </p>
        </section>
      ) : legacyCountReadOnly ? (
        <section className="approval-warning" role="alert" aria-label="历史盘点只读提示">
          <strong>历史盘点仅可查看，缺少期间流水/价值冻结不能执行</strong>
          <p>
            这笔旧记录缺少审批等待期间的库存流水和价值冻结，系统不能安全计算当前应执行的数量与库存价值。请重新打开当前库存，按现场实盘发起新的盘点申请。
          </p>
        </section>
      ) : null}

      <section className="approval-steps" aria-label="审批步骤">
        <h3>审批步骤</h3>
        {approval.steps.map((approvalStep) => (
          <div
            className={"approval-step step-" + approvalStep.status.toLowerCase()}
            key={approvalStep.stepNumber}
          >
            <span>{approvalStep.stepNumber}</span>
            <div>
              <strong>
                {approvalStep.requiredRole === "OWNER"
                  ? "老板审批"
                  : approvalStep.requiredRole === "MANAGER"
                    ? "店长审批"
                    : "店长或老板审批"}
              </strong>
              <p>
                {approvalStep.status === "AVAILABLE"
                  ? "现在轮到这一步"
                  : approvalStep.status === "WAITING"
                    ? "等待前一步完成"
                    : approvalStep.status === "APPROVED"
                      ? "已批准"
                      : approvalStep.status === "REJECTED"
                        ? "已驳回"
                        : "已撤回"}
                {approvalStep.decidedBy ? " · " + approvalStep.decidedBy.displayName : ""}
                {approvalStep.reason ? " · 原因：" + approvalStep.reason : ""}
              </p>
            </div>
          </div>
        ))}
      </section>

      <section
        className={
          approval.execution.status === "READY" || approval.execution.status === "SUCCEEDED"
            ? "approval-execution is-positive"
            : "approval-execution"
        }
      >
        <strong>{executionLabel(approval)}</strong>
        {approval.execution.lastErrorMessage ? <p>{approval.execution.lastErrorMessage}</p> : null}
        {approval.status === "APPROVED" && approval.execution.status !== "SUCCEEDED" ? (
          <p>
            {snapshot.kind === "INVENTORY_OPERATION"
              ? "批准只是允许系统进入安全执行；没有执行成功记录前，不能把库存当成已经变更。"
              : snapshot.kind === "PURCHASE_ORDER_APPROVAL"
                ? "批准只是允许系统确认采购单进入可收货状态；没有执行成功记录前，不能把采购单当成可以收货。本页不会收货、增加库存、形成应付或付款。"
                : snapshot.kind === "GROUP_BUY_DAILY_RECONCILIATION"
                  ? "批准只是允许系统按这张冻结日结单逐笔写入核对结果；执行成功前，不能把差异当成已经处理。本页不会修改订单金额、库存、会员余额或新增收付款。"
                  : snapshot.kind === "GROUP_BUY_RECONCILIATION_RESOLUTION"
                    ? "批准只是允许系统按冻结的逐笔处置追加平台应收或结算调整；执行并取得签结记录前，不能把差异当成已经处理。原团购履约、订单、库存、会员余额和既有收付款不会改写。"
                    : snapshot.kind === "CASH_SHIFT_DIFFERENCE"
                      ? "批准只是允许系统继续安全关闭现金班次；没有执行成功和交接记录前，班次仍未关闭，不能另起一笔重复交接。"
                      : snapshot.kind === "ORDER_ITEM_PAID_RETURN"
                        ? snapshot.itemKind === "PACKAGE"
                          ? "批准只是允许收银员执行整包原路退款；没有执行成功记录前，订单金额、套餐状态、商品库存和本单服务权益都不会改变。"
                          : snapshot.inventoryEffect === "SALEABLE_STOCK_RESTORED"
                            ? "批准只是允许收银员按原收款方式执行；没有执行成功记录前，订单金额未减少、退款未完成、可售库存也未增加。"
                            : "批准只是允许收银员按原收款方式执行；没有执行成功记录前，订单金额未减少、退款未完成、不可售实物状态也未记账。可售库存不会因这次退回增加，也不会再扣一次。"
                        : "批准只是允许系统进入安全执行；没有执行成功记录前，不能把它当成退款完成。"}
          </p>
        ) : null}
        {canExecutePaidReturn && snapshot.kind === "ORDER_ITEM_PAID_RETURN" ? (
          <form
            className="paid-order-return-execution-form"
            onSubmit={(event) => {
              event.preventDefault();
              if (
                combinedRefund
                  ? !combinedStaticReferencesValid || !combinedCashConfirmationsValid
                  : !staticRefundReferenceValid ||
                    (snapshot.refundExecutionMode === "LOCAL_CASH_CONFIRMATION" &&
                      !cashRefundConfirmed)
              ) {
                return;
              }
              onExecutePaidReturn(
                approval,
                combinedRefund
                  ? {
                      externalRefundReference: null,
                      externalRefundReferences: staticRefundAllocations.map((allocation) => ({
                        paymentId: allocation.paymentId,
                        externalRefundReference:
                          allocationExternalReferences[allocation.paymentId]?.trim() ?? "",
                      })),
                    }
                  : {
                      externalRefundReference: staticRefundReferenceRequired
                        ? normalizedExternalRefundReference
                        : null,
                    },
              );
            }}
          >
            <strong>
              {snapshot.itemKind === "PACKAGE"
                ? combinedRefund
                  ? "批准后由收银员逐路完成整包退款、库存与服务权益撤销"
                  : "批准后由收银员完成整包原路退款、库存与服务权益撤销"
                : combinedRefund
                  ? "批准后由收银员逐路完成组合退款和实物退回"
                  : "批准后由收银员完成原路退款和实物退回"}
            </strong>
            <p>
              {combinedRefund
                ? `本次 ${fee(snapshot.amountFen)} 必须按下方冻结分配一次完成；任一路结果未知都会锁定整笔原请求，不能换凭证或另起一笔。`
                : snapshot.refundExecutionMode === "LOCAL_CASH_CONFIRMATION"
                  ? `现场退还现金 ${fee(snapshot.amountFen)} 后确认；执行成功才会减少原订单。`
                  : snapshot.refundExecutionMode === "MANUAL_STATIC_CODE_CONFIRMATION"
                    ? `请先在原${snapshot.paymentMethodLabel}渠道完成 ${fee(snapshot.amountFen)} 退款，再填写渠道退款凭证；系统不会替你向静态码自动退款。`
                    : snapshot.refundExecutionMode === "MEMBER_BALANCE_CLOUD_REFUND"
                      ? `系统会通过联网服务把 ${fee(snapshot.amountFen)} 退回原会员余额；结果未知时只查询或重试原请求。`
                      : `系统会向原官方支付渠道查询并退款 ${fee(snapshot.amountFen)}；结果未知时只查询原渠道请求，不能另起一笔。`}
            </p>
            <p className="field-help">
              <strong>实物状态后果：</strong>
              {snapshot.itemKind === "PACKAGE"
                ? snapshot.inventoryEffect === "SALEABLE_STOCK_RESTORED"
                  ? "套餐内所有受管商品按原销售成本恢复可售库存，本单服务权益同时撤销。"
                  : "套餐内商品可售库存不增加且不再扣一次，本单服务权益同时撤销。"
                : snapshot.inventoryEffect === "SALEABLE_STOCK_RESTORED"
                  ? `完好商品会增加 ${snapshot.returnQuantity} 件可售库存。`
                  : "已开封或破损商品不会增加可售库存，也不会再扣一次库存。"}
            </p>
            {combinedRefund ? (
              <section className="approval-steps" aria-label="逐路退款确认">
                <h3>逐路确认后一次提交</h3>
                {refundAllocations.map((allocation, index) => (
                  <article className="approval-step" key={allocation.paymentId}>
                    <span>{index + 1}</span>
                    <div>
                      <strong>
                        {paidReturnPaymentMethodLabel(allocation.method)}，本次退
                        {fee(allocation.refundAmountFen)}
                      </strong>
                      <p>
                        原收款：{fee(allocation.paymentAmountFen)}；已退：
                        {fee(allocation.alreadyRefundedFen)}；尚可退：
                        {fee(allocation.availableFen)}
                      </p>
                      {allocation.executionMode === "LOCAL_CASH_CONFIRMATION" ? (
                        <label className="paid-return-cash-confirmation">
                          <input
                            type="checkbox"
                            checked={allocationCashConfirmations[allocation.paymentId] === true}
                            onChange={(event) =>
                              setAllocationCashConfirmations((current) => ({
                                ...current,
                                [allocation.paymentId]: event.target.checked,
                              }))
                            }
                          />
                          <span>
                            我已当面退还第 {index + 1} 路现金
                            {fee(allocation.refundAmountFen)}
                          </span>
                        </label>
                      ) : allocation.executionMode === "MANUAL_STATIC_CODE_CONFIRMATION" ? (
                        <label>
                          第 {index + 1} 路{paidReturnPaymentMethodLabel(allocation.method)}退款凭证
                          <input
                            value={allocationExternalReferences[allocation.paymentId] ?? ""}
                            maxLength={128}
                            autoComplete="off"
                            placeholder="填写该路自己的退款记录编号"
                            onChange={(event) =>
                              setAllocationExternalReferences((current) => ({
                                ...current,
                                [allocation.paymentId]: event.target.value,
                              }))
                            }
                          />
                          <small>必须填写这一路自己的可核对凭证，不能与其他路径合并。</small>
                        </label>
                      ) : (
                        <p className="field-help">
                          {allocation.executionMode === "MEMBER_BALANCE_CLOUD_REFUND"
                            ? "系统将查询并退回原会员余额，无需手工填写凭证。"
                            : "系统将查询原官方支付渠道并原路退款，无需手工填写凭证。"}
                        </p>
                      )}
                    </div>
                  </article>
                ))}
              </section>
            ) : snapshot.refundExecutionMode === "LOCAL_CASH_CONFIRMATION" ? (
              <label className="paid-return-cash-confirmation">
                <input
                  type="checkbox"
                  checked={cashRefundConfirmed}
                  onChange={(event) => setCashRefundConfirmed(event.target.checked)}
                />
                <span>我已当面退还顾客 {fee(snapshot.amountFen)} 现金</span>
              </label>
            ) : null}
            {staticRefundReferenceRequired ? (
              <label>
                外部退款凭证（2–128 字）
                <input
                  value={externalRefundReference}
                  maxLength={128}
                  autoComplete="off"
                  placeholder="例如 WXREFUND-20260830-001"
                  onChange={(event) => setExternalRefundReference(event.target.value)}
                />
                <small>
                  填写微信或支付宝退款记录中的可核对凭证，只允许字母、数字、点、下划线、冒号和短横线。
                </small>
              </label>
            ) : null}
            {!staticRefundReferenceValid ? (
              <p className="form-hint">还需修改：外部退款凭证格式不正确，请填写 2–128 字。</p>
            ) : null}
            {combinedRefund && !combinedCashConfirmationsValid ? (
              <p className="form-hint">还需确认：请逐路勾选已经当面退还的现金。</p>
            ) : null}
            {combinedRefund && !combinedStaticReferencesValid ? (
              <p className="form-hint">还需填写：每一路静态码退款都要填写自己的有效退款凭证。</p>
            ) : null}
            <button
              className="button primary"
              type="submit"
              disabled={
                paidReturnExecutionPending ||
                (combinedRefund
                  ? !combinedStaticReferencesValid || !combinedCashConfirmationsValid
                  : !staticRefundReferenceValid ||
                    (snapshot.refundExecutionMode === "LOCAL_CASH_CONFIRMATION" &&
                      !cashRefundConfirmed))
              }
            >
              {paidReturnExecutionPending
                ? "正在核对退款、订单和实物状态…"
                : combinedRefund
                  ? "按以上各路退款并完成商品退回"
                  : snapshot.refundExecutionMode === "LOCAL_CASH_CONFIRMATION"
                    ? "确认现金已退并完成商品退回"
                    : snapshot.refundExecutionMode === "MANUAL_STATIC_CODE_CONFIRMATION"
                      ? "登记外部退款并完成商品退回"
                      : snapshot.refundExecutionMode === "MEMBER_BALANCE_CLOUD_REFUND"
                        ? "退回会员余额并完成商品退回"
                        : "查询原渠道并完成商品退回"}
            </button>
          </form>
        ) : paidReturnRecoveryForThisApproval ? (
          <p>
            这笔商品退回已有原执行请求待核对。请使用页面上方“查询原执行结果”或“使用原请求安全重试”。
          </p>
        ) : null}
        {approval.execution.status === "BLOCKED_PRECONDITION_CHANGED" ? (
          <p>
            {snapshot.kind === "INVENTORY_OPERATION"
              ? "商品或库存数量已经变化，系统不会继续使用旧数量执行。请重新核对当前库存后发起新申请。"
              : snapshot.kind === "PURCHASE_ORDER_APPROVAL"
                ? "采购草稿内容或状态已经变化，系统不会继续使用旧快照执行。请重新核对采购单后再申请。"
                : snapshot.kind === "GROUP_BUY_DAILY_RECONCILIATION"
                  ? "平台日结单或本地团购履约事实已经变化，系统不会继续使用旧核对结果。请重新检查原日结单后再申请。"
                  : snapshot.kind === "GROUP_BUY_RECONCILIATION_RESOLUTION"
                    ? "原 GH 对账事实、异常工作项或逐笔处置依据已经变化，系统不会继续使用旧处置。请重新打开团购差异处理页核对。"
                    : snapshot.kind === "CASH_SHIFT_DIFFERENCE"
                      ? "班次、交班接班责任或现金事实已经变化，系统不会继续使用旧快照关闭班次。请回交接班工作台重新核对。"
                      : snapshot.kind === "MEMBER_BALANCE_REFUND"
                        ? "会员消费或退款情况已经变化，系统不会继续使用旧的可退事实。请让收银员重新打开会员资金流水后发起新申请。"
                        : "原单已变化，系统不会继续使用旧金额退款。请让收银员重新核对原单后发起新申请。"}
          </p>
        ) : null}
        {approval.execution.status === "FAILED_TERMINAL" ? (
          <p>这次执行不能自动继续。请保留申请和处理记录，在异常待核对区跟进。</p>
        ) : null}
        {approval.execution.status === "BLOCKED_INTEGRITY_REVIEW" ? (
          <p>
            {snapshot.kind === "PURCHASE_ORDER_APPROVAL"
              ? "采购单编号或业务关联发生冲突。请保留审批编号和页面提示，由负责人核对采购单状态；不要重新申请或继续自动执行。"
              : snapshot.kind === "GROUP_BUY_DAILY_RECONCILIATION"
                ? "GH 单号、平台日结行或团购履约关联发生冲突。请保留团购对账单号，由负责人逐笔核对；不要另建一张日对账单。"
                : snapshot.kind === "GROUP_BUY_RECONCILIATION_RESOLUTION"
                  ? "GH 单号、逐笔处置或结算账簿关联发生冲突。请保留 GH 单号和处置凭证，由负责人核对；不要另建一笔处置。"
                  : "防重复编号或业务关联发生冲突。请保留审批编号和页面提示，由负责人核对库存流水；不要重新申请或继续自动执行。"}
          </p>
        ) : null}
        {approval.execution.status === "SUCCEEDED" ? (
          <p>
            {snapshot.kind === "INVENTORY_OPERATION"
              ? "库存流水"
              : snapshot.kind === "PURCHASE_ORDER_APPROVAL"
                ? "采购单记录"
                : snapshot.kind === "GROUP_BUY_DAILY_RECONCILIATION"
                  ? "团购对账记录"
                  : snapshot.kind === "GROUP_BUY_RECONCILIATION_RESOLUTION"
                    ? "团购差异签结记录"
                    : snapshot.kind === "CASH_SHIFT_DIFFERENCE"
                      ? "现金班次交接记录"
                      : "退款业务记录"}
            ：<strong>{approval.execution.resultReference}</strong>
          </p>
        ) : null}
        {canExecute ? (
          <div className="approval-actions">
            <button
              className="button primary"
              type="button"
              disabled={pending || executionPending}
              onClick={() => onExecute(approval)}
            >
              {mustReuseUnknownAttempt
                ? "按原请求核对执行结果"
                : approval.execution.status === "READY"
                  ? `执行${approvalOperationName(snapshot)}`
                  : `重新尝试执行${approvalOperationName(snapshot)}`}
            </button>
          </div>
        ) : null}
      </section>

      {canDecide ? (
        <form
          className="approval-decision-form"
          onSubmit={(event) => {
            event.preventDefault();
          }}
        >
          <label>
            审批原因
            <textarea
              value={decisionReason}
              maxLength={snapshot.kind === "PURCHASE_ORDER_APPROVAL" ? 200 : 500}
              rows={3}
              onChange={(event) => setDecisionReason(event.target.value)}
              placeholder="写明为什么批准或驳回，至少 2 个字"
            />
          </label>
          <p>申请人不能审批自己的申请；系统会再次核验你的账号、门店和当前审批步骤。</p>
          {snapshot.kind === "PURCHASE_ORDER_APPROVAL" ? (
            <p>
              批准只允许采购单进入后续收货，不代表已经收货；不会在本页增加库存、形成应付或付款。
            </p>
          ) : snapshot.kind === "GROUP_BUY_DAILY_RECONCILIATION" ? (
            <p>
              批准后仍要安全执行才会写入逐笔核对结果；不会修改订单金额、库存、会员余额，也不会新增收款、付款或退款。
            </p>
          ) : snapshot.kind === "GROUP_BUY_RECONCILIATION_RESOLUTION" ? (
            <p>
              批准后仍要安全执行并取得签结记录，才会追加平台应收或结算调整；原团购履约、订单、库存、会员余额和既有收付款保持不变。
            </p>
          ) : snapshot.kind === "CASH_SHIFT_DIFFERENCE" ? (
            <p>
              {snapshot.assessment.approvalRequirement === "OWNER"
                ? "这是高风险现金差异，只能老板审批。批准后仍要安全执行并取得交接记录，班次才会关闭。"
                : "这笔现金差异可由另一位店长或老板审批。批准后仍要安全执行并取得交接记录，班次才会关闭。"}
              发起人、代办交班人和接班人不能自批，服务端会再次核验。
            </p>
          ) : null}
          <div className="approval-actions">
            {approval.actions.canReject ? (
              <button
                className="button danger"
                type="button"
                disabled={pending || decisionReason.trim().length < 2}
                onClick={() =>
                  onSubmit({
                    kind: "DECIDE",
                    request: {
                      approvalId: approval.approvalId,
                      expectedCaseVersion: approval.version,
                      expectedStepNumber: step.stepNumber,
                      decision: "REJECT",
                      reason: decisionReason.trim(),
                      idempotencyKey: crypto.randomUUID(),
                    },
                  })
                }
              >
                <XCircle size={16} aria-hidden="true" /> 驳回申请
              </button>
            ) : null}
            {approval.actions.canApprove ? (
              <button
                className="button primary"
                type="button"
                disabled={pending || decisionReason.trim().length < 2}
                onClick={() =>
                  onSubmit({
                    kind: "DECIDE",
                    request: {
                      approvalId: approval.approvalId,
                      expectedCaseVersion: approval.version,
                      expectedStepNumber: step.stepNumber,
                      decision: "APPROVE",
                      reason: decisionReason.trim(),
                      idempotencyKey: crypto.randomUUID(),
                    },
                  })
                }
              >
                <CheckCircle2 size={16} aria-hidden="true" />
                {snapshot.kind === "PURCHASE_ORDER_APPROVAL"
                  ? "批准采购单"
                  : snapshot.kind === "GROUP_BUY_DAILY_RECONCILIATION"
                    ? "批准团购日对账"
                    : snapshot.kind === "GROUP_BUY_RECONCILIATION_RESOLUTION"
                      ? "批准团购差异处置"
                      : snapshot.kind === "CASH_SHIFT_DIFFERENCE"
                        ? "批准现金差异并等待关闭班次"
                        : snapshot.kind === "INVENTORY_OPERATION" &&
                            snapshot.operationType === "LOSS_OUT"
                          ? "批准报损并等待安全执行"
                          : "批准并等待安全执行"}
              </button>
            ) : null}
          </div>
        </form>
      ) : null}

      {approval.actions.canCancel && !legacyLossReadOnly ? (
        <form
          className="approval-cancel-form"
          onSubmit={(event) => {
            event.preventDefault();
            if (cancelReason.trim().length < 2) return;
            onSubmit({
              kind: "CANCEL",
              request: {
                approvalId: approval.approvalId,
                expectedCaseVersion: approval.version,
                reason: cancelReason.trim(),
                idempotencyKey: crypto.randomUUID(),
              },
            });
          }}
        >
          <label>
            撤回原因
            <textarea
              value={cancelReason}
              maxLength={500}
              rows={2}
              onChange={(event) => setCancelReason(event.target.value)}
              placeholder="说明为什么不再申请，至少 2 个字"
            />
          </label>
          <button
            className="button secondary"
            type="submit"
            disabled={pending || cancelReason.trim().length < 2}
          >
            撤回我的申请
          </button>
        </form>
      ) : null}

      {!canDecide && !approval.actions.canCancel && approval.actions.unavailableReason ? (
        <p className="approval-unavailable">{approval.actions.unavailableReason}</p>
      ) : null}

      <section className="approval-timeline">
        <h3>处理记录</h3>
        {approval.timeline.map((event) => (
          <div key={event.eventId}>
            <Clock3 size={15} aria-hidden="true" />
            <p>
              <strong>{event.summary}</strong>
              <span>
                {event.actor.displayName} · {time(event.occurredAt)}
                {event.reason ? " · 原因：" + event.reason : ""}
              </span>
            </p>
          </div>
        ))}
      </section>
    </article>
  );
}

function ApprovalList({
  view,
  selectedApprovalId,
}: {
  view: ApprovalListView;
  selectedApprovalId: string | undefined;
}): React.JSX.Element {
  const query = useQuery({
    queryKey: ["approvals", view],
    queryFn: () => listApprovals(view),
    refetchInterval: 2_000,
  });
  if (query.isLoading) return <div className="empty-state">正在读取审批记录</div>;
  if (query.isError) {
    return (
      <div className="approval-warning" role="alert">
        审批记录暂时无法安全读取，旧内容已隐藏。
        <button className="button secondary compact" type="button" onClick={() => query.refetch()}>
          重新读取
        </button>
      </div>
    );
  }
  if (!query.data?.items.length) {
    return (
      <div className="empty-state">
        {view === "TO_DECIDE"
          ? "当前没有轮到你审批的申请"
          : view === "MINE"
            ? "你还没有待处理的申请"
            : "当前没有审批历史"}
      </div>
    );
  }
  return (
    <div className="approval-list">
      {query.data.items.map((item) => (
        <NavLink
          className={
            "approval-list-card" + (selectedApprovalId === item.approvalId ? " is-selected" : "")
          }
          key={item.approvalId}
          to={"/review/" + encodeURIComponent(item.approvalId) + "?view=" + view}
        >
          <div>
            <span className="approval-kicker">{item.originalDocumentLabel}</span>
            <h3>{item.title}</h3>
            <p>
              申请人 {item.applicant.displayName} · {time(item.requestedAt)}
            </p>
          </div>
          <strong className="approval-list-amount">
            {approvalListBusinessSummary(item.summary)}
          </strong>
          <span className={"approval-status status-" + item.status.toLowerCase()}>
            {approvalStatusLabels[item.status]}
          </span>
          <p className="approval-current-step">当前：{item.currentStepLabel}</p>
        </NavLink>
      ))}
    </div>
  );
}

export function ApprovalCenterDesk({
  operator,
  effectiveRoles,
  personalSession = true,
  simulated = false,
}: {
  operator: OperatorIdentity;
  effectiveRoles: readonly OperatorRole[];
  personalSession?: boolean;
  simulated?: boolean;
}): React.JSX.Element {
  const queryClient = useQueryClient();
  const location = useLocation();
  const navigate = useNavigate();
  const { approvalId } = useParams<{ approvalId: string }>();
  const requestedView = new URLSearchParams(location.search).get("view");
  const manualReviewFilterSelection = manualReviewFilterFromSearch(location.search);
  const manualReviewFilter = manualReviewFilterSelection.filter;
  const defaultView: ApprovalListView = effectiveRoles.some(
    (role) => role === "OWNER" || role === "MANAGER",
  )
    ? "TO_DECIDE"
    : "MINE";
  const selectedView: WorkCenterView =
    requestedView === "TO_DECIDE" ||
    requestedView === "MINE" ||
    requestedView === "HISTORY" ||
    requestedView === "SERVICE" ||
    requestedView === "MANUAL"
      ? requestedView
      : defaultView;
  const summary = useQuery({
    queryKey: ["approval-summary"],
    queryFn: getApprovalSummary,
    refetchInterval: 2_000,
  });
  const detail = useQuery({
    queryKey: ["approval", approvalId],
    queryFn: () => getApproval(approvalId ?? ""),
    enabled: Boolean(approvalId) && selectedView !== "MANUAL" && selectedView !== "SERVICE",
    refetchInterval: 2_000,
  });
  const serviceRequests = useQuery({
    queryKey: serviceWorkItemsQueryKey(operator),
    queryFn: listServiceWorkItems,
    refetchInterval: 1_000,
    enabled: selectedView === "SERVICE",
  });
  const [queryNotice, setQueryNotice] = useState<string | null>(null);
  const [executionLookupPending, setExecutionLookupPending] = useState(false);
  const recovery = useApprovalMutationRecovery(operator, (approval, command, recoveredExisting) => {
    setQueryNotice(
      command.kind === "DECIDE"
        ? approval.status === "REJECTED"
          ? approval.snapshot.kind === "CASH_SHIFT_DIFFERENCE"
            ? "现金差异审批已驳回，班次已重新开放盘点；原审批记录继续保留。"
            : "审批已驳回，申请不会执行。"
          : "审批决定已记录；已批准的申请还要等待安全执行。"
        : command.kind === "CANCEL"
          ? "申请已撤回，不会继续执行。"
          : command.kind === "CREATE_INVENTORY_OPERATION"
            ? "库存审批申请已提交，当前尚未改变库存。"
            : command.kind === "CREATE_PAID_ORDER_ITEM_RETURN"
              ? "已结账商品退回申请已提交；当前尚未退款、减少原订单或处理退回实物。"
              : command.kind === "CREATE_PURCHASE_ORDER_APPROVAL"
                ? recoveredExisting
                  ? `已查到原申请后续状态：${approvalStatusLabels[approval.status]}。这不是刚创建成功，也不代表已经收货、增加库存、形成应付或付款。`
                  : "采购审批申请已提交，当前仍未收货、未增加库存、未形成应付、未付款。"
                : command.kind === "CREATE_GROUP_BUY_DAILY_RECONCILIATION"
                  ? recoveredExisting
                    ? `已权威查到原团购日对账申请：${approvalStatusLabels[approval.status]}。系统没有另建一张日对账单。`
                    : "团购日对账申请已提交并完成权威回读；当前尚未写入核对结果，也没有修改订单、库存、会员余额或收付款。"
                  : command.kind === "CREATE_GROUP_BUY_RECONCILIATION_RESOLUTION"
                    ? recoveredExisting
                      ? `已权威查到原团购差异处置申请：${approvalStatusLabels[approval.status]}。系统没有另建一笔处置。`
                      : "团购差异处置申请已提交并完成权威回读；当前尚未新增平台应收或结算调整，也没有改写原业务。"
                    : "申请已提交，当前还没有退款。",
    );
    const targetView =
      (command.kind === "CREATE_PURCHASE_ORDER_APPROVAL" ||
        command.kind === "CREATE_GROUP_BUY_DAILY_RECONCILIATION" ||
        command.kind === "CREATE_GROUP_BUY_RECONCILIATION_RESOLUTION") &&
      recoveredExisting &&
      !approval.status.startsWith("PENDING_")
        ? "HISTORY"
        : command.kind === "CREATE_ORDER_CASH_REFUND" ||
            command.kind === "CREATE_MEMBER_BALANCE_REFUND" ||
            command.kind === "CREATE_PAID_ORDER_ITEM_RETURN" ||
            command.kind === "CREATE_INVENTORY_OPERATION" ||
            command.kind === "CREATE_PURCHASE_ORDER_APPROVAL" ||
            command.kind === "CREATE_GROUP_BUY_DAILY_RECONCILIATION" ||
            command.kind === "CREATE_GROUP_BUY_RECONCILIATION_RESOLUTION"
          ? "MINE"
          : selectedView;
    navigate(`/review/${encodeURIComponent(approval.approvalId)}?view=${targetView}`);
  });
  const executionRecovery = useApprovalExecutionRecovery(operator, (approval) => {
    const operationName = approvalOperationName(approval.snapshot);
    setQueryNotice(
      approval.execution.status === "SUCCEEDED"
        ? approval.snapshot.kind === "INVENTORY_OPERATION"
          ? `${operationName}已安全完成，并已记录库存流水编号。`
          : approval.snapshot.kind === "PURCHASE_ORDER_APPROVAL"
            ? "采购单审批已安全完成，采购单可以进入后续收货；当前没有收货、库存、应付或付款结果。"
            : approval.snapshot.kind === "GROUP_BUY_DAILY_RECONCILIATION"
              ? "团购日对账已安全完成，逐笔一致和差异结果已经记录；订单、库存、会员余额和收付款均未改变。"
              : approval.snapshot.kind === "GROUP_BUY_RECONCILIATION_RESOLUTION"
                ? "团购差异处置已安全签结，平台应收和结算调整记录已追加；原团购履约、订单、库存、会员余额和既有收付款均未改写。"
                : approval.snapshot.kind === "CASH_SHIFT_DIFFERENCE"
                  ? "现金差异已审批并关闭班次；审批人、交接双方、逐分金额和差异原因均已留痕。"
                  : `${operationName}已安全完成，并已记录退款业务编号。`
        : approval.execution.status === "BLOCKED_PRECONDITION_CHANGED"
          ? approval.snapshot.kind === "INVENTORY_OPERATION"
            ? "商品或库存数量已变化，旧申请已停止执行；请重新核对当前库存后发起新申请。"
            : approval.snapshot.kind === "PURCHASE_ORDER_APPROVAL"
              ? "采购草稿内容或状态已变化，旧申请已停止执行；请重新核对采购单后发起新申请。"
              : approval.snapshot.kind === "GROUP_BUY_DAILY_RECONCILIATION"
                ? "平台日结单或团购履约事实已变化，旧申请已停止执行；请重新检查日结单后发起新申请。"
                : approval.snapshot.kind === "GROUP_BUY_RECONCILIATION_RESOLUTION"
                  ? "原 GH 对账事实、异常工作项或逐笔处置依据已变化，旧申请已停止执行；请重新打开团购差异处理页。"
                  : approval.snapshot.kind === "CASH_SHIFT_DIFFERENCE"
                    ? "交接班现金、责任人或版本已变化，旧审批已停止执行；请回到交接班页面重新核对。"
                    : approval.snapshot.kind === "MEMBER_BALANCE_REFUND"
                      ? "会员消费或退款情况已变化，旧申请已停止执行；请重新核对会员资金流水后发起新申请。"
                      : "原单已变化，旧申请已停止执行；请重新核对原单后发起新申请。"
          : approval.execution.status === "BLOCKED_INTEGRITY_REVIEW"
            ? `${operationName}的防重复编号或业务关联发生冲突，已停止自动执行并转为人工核对。`
            : approval.execution.status === "FAILED_TERMINAL"
              ? `${operationName}未自动完成，已转为需要人工核对。`
              : approval.execution.status === "FAILED_RETRYABLE"
                ? `${operationName}尚未完成；系统保留原请求，可按页面提示安全重试。`
                : `${operationName}结果尚未终结；请勿重复发起或执行。`,
    );
    navigate("/review/" + encodeURIComponent(approval.approvalId) + "?view=HISTORY");
  });
  const paidReturnExecution = usePaidOrderItemReturnExecutionRecovery(
    operator,
    (result, recovery) => {
      if (result.snapshot.kind !== "ORDER_ITEM_PAID_RETURN") {
        setQueryNotice("商品退回执行回执与原审批不一致，系统仍保留原请求，请先查询。");
        return;
      }
      const snapshot = result.snapshot;
      const inventoryResult =
        snapshot.inventoryEffect === "SALEABLE_STOCK_RESTORED"
          ? `库存已增加 ${snapshot.returnQuantity} 件。`
          : "不可再次销售的实物不会增加可售库存，也不会再扣一次库存。";
      setQueryNotice(
        result.execution.status === "SUCCEEDED"
          ? recovery.expected.refundAllocations
            ? `商品已退回 ${snapshot.returnQuantity} 件，组合支付各路退款合计 ${fee(snapshot.amountFen)} 已全部确认。${inventoryResult}`
            : recovery.expected.refundExecutionMode === "LOCAL_CASH_CONFIRMATION"
              ? `商品已退回 ${snapshot.returnQuantity} 件，订单金额减少 ${fee(snapshot.amountFen)}，现金退款已记录。${inventoryResult}`
              : recovery.expected.refundExecutionMode === "MANUAL_STATIC_CODE_CONFIRMATION"
                ? `商品已退回 ${snapshot.returnQuantity} 件，已登记外部退款凭证和 ${fee(snapshot.amountFen)} 退款。${inventoryResult}`
                : recovery.expected.refundExecutionMode === "MEMBER_BALANCE_CLOUD_REFUND"
                  ? `商品已退回 ${snapshot.returnQuantity} 件，会员余额已退回 ${fee(snapshot.amountFen)}。${inventoryResult}`
                  : `商品已退回 ${snapshot.returnQuantity} 件，官方支付渠道已确认退款 ${fee(snapshot.amountFen)}。${inventoryResult}`
          : result.execution.status === "FAILED_RETRYABLE"
            ? "商品退回尚未完成；本次请求已经结束，请按当前审批状态重新尝试。"
            : approvalExecutionResultIsTerminal(result)
              ? "商品退回已停止自动执行，请按审批页显示的最新原因核对，不要另起一笔退款。"
              : recovery.expected.refundAllocations
                ? "组合支付中至少一路仍未取得终局结果；系统保留全部原确认和凭证，只能查询或安全重试这一整笔。"
                : recovery.expected.refundExecutionMode === "OFFICIAL_PROVIDER_REFUND"
                  ? "官方支付退款仍未取得终局结果；系统保留原请求，只能查询或安全重试这一笔。"
                  : "商品退回仍未取得终局结果；系统保留原请求，请勿重复退款或处理库存。",
      );
      navigate(`/review/${encodeURIComponent(recovery.command.approvalId)}?view=HISTORY`);
    },
  );

  const queryRecovery = async (): Promise<void> => {
    if (recovery.recoveryView.status !== "OWNED") return;
    const pendingRecovery = recovery.recoveryView.recovery;
    if (pendingRecovery.command.kind === "CREATE_GROUP_BUY_RECONCILIATION_RESOLUTION") {
      try {
        const latest = await findGroupBuyReconciliationResolutionApprovalForRecovery(
          pendingRecovery.command.request,
        );
        if (!latest) {
          setQueryNotice(
            "没有查到与原 GH 单号、逐笔处置、凭证和金额完全一致的申请。系统继续保留原请求；如需重试，只能使用原操作。",
          );
          return;
        }
        if (
          !clearApprovalMutationRecovery(pendingRecovery, approvalMutationIdentityKey(operator))
        ) {
          throw new ApiRequestError(
            "APPROVAL_RECOVERY_CLEAR_FAILED",
            "已权威查到原团购差异处置申请，但本机恢复锁无法安全结束；请勿再次提交",
          );
        }
        queryClient.setQueryData(["approval", latest.approvalId], latest);
        recovery.refresh();
        setQueryNotice(
          `已权威查到原团购差异处置申请：${approvalStatusLabels[latest.status]}。恢复锁已安全结束，系统没有另建一笔处置。`,
        );
        const targetView = latest.status.startsWith("PENDING_") ? "MINE" : "HISTORY";
        navigate(`/review/${encodeURIComponent(latest.approvalId)}?view=${targetView}`);
        void Promise.allSettled([
          summary.refetch(),
          queryClient.invalidateQueries({ queryKey: ["approvals"] }),
        ]);
      } catch (error) {
        recovery.refresh();
        setQueryNotice(failureMessage(error, "暂时无法权威查询原团购差异处置，系统仍保留原请求"));
      }
      return;
    }
    if (pendingRecovery.command.kind === "CREATE_GROUP_BUY_DAILY_RECONCILIATION") {
      try {
        const latest = await findGroupBuyDailyReconciliationApprovalForRecovery(
          pendingRecovery.command.request,
        );
        if (!latest) {
          setQueryNotice(
            "没有查到与原 GH 单号、平台日结行、金额和核对事实完全一致的申请。系统继续保留原请求；如需重试，只能使用原操作。",
          );
          return;
        }
        if (
          !clearApprovalMutationRecovery(pendingRecovery, approvalMutationIdentityKey(operator))
        ) {
          throw new ApiRequestError(
            "APPROVAL_RECOVERY_CLEAR_FAILED",
            "已权威查到原团购日对账申请，但本机恢复锁无法安全结束；请勿再次提交",
          );
        }
        queryClient.setQueryData(["approval", latest.approvalId], latest);
        recovery.refresh();
        setQueryNotice(
          `已权威查到原团购日对账申请：${approvalStatusLabels[latest.status]}。恢复锁已安全结束，系统没有另建一张日对账单。`,
        );
        const targetView = latest.status.startsWith("PENDING_") ? "MINE" : "HISTORY";
        navigate(`/review/${encodeURIComponent(latest.approvalId)}?view=${targetView}`);
        void Promise.allSettled([
          summary.refetch(),
          queryClient.invalidateQueries({ queryKey: ["approvals"] }),
        ]);
      } catch (error) {
        recovery.refresh();
        setQueryNotice(failureMessage(error, "暂时无法权威查询原团购日对账状态，系统仍保留原请求"));
      }
      return;
    }
    if (pendingRecovery.command.kind === "CREATE_PURCHASE_ORDER_APPROVAL") {
      try {
        const latest = await findPurchaseOrderApprovalForRecovery(pendingRecovery.command.request);
        if (!latest) {
          setQueryNotice(
            "没有查到与原采购单、版本、原因和核对结果完全一致的申请。系统继续保留原请求；如需重试，只能使用原操作。",
          );
          return;
        }
        if (
          !clearApprovalMutationRecovery(pendingRecovery, approvalMutationIdentityKey(operator))
        ) {
          throw new ApiRequestError(
            "APPROVAL_RECOVERY_CLEAR_FAILED",
            "已查到原申请后续状态，但本机恢复锁无法安全结束；请勿再次提交",
          );
        }
        queryClient.setQueryData(["approval", latest.approvalId], latest);
        recovery.refresh();
        setQueryNotice(
          `已查到原申请后续状态：${approvalStatusLabels[latest.status]}。恢复锁已安全结束；这不是刚创建成功，也不代表已经收货。`,
        );
        const targetView = latest.status.startsWith("PENDING_") ? "MINE" : "HISTORY";
        navigate(`/review/${encodeURIComponent(latest.approvalId)}?view=${targetView}`);
        void Promise.allSettled([
          summary.refetch(),
          queryClient.invalidateQueries({ queryKey: ["approvals"] }),
        ]);
      } catch (error) {
        recovery.refresh();
        setQueryNotice(failureMessage(error, "暂时无法精确查询原采购审批状态，系统仍保留原请求"));
      }
      return;
    }
    if (
      pendingRecovery.command.kind === "CREATE_ORDER_CASH_REFUND" ||
      pendingRecovery.command.kind === "CREATE_MEMBER_BALANCE_REFUND" ||
      pendingRecovery.command.kind === "CREATE_PAID_ORDER_ITEM_RETURN" ||
      pendingRecovery.command.kind === "CREATE_INVENTORY_OPERATION"
    ) {
      setQueryNotice(
        "已打开“我的申请”。请按原业务记录查找；没有明确记录前，仍可按原操作编号安全重试。",
      );
      navigate("/review?view=MINE");
      await summary.refetch();
      return;
    }
    const id = pendingRecovery.command.request.approvalId;
    try {
      const latest = await getApproval(id);
      const hasAdvanced =
        latest.version > pendingRecovery.command.request.expectedCaseVersion ||
        (pendingRecovery.command.kind === "CANCEL" && latest.status === "CANCELLED") ||
        (pendingRecovery.command.kind === "DECIDE" &&
          pendingRecovery.command.request.decision === "REJECT" &&
          latest.status === "REJECTED");
      if (hasAdvanced) {
        clearApprovalMutationRecovery(pendingRecovery, approvalMutationIdentityKey(operator));
        recovery.refresh();
        setQueryNotice("已查到原操作后的最新记录，不会重复提交。");
      } else {
        setQueryNotice("最新记录还没有这次操作结果；如需继续，只能按原操作重试。");
      }
      navigate("/review/" + encodeURIComponent(id) + "?view=" + selectedView);
      await detail.refetch();
    } catch (error) {
      setQueryNotice(failureMessage(error, "暂时无法查询最新状态，系统仍保留原操作"));
    }
  };

  const queryExecutionRecovery = async (): Promise<void> => {
    if (executionRecovery.recoveryView.status !== "OWNED") return;
    const pendingRecovery = executionRecovery.recoveryView.recovery;
    try {
      const latest = await getApproval(pendingRecovery.approvalId);
      const operationName = approvalOperationName(latest.snapshot);
      queryClient.setQueryData(["approval", latest.approvalId], latest);
      if (approvalExecutionResultIsTerminal(latest)) {
        if (!clearApprovalExecutionRecovery(pendingRecovery, operator)) {
          throw new ApiRequestError(
            "APPROVAL_EXECUTION_RECOVERY_CLEAR_FAILED",
            "已查到终局结果，但本机执行记录无法清除；请勿再次执行",
          );
        }
        setQueryNotice(
          latest.execution.status === "SUCCEEDED"
            ? `已查到${operationName}完成记录，不会重复执行。`
            : latest.execution.status === "BLOCKED_PRECONDITION_CHANGED"
              ? latest.snapshot.kind === "INVENTORY_OPERATION"
                ? "已确认商品或库存数量变化，旧申请不会继续执行；请重新核对当前库存后申请。"
                : latest.snapshot.kind === "PURCHASE_ORDER_APPROVAL"
                  ? "已确认采购草稿内容或状态变化，旧申请不会继续执行；请重新核对采购单后申请。"
                  : latest.snapshot.kind === "GROUP_BUY_DAILY_RECONCILIATION"
                    ? "已确认平台日结单或团购履约事实变化，旧申请不会继续执行；请重新检查日结单后申请。"
                    : latest.snapshot.kind === "GROUP_BUY_RECONCILIATION_RESOLUTION"
                      ? "已确认原 GH 对账事实、异常工作项或逐笔处置依据变化，旧申请不会继续执行；请重新核对后申请。"
                      : latest.snapshot.kind === "MEMBER_BALANCE_REFUND"
                        ? "已确认会员消费或退款情况变化，旧申请不会继续退款；请重新核对资金流水后申请。"
                        : "已确认原单变化，旧申请不会继续退款；请重新申请。"
              : latest.execution.status === "BLOCKED_INTEGRITY_REVIEW"
                ? latest.snapshot.kind === "PURCHASE_ORDER_APPROVAL"
                  ? "已确认采购单关联校验冲突，自动执行已停止；请保留审批编号并人工核对采购单状态。"
                  : latest.snapshot.kind === "GROUP_BUY_DAILY_RECONCILIATION"
                    ? "已确认 GH 单号、平台日结行或团购履约关联冲突，自动执行已停止；请保留 GH 单号并逐笔核对。"
                    : latest.snapshot.kind === "GROUP_BUY_RECONCILIATION_RESOLUTION"
                      ? "已确认 GH 单号、逐笔处置或结算账簿关联冲突，自动执行已停止；请保留 GH 单号和处置凭证人工核对。"
                      : `已确认${operationName}关联校验冲突，自动执行已停止；请保留审批编号并人工核对库存流水。`
                : "已确认这次执行不能自动继续，请按异常提示核对。",
        );
      } else if (latest.execution.status === "FAILED_RETRYABLE") {
        if (!clearApprovalExecutionRecovery(pendingRecovery, operator)) {
          throw new ApiRequestError(
            "APPROVAL_EXECUTION_RECOVERY_CLEAR_FAILED",
            "已确认旧执行失败，但本机无法结束旧操作记录；请勿再次执行",
          );
        }
        setQueryNotice(`已确认上次执行失败。旧操作已结束，可按当前申请重新尝试${operationName}。`);
      } else {
        markApprovalExecutionOutcomeUnknown(pendingRecovery, operator);
        setQueryNotice(
          `${operationName}仍未取得终局结果，系统继续保留原请求。请勿重复发起或执行。`,
        );
      }
      executionRecovery.refresh();
      navigate("/review/" + encodeURIComponent(latest.approvalId) + "?view=HISTORY");
      await Promise.all([summary.refetch(), detail.refetch()]);
    } catch (error) {
      setQueryNotice(failureMessage(error, "暂时无法查询审批执行结果，系统仍保留原请求"));
    }
  };

  const queryPaidReturnExecutionRecovery = async (): Promise<void> => {
    if (paidReturnExecution.recoveryView.status !== "OWNED") return;
    const pendingRecovery = paidReturnExecution.recoveryView.recovery;
    try {
      const latest = await getApproval(pendingRecovery.command.approvalId);
      queryClient.setQueryData(["approval", latest.approvalId], latest);
      const settlement = settlePaidOrderItemReturnExecutionRecovery(
        pendingRecovery,
        operator,
        latest.execution.status,
      );
      if (settlement === null) {
        throw new ApiRequestError(
          latest.execution.status === "SUCCEEDED"
            ? "PAID_RETURN_EXECUTION_RECOVERY_CLEAR_FAILED"
            : "PAID_RETURN_EXECUTION_RECOVERY_SAVE_FAILED",
          latest.execution.status === "SUCCEEDED"
            ? "已查到商品退回成功结果，但本机原请求无法清除；请勿再次执行"
            : "商品退回尚未成功，但本机无法继续保留原请求；当前已暂停重试",
        );
      }
      if (latest.execution.status === "SUCCEEDED") {
        setQueryNotice(
          pendingRecovery.expected.inventoryEffect === "SALEABLE_STOCK_RESTORED"
            ? "已查到商品退回、原路退款和可售库存增加的完成记录，不会重复执行。"
            : "已查到商品退回和原路退款的完成记录；不可售实物没有增加或再次扣减库存，不会重复执行。",
        );
      } else {
        setQueryNotice(
          latest.execution.status === "BLOCKED_PRECONDITION_CHANGED"
            ? "原订单、商品可退数量、退款或实物状态事实已经变化；系统仍保留原请求，请按审批页提示核对。"
            : approvalExecutionResultIsTerminal(latest)
              ? "已确认原商品退回不能自动继续；系统仍保留原请求，请保留审批编号，在异常待核对区跟进。"
              : pendingRecovery.expected.refundAllocations
                ? "组合支付中至少一路仍未返回终局结果；系统继续保留全部原确认和凭证，请勿修改任一路或另起退款。"
                : pendingRecovery.expected.refundExecutionMode === "OFFICIAL_PROVIDER_REFUND"
                  ? "官方支付渠道仍未返回终局退款结果；系统继续保留原请求，请勿另起一笔退款。"
                  : "商品退回仍未取得终局结果；系统继续保留原请求，请勿重复退款或处理库存。",
        );
      }
      paidReturnExecution.refresh();
      navigate(`/review/${encodeURIComponent(latest.approvalId)}?view=HISTORY`);
      await Promise.allSettled([
        summary.refetch(),
        detail.refetch(),
        queryClient.invalidateQueries({ queryKey: ["orders"] }),
        queryClient.invalidateQueries({ queryKey: ["paid-order-item-return-candidates"] }),
      ]);
    } catch (error) {
      setQueryNotice(failureMessage(error, "暂时无法查询商品退回结果，系统仍保留原请求"));
    }
  };

  const executeCurrentApproval = async (approval: ApprovalRequestReadModel): Promise<void> => {
    if (
      executionRecovery.recoveryView.status === "OWNED" &&
      executionRecovery.recoveryView.recovery.approvalId === approval.approvalId
    ) {
      if (
        (approval.execution.status === "PROCESSING" ||
          approval.execution.status === "OUTCOME_UNKNOWN" ||
          approval.execution.status === "FAILED_RETRYABLE") &&
        executionRecovery.recoveryView.recovery.expectedCaseVersion < approval.version
      ) {
        executionRecovery.restart(approval);
      } else {
        executionRecovery.retry();
      }
      return;
    }
    setExecutionLookupPending(true);
    try {
      const latest = await getApproval(approval.approvalId);
      queryClient.setQueryData(["approval", latest.approvalId], latest);
      const eligibleStatus =
        latest.execution.status === "READY" ||
        latest.execution.status === "PROCESSING" ||
        latest.execution.status === "FAILED_RETRYABLE" ||
        latest.execution.status === "OUTCOME_UNKNOWN";
      if (!latest.actions.canRetryExecution || !eligibleStatus) {
        setQueryNotice("已查询最新审批状态；当前记录不再允许执行，请按页面显示的最新结果处理。");
        return;
      }
      executionRecovery.submit(latest);
    } catch (error) {
      setQueryNotice(failureMessage(error, "暂时无法查询最新审批状态，本次没有执行"));
    } finally {
      setExecutionLookupPending(false);
    }
  };

  return (
    <main className="approval-center">
      <header className="approval-page-header">
        <div>
          <span className="approval-kicker">今天要处理的经营工作</span>
          <h1>工作中心</h1>
          <p>
            这里汇总审批、自己的申请、现场请求和异常核对。每项处理都会带你回到原申请或原球台，
            不会在汇总页拼出一个不可靠的总数。
          </p>
        </div>
        <div className="approval-safety-badge">
          <ShieldCheck size={20} aria-hidden="true" />
          申请人不能审批自己的申请
        </div>
      </header>

      {summary.isError ? (
        <div className="approval-warning" role="alert">
          审批数量暂时无法确认，列表不会用旧数量冒充最新状态。
        </div>
      ) : null}
      <nav className="approval-tabs" aria-label="工作中心分类">
        {listTabs.map((tab) => (
          <button
            className={selectedView === tab.id ? "is-active" : ""}
            key={tab.id}
            type="button"
            onClick={() => navigate("/review?view=" + tab.id)}
          >
            {tab.label}
            {tab.summaryKey === "serviceCount" ? (
              <span>
                {selectedView !== "SERVICE" || serviceRequests.isLoading || serviceRequests.isError
                  ? "—"
                  : serviceRequests.data?.activeCount}
              </span>
            ) : tab.summaryKey ? (
              <span>
                {summary.isLoading || summary.isError ? "—" : summary.data?.[tab.summaryKey]}
              </span>
            ) : null}
          </button>
        ))}
      </nav>

      {selectedView === "HISTORY" && (summary.data?.executionAttentionCount ?? 0) > 0 ? (
        <div className="approval-info">
          <AlertTriangle size={18} aria-hidden="true" />
          <p>
            全部记录中有 {summary.data?.executionAttentionCount} 笔已审批操作的执行结果需要关注。
          </p>
        </div>
      ) : null}
      {queryNotice ? <output className="approval-info">{queryNotice}</output> : null}
      <ApprovalRecoveryNotice
        recoveryView={recovery.recoveryView}
        pending={recovery.mutation.isPending}
        onRetry={recovery.retry}
        onQuery={() => {
          void queryRecovery();
        }}
      />
      <ApprovalExecutionRecoveryNotice
        recoveryView={executionRecovery.recoveryView}
        pending={executionRecovery.mutation.isPending}
        onQuery={() => {
          void queryExecutionRecovery();
        }}
      />
      <PaidOrderItemReturnExecutionRecoveryNotice
        recoveryView={paidReturnExecution.recoveryView}
        pending={paidReturnExecution.mutation.isPending}
        onQuery={() => {
          void queryPaidReturnExecutionRecovery();
        }}
        onRetry={paidReturnExecution.retry}
      />
      {recovery.mutation.isError ? (
        <div className="approval-warning" role="alert">
          {failureMessage(recovery.mutation.error, "操作结果无法确认")}
        </div>
      ) : null}
      {executionRecovery.mutation.isError ? (
        <div className="approval-warning" role="alert">
          {failureMessage(
            executionRecovery.mutation.error,
            "审批执行结果无法确认，系统仍保留原请求",
          )}
        </div>
      ) : null}
      {paidReturnExecution.mutation.isError ? (
        <div className="approval-warning" role="alert">
          {failureMessage(
            paidReturnExecution.mutation.error,
            "商品退回执行结果无法确认，系统仍保留原请求",
          )}
        </div>
      ) : null}

      {selectedView === "MANUAL" ? (
        <ApprovalManualReviewPanel
          key={`${operator.tenantId}:${operator.storeId}:${operator.id}:${manualReviewFilterSelection.status}:${manualReviewFilter?.businessDate ?? "ALL"}`}
          operator={operator}
          effectiveRoles={effectiveRoles}
          filter={manualReviewFilter}
          filterInvalid={manualReviewFilterSelection.status === "INVALID"}
          onClearFilter={() => navigate("/review?view=MANUAL")}
        />
      ) : selectedView === "SERVICE" ? (
        <WorkCenterServicePanel
          operator={operator}
          effectiveRoles={effectiveRoles}
          personalSession={personalSession}
          simulated={simulated}
          data={serviceRequests.data}
          isLoading={serviceRequests.isLoading}
          isError={serviceRequests.isError}
          isFetching={serviceRequests.isFetching}
          onRetry={() => {
            void serviceRequests.refetch();
          }}
        />
      ) : (
        <div className="approval-workspace">
          <ApprovalList
            view={selectedView}
            selectedApprovalId={approvalId ? decodeURIComponent(approvalId) : undefined}
          />
          {approvalId ? (
            detail.isLoading ? (
              <div className="empty-state">正在读取申请详情</div>
            ) : detail.isError || !detail.data ? (
              <div className="approval-warning" role="alert">
                申请详情暂时无法安全读取，旧内容已隐藏。
                <button
                  className="button secondary compact"
                  type="button"
                  onClick={() => detail.refetch()}
                >
                  重新读取
                </button>
              </div>
            ) : (
              <ApprovalDetail
                approval={detail.data}
                effectiveRoles={effectiveRoles}
                onSubmit={recovery.submit}
                pending={recovery.mutation.isPending}
                executionRecoveryView={executionRecovery.recoveryView}
                executionPending={executionRecovery.mutation.isPending || executionLookupPending}
                paidReturnExecutionRecoveryView={paidReturnExecution.recoveryView}
                paidReturnExecutionPending={paidReturnExecution.mutation.isPending}
                onExecutePaidReturn={paidReturnExecution.submit}
                onExecute={(approval) => {
                  void executeCurrentApproval(approval);
                }}
              />
            )
          ) : (
            <div className="approval-empty-detail">
              <ShieldCheck size={28} aria-hidden="true" />
              <strong>选择一笔申请查看完整内容</strong>
              <p>审批前会显示关键业务数据、申请原因、经营后果和每一步处理记录。</p>
            </div>
          )}
        </div>
      )}
    </main>
  );
}

function yuanInputToFen(value: string): number | null {
  const normalized = value.trim();
  if (!/^(0|[1-9]\d{0,12})(\.\d{1,2})?$/.test(normalized)) return null;
  const [yuan, decimal = ""] = normalized.split(".");
  const fen = Number(yuan) * 100 + Number(decimal.padEnd(2, "0"));
  return Number.isSafeInteger(fen) && fen > 0 ? fen : null;
}

function RefundApprovalPreview({
  preview,
}: {
  preview: OrderCashRefundApprovalPreviewReadModel;
}): React.JSX.Element {
  return (
    <section className="refund-approval-preview" aria-label="现金退款申请预览">
      <header>
        <div>
          <span>本次申请金额</span>
          <strong>{fee(preview.amountFen)}</strong>
        </div>
        <span>{preview.canSubmit ? "可以提交审批" : "暂时不能提交"}</span>
      </header>
      <dl>
        <div>
          <dt>原单</dt>
          <dd>{preview.originalDocumentLabel}</dd>
        </div>
        <div>
          <dt>业务单号</dt>
          <dd>{preview.orderNumber}</dd>
        </div>
        <div>
          <dt>原单已收</dt>
          <dd>{fee(preview.orderPaidFen)}</dd>
        </div>
        <div>
          <dt>此前已退</dt>
          <dd>{fee(preview.orderAlreadyRefundedFen)}</dd>
        </div>
        <div>
          <dt>本次原因</dt>
          <dd>{preview.reason}</dd>
        </div>
        <div>
          <dt>谁来审批</dt>
          <dd>{preview.approverLabel}</dd>
        </div>
      </dl>
      <div className="approval-consequence-grid">
        <section>
          <h4>批准后会发生什么</h4>
          <ul>
            {preview.consequences.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        </section>
        <section>
          <h4>提交后的步骤</h4>
          <ol>
            {preview.nextSteps.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ol>
        </section>
      </div>
      {preview.blocker ? <div className="approval-warning">{preview.blocker}</div> : null}
    </section>
  );
}

function paidReturnExecutionExplanation(
  preview: PaidOrderItemReturnApprovalPreviewReadModel,
): string {
  const packageReturn = preview.itemKind === "PACKAGE";
  const inventoryResult =
    preview.inventoryEffect === "SALEABLE_STOCK_RESTORED"
      ? packageReturn
        ? "套餐内所有受管商品会按原销售成本一次恢复可售库存；本单服务权益同时撤销。"
        : `完好商品会增加 ${preview.returnQuantity} 件可售库存。`
      : packageReturn
        ? "套餐内商品不会增加可售库存，也不会再扣一次；本单服务权益仍随整包撤销。"
        : "已开封或破损商品不会增加可售库存，也不会再扣一次库存。";
  if (preview.refundAllocations) {
    return `审批通过后，收银员按服务端冻结的逐路分配一次完成组合退款；任一路结果未知都会锁定整笔原请求。${inventoryResult}`;
  }
  switch (preview.refundExecutionMode) {
    case "LOCAL_CASH_CONFIRMATION":
      return `审批通过后，由收银员当面退还现金并确认；成功后才会减少订单。${inventoryResult}`;
    case "MANUAL_STATIC_CODE_CONFIRMATION":
      return `审批通过后，收银员先在原静态码渠道退款，再填写可核对的外部退款凭证。${inventoryResult}`;
    case "MEMBER_BALANCE_CLOUD_REFUND":
      return `审批通过后，系统通过联网服务退回原会员余额；没有成功记录前不能当成已退款。${inventoryResult}`;
    case "OFFICIAL_PROVIDER_REFUND":
      return `审批通过后，系统查询并使用原官方支付渠道退款；结果未知时只核对原请求。${inventoryResult}`;
    case null:
      return `退款路径暂时无法确认，页面不会提交。${inventoryResult}`;
  }
}

function PaidReturnApprovalPreview({
  preview,
}: {
  preview: PaidOrderItemReturnApprovalPreviewReadModel;
}): React.JSX.Element {
  const packageReturn = preview.itemKind === "PACKAGE";
  return (
    <section
      className="refund-approval-preview"
      aria-label={packageReturn ? "已结账门店套餐整包退回申请预览" : "已结账商品退回申请预览"}
    >
      <header>
        <div>
          <span>本次原路退款</span>
          <strong>{fee(preview.amountFen)}</strong>
        </div>
        <span>{preview.canSubmit ? "可以提交审批" : "暂时不能提交"}</span>
      </header>
      <dl>
        <div>
          <dt>{packageReturn ? "门店套餐" : "商品"}</dt>
          <dd>{preview.productName}</dd>
        </div>
        {packageReturn && preview.lineNumber ? (
          <div>
            <dt>套餐业务单号</dt>
            <dd>{preview.lineNumber}</dd>
          </div>
        ) : null}
        <div>
          <dt>退回数量</dt>
          <dd>{packageReturn ? "整包 1 份" : `${preview.returnQuantity} 件`}</dd>
        </div>
        <div>
          <dt>退款方式</dt>
          <dd>{preview.paymentMethodLabel}</dd>
        </div>
        <div>
          <dt>{packageReturn ? "套餐内商品状态" : "退回实物状态"}</dt>
          <dd>
            {preview.physicalDisposition === "SALEABLE_RETURNED"
              ? "完好可再次销售"
              : "已开封或破损，不可再次销售"}
          </dd>
        </div>
        <div>
          <dt>{packageReturn ? "库存与本单服务" : "可售库存"}</dt>
          <dd>
            {packageReturn
              ? preview.inventoryEffect === "SALEABLE_STOCK_RESTORED"
                ? "全部受管商品按冻结数量和原销售成本恢复；本单服务权益同时撤销"
                : "商品可售库存不增加；本单服务权益仍随整包撤销"
              : preview.inventoryEffect === "SALEABLE_STOCK_RESTORED"
                ? `${preview.inventoryQuantityBefore} → ${preview.inventoryQuantityAfter}（增加 ${preview.returnQuantity} 件）`
                : "不增加，也不会再扣一次；系统记录为不可再次销售"}
          </dd>
        </div>
        {packageReturn && preview.componentNames ? (
          <div>
            <dt>整包内容</dt>
            <dd>{preview.componentNames.join("、")}</dd>
          </div>
        ) : null}
        <div>
          <dt>审批人</dt>
          <dd>{preview.approverLabel}</dd>
        </div>
        <div>
          <dt>原因</dt>
          <dd>{preview.reason}</dd>
        </div>
      </dl>
      {preview.refundAllocations ? (
        <PaidReturnAllocationFacts
          allocations={preview.refundAllocations}
          totalFen={preview.amountFen}
        />
      ) : null}
      <p className="field-help">{paidReturnExecutionExplanation(preview)}</p>
      <div className="approval-consequence-grid">
        <section>
          <h4>批准后会发生什么</h4>
          <ul>
            {preview.consequences.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        </section>
        <section>
          <h4>提交后的步骤</h4>
          <ol>
            {preview.nextSteps.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ol>
        </section>
      </div>
      {preview.blocker ? <div className="approval-warning">{preview.blocker}</div> : null}
    </section>
  );
}

function paidReturnCandidateKey(orderId: string, itemId: string): string {
  return JSON.stringify([orderId, itemId]);
}

function paidReturnQuantityOptions(maximum: number): number[] {
  const options: number[] = [];
  for (let quantity = 1; quantity <= maximum; quantity += 1) options.push(quantity);
  return options;
}

export function PaidOrderItemReturnApprovalPanel({
  operator,
  effectiveRoles,
}: {
  operator: OperatorIdentity;
  effectiveRoles: readonly OperatorRole[];
}): React.JSX.Element | null {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const canApply =
    effectiveRoles.includes("CASHIER") &&
    !effectiveRoles.includes("OWNER") &&
    !effectiveRoles.includes("MANAGER");
  const readOnlyApprover =
    !canApply && (effectiveRoles.includes("OWNER") || effectiveRoles.includes("MANAGER"));
  const visible = canApply || readOnlyApprover;
  const operatorKey = approvalMutationIdentityKey(operator);
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [quantity, setQuantity] = useState(1);
  const [reason, setReason] = useState("");
  const [physicalDisposition, setPhysicalDisposition] =
    useState<SaleReturnPhysicalDisposition>("SALEABLE_RETURNED");
  const [physicalReturnConfirmed, setPhysicalReturnConfirmed] = useState(false);
  const [createdApproval, setCreatedApproval] = useState<ApprovalRequestReadModel | null>(null);
  const candidates = useQuery({
    queryKey: [
      "paid-order-item-return-candidates",
      operator.tenantId,
      operator.storeId,
      operator.id,
    ],
    queryFn: listPaidOrderItemReturnCandidates,
    enabled: canApply,
    refetchInterval: canApply ? 2_000 : false,
  });
  const candidateOrders = candidates.isError ? [] : (candidates.data?.orders ?? []);
  const selected = candidateOrders
    .flatMap((order) =>
      order.items.map((item) => ({
        order,
        item,
        key: paidReturnCandidateKey(order.orderId, item.itemId),
      })),
    )
    .find((candidate) => candidate.key === selectedKey);
  const previewMutation = useMutation({
    mutationFn: () => {
      if (!selected) {
        throw new ApiRequestError("REQUEST_VALIDATION_FAILED", "请选择仍可退回的已结账商品");
      }
      return previewPaidOrderItemReturnApproval({
        orderId: selected.order.orderId,
        itemId: selected.item.itemId,
        quantity,
        physicalDisposition,
        reason: reason.trim(),
      });
    },
  });
  const recovery = useApprovalMutationRecovery(operator, (approval, command) => {
    if (command.kind === "CREATE_PAID_ORDER_ITEM_RETURN") {
      setCreatedApproval(approval);
      setSelectedKey(null);
      previewMutation.reset();
      void queryClient.invalidateQueries({ queryKey: ["paid-order-item-return-candidates"] });
    }
  });
  useEffect(() => {
    if (operatorKey.length === 0) return;
    setSelectedKey(null);
    setQuantity(1);
    setReason("");
    setPhysicalDisposition("SALEABLE_RETURNED");
    setPhysicalReturnConfirmed(false);
    setCreatedApproval(null);
    previewMutation.reset();
  }, [operatorKey, previewMutation.reset]);
  if (!visible) return null;
  if (readOnlyApprover) {
    return (
      <section className="paid-order-return-entry is-readonly">
        <strong>已结账商品退回由收银员发起</strong>
        <p>
          店长或老板不在订单页代替收银员填写退回；申请提交后，请到工作中心核对商品、原路退款、实物状态、库存后果和原因，再批准或驳回。
        </p>
        <button
          className="button secondary compact"
          type="button"
          onClick={() => navigate("/review?view=TO_DECIDE")}
        >
          查看待我审批
        </button>
      </section>
    );
  }
  const reasonLength = [...reason.trim()].length;
  const inputError = !selected
    ? null
    : selected.item.itemKind === "PACKAGE" && quantity !== 1
      ? "门店套餐只能整包退回 1 份，不能拆退其中内容"
      : quantity < 1 || quantity > selected.item.returnableQuantity
        ? `请选择 1 至 ${selected.item.returnableQuantity} 件`
        : reasonLength < 2 || reasonLength > 200
          ? "请填写 2 至 200 字的退回原因"
          : !physicalReturnConfirmed
            ? "请确认实物已经交回门店，所选状态与现场一致"
            : null;
  const preview = previewMutation.data;
  const previewMatches =
    selected !== undefined &&
    preview?.orderId === selected.order.orderId &&
    preview.itemId === selected.item.itemId &&
    preview.returnQuantity === quantity &&
    preview.physicalDisposition === physicalDisposition &&
    preview.reason === reason.trim();
  const ownedRecovery =
    recovery.recoveryView.status === "OWNED" &&
    recovery.recoveryView.recovery.command.kind === "CREATE_PAID_ORDER_ITEM_RETURN"
      ? recovery.recoveryView
      : recovery.recoveryView.status === "NONE" ||
          recovery.recoveryView.status === "RECOVERY_STORAGE_UNAVAILABLE"
        ? recovery.recoveryView
        : ({ status: "OTHER_OPERATOR" } as const);
  return (
    <section className="paid-order-return-entry" aria-label="已结账商品或门店套餐退回">
      <header>
        <div>
          <span className="approval-kicker">已付款后顾客退回商品或整包套餐</span>
          <h2>申请已结账商品或套餐退回</h2>
          <p>
            先选原订单和业务批次，再按现场选择实物是否还能销售；门店套餐只能整包退回。提交申请不会立即退款、减少订单或处理库存。
          </p>
        </div>
      </header>
      {createdApproval?.snapshot.kind === "ORDER_ITEM_PAID_RETURN" ? (
        <output className="approval-info">
          <div>
            <strong>
              {createdApproval.snapshot.itemKind === "PACKAGE" ? "套餐整包" : "商品"}
              退回申请已提交，当前还没有退款、减少订单或处理库存
            </strong>
            <p>
              {createdApproval.snapshot.productName} · {fee(createdApproval.snapshot.amountFen)} ·{" "}
              {executionLabel(createdApproval)}
            </p>
          </div>
          <NavLink
            className="button secondary compact"
            to={`/review/${encodeURIComponent(createdApproval.approvalId)}?view=MINE`}
          >
            查看我的申请
          </NavLink>
        </output>
      ) : null}
      <ApprovalRecoveryNotice
        recoveryView={ownedRecovery}
        pending={recovery.mutation.isPending}
        onRetry={recovery.retry}
        onQuery={() => navigate("/review?view=MINE")}
      />
      {recovery.mutation.isError ? (
        <div className="approval-warning" role="alert">
          {failureMessage(recovery.mutation.error, "商品退回申请结果无法确认")}
        </div>
      ) : null}
      {candidates.isLoading ? (
        <div className="empty-state">正在读取最近已结账订单和可退商品或套餐</div>
      ) : candidates.isError ? (
        <div className="approval-warning" role="alert">
          已结账商品和套餐清单暂时无法安全读取，旧内容已隐藏。
          <button
            className="button secondary compact"
            type="button"
            onClick={() => candidates.refetch()}
          >
            重新读取
          </button>
        </div>
      ) : candidateOrders.length === 0 ? (
        <div className="empty-state">最近已结账订单中没有可办理退回的商品或门店套餐</div>
      ) : (
        <div className="paid-return-candidate-list">
          {candidateOrders.map((order) => (
            <article key={order.orderId}>
              <header>
                <div>
                  <strong>{order.orderNumber}</strong>
                  <small>
                    营业日 {order.businessDate} · {order.paymentMethodLabel} · 原收款{" "}
                    {fee(order.originalPaidFen)} · 已退 {fee(order.alreadyRefundedFen)}
                  </small>
                </div>
                <span>{order.canStartReturn ? "可以选择商品或套餐" : "当前不能发起"}</span>
              </header>
              {order.blocker ? <p className="form-hint">{order.blocker}</p> : null}
              <div className="paid-return-candidate-items">
                {order.items.map((item) => {
                  const key = paidReturnCandidateKey(order.orderId, item.itemId);
                  return (
                    <button
                      className={selectedKey === key ? "is-selected" : ""}
                      type="button"
                      key={item.itemId}
                      disabled={
                        !order.canStartReturn ||
                        !item.canReturn ||
                        recovery.recoveryView.status !== "NONE"
                      }
                      onClick={() => {
                        setSelectedKey(key);
                        setQuantity(1);
                        setReason("");
                        setPhysicalDisposition("SALEABLE_RETURNED");
                        setPhysicalReturnConfirmed(false);
                        setCreatedApproval(null);
                        previewMutation.reset();
                      }}
                    >
                      <strong>
                        {item.itemKind === "PACKAGE" ? "门店套餐 · " : ""}
                        {item.productName}
                      </strong>
                      <span>
                        {item.itemKind === "PACKAGE"
                          ? `整包 1 份 · ${item.returnableQuantity === 1 ? "可整包退回" : "已整包退回"}`
                          : `原售 ${item.soldQuantity} 件 · 已退 ${item.returnedQuantity} 件 · 可退 ${item.returnableQuantity} 件`}
                      </span>
                      {item.itemKind === "PACKAGE" && item.componentNames ? (
                        <small>整包内容：{item.componentNames.join("、")}</small>
                      ) : null}
                      <small>
                        单价 {fee(item.unitSalePriceFen)} · 最多退款{" "}
                        {fee(item.maximumRefundAmountFen)}
                      </small>
                      {item.blocker ? <small>{item.blocker}</small> : null}
                    </button>
                  );
                })}
              </div>
            </article>
          ))}
        </div>
      )}
      {selected ? (
        <form
          className="paid-return-approval-form"
          onSubmit={(event) => {
            event.preventDefault();
            if (inputError || !preview?.canSubmit || !previewMatches) return;
            recovery.submit({
              kind: "CREATE_PAID_ORDER_ITEM_RETURN",
              request: {
                orderId: selected.order.orderId,
                itemId: selected.item.itemId,
                quantity,
                physicalDisposition,
                reason: reason.trim(),
                idempotencyKey: crypto.randomUUID(),
              },
            });
          }}
        >
          <header>
            <div>
              <strong>
                {selected.item.itemKind === "PACKAGE" ? "整包退回门店套餐" : "退回商品"}“
                {selected.item.productName}”
              </strong>
              <p>
                原订单 {selected.order.orderNumber} · 原收款 {selected.order.paymentMethodLabel}
              </p>
            </div>
            <button
              className="button ghost compact"
              type="button"
              onClick={() => {
                setSelectedKey(null);
                previewMutation.reset();
              }}
            >
              取消
            </button>
          </header>
          {selected.item.itemKind === "PACKAGE" ? (
            <section className="paid-return-input-preview">
              <strong>退回范围：整包 1 份</strong>
              <span>全部商品和本单服务权益一次处理，不能只退其中一项。</span>
              {selected.item.lineNumber ? (
                <span>套餐业务单号：{selected.item.lineNumber}</span>
              ) : null}
            </section>
          ) : (
            <label>
              退回数量
              <select
                value={quantity}
                onChange={(event) => {
                  setQuantity(Number(event.target.value));
                  previewMutation.reset();
                }}
              >
                {paidReturnQuantityOptions(selected.item.returnableQuantity).map((option) => (
                  <option key={`${selected.item.itemId}:paid-return:${option}`} value={option}>
                    {option} 件
                  </option>
                ))}
              </select>
            </label>
          )}
          <fieldset className="sale-return-disposition-options">
            <legend>
              {selected.item.itemKind === "PACKAGE" ? "套餐内商品实物状态" : "退回实物状态"}
            </legend>
            <label>
              <input
                type="radio"
                name="paid-return-physical-disposition"
                value="SALEABLE_RETURNED"
                checked={physicalDisposition === "SALEABLE_RETURNED"}
                onChange={() => {
                  setPhysicalDisposition("SALEABLE_RETURNED");
                  setPhysicalReturnConfirmed(false);
                  previewMutation.reset();
                }}
              />
              <span>
                <strong>完好可再次销售</strong>
                <small>
                  {selected.item.itemKind === "PACKAGE"
                    ? "套餐内如有受管商品，均完好并已全部交回；退款并减少订单后按原销售成本恢复库存，本单服务权益同时撤销。"
                    : `商品未开封且状态完好；退款并减少订单后，可售库存增加 ${quantity} 件。`}
                </small>
              </span>
            </label>
            <label>
              <input
                type="radio"
                name="paid-return-physical-disposition"
                value="UNSALEABLE_RETURNED"
                checked={physicalDisposition === "UNSALEABLE_RETURNED"}
                onChange={() => {
                  setPhysicalDisposition("UNSALEABLE_RETURNED");
                  setPhysicalReturnConfirmed(false);
                  previewMutation.reset();
                }}
              />
              <span>
                <strong>已开封或破损，不可再次销售</strong>
                <small>
                  {selected.item.itemKind === "PACKAGE"
                    ? "套餐仍会整包原路退款并减少订单；套餐内商品可售库存不会增加，本单服务权益同时撤销。"
                    : "仍会原路退款并减少订单；可售库存不会增加，也不会再扣一次库存。"}
                </small>
              </span>
            </label>
          </fieldset>
          <label className="paid-return-physical-confirmation">
            <input
              type="checkbox"
              checked={physicalReturnConfirmed}
              onChange={(event) => {
                setPhysicalReturnConfirmed(event.target.checked);
                previewMutation.reset();
              }}
            />
            <span>
              {selected.item.itemKind === "PACKAGE"
                ? "我已确认套餐内应交回的商品均已交回门店，以上状态与现场一致"
                : "我已确认实物已经交回门店，以上实物状态与现场一致"}
            </span>
          </label>
          <label>
            退回原因（2–200 字）
            <textarea
              value={reason}
              rows={3}
              maxLength={200}
              placeholder="例如：顾客点错，商品未开封"
              onChange={(event) => {
                setReason(event.target.value);
                previewMutation.reset();
              }}
            />
            <small>{reasonLength}/200 字</small>
          </label>
          <section className="paid-return-input-preview">
            <strong>当前选择预计</strong>
            <span>原路退款：{fee(selected.item.unitSalePriceFen * quantity)}</span>
            {selected.item.itemKind === "PACKAGE" ? (
              <span>
                {physicalDisposition === "SALEABLE_RETURNED"
                  ? "全部受管商品按原销售成本恢复库存，本单服务权益同时撤销"
                  : "商品可售库存不增加，本单服务权益仍随整包撤销"}
              </span>
            ) : physicalDisposition === "SALEABLE_RETURNED" ? (
              <span>可售库存增加：+{quantity} 件</span>
            ) : (
              <span>可售库存：不增加，也不会再扣一次；记录为不可再次销售</span>
            )}
            <span>审批通过且安全执行成功后才会生效</span>
          </section>
          {inputError ? <p className="form-hint">还需修改：{inputError}</p> : null}
          {previewMutation.isError ? (
            <div className="approval-warning" role="alert">
              {failureMessage(previewMutation.error, "暂时无法检查商品退回影响")}
            </div>
          ) : null}
          {preview ? <PaidReturnApprovalPreview preview={preview} /> : null}
          <div className="approval-actions">
            <button
              className="button secondary"
              type="button"
              disabled={Boolean(inputError) || previewMutation.isPending}
              onClick={() => previewMutation.mutate()}
            >
              {previewMutation.isPending ? "正在检查…" : "检查退款、订单和库存影响"}
            </button>
            <button
              className="button primary"
              type="submit"
              disabled={
                recovery.mutation.isPending ||
                Boolean(inputError) ||
                !preview?.canSubmit ||
                !previewMatches
              }
            >
              提交{selected.item.itemKind === "PACKAGE" ? "套餐整包" : "商品"}退回审批
            </button>
          </div>
        </form>
      ) : null}
    </section>
  );
}

export function OrderCashRefundApprovalForm({
  order,
  operator,
  effectiveRoles,
  personalSession,
}: {
  order: OrderHistoryReadModel;
  operator: OperatorIdentity;
  effectiveRoles: readonly OperatorRole[];
  personalSession: boolean;
}): React.JSX.Element | null {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [amountYuan, setAmountYuan] = useState("");
  const [reason, setReason] = useState("");
  const [createdApproval, setCreatedApproval] = useState<ApprovalRequestReadModel | null>(null);
  const amountInputRef = useRef<HTMLInputElement>(null);
  const reasonInputRef = useRef<HTMLTextAreaElement>(null);
  const previewErrorRef = useRef<HTMLDivElement>(null);
  const mutationErrorRef = useRef<HTMLDivElement>(null);
  const amountFen = yuanInputToFen(amountYuan);
  const orderReference = orderDisplayReference({
    id: order.id,
    orderNumber: order.orderNumber,
  });
  const remainingFen = Math.max(0, order.paidFen - order.refundedFen);
  const relevantOrderStatus = order.status === "SETTLED" || order.status === "REFUNDED";
  const cashier =
    personalSession &&
    effectiveRoles.includes("CASHIER") &&
    !effectiveRoles.includes("OWNER") &&
    !effectiveRoles.includes("MANAGER");
  const onlyCash = order.payments.length === 1 && order.payments[0]?.method === "CASH";
  const previewMutation = useMutation({
    mutationFn: () => {
      if (amountFen === null) {
        throw new ApiRequestError("REQUEST_VALIDATION_FAILED", "退款金额格式不正确");
      }
      return previewOrderCashRefundApproval({
        orderId: order.id,
        amountFen,
        reason: reason.trim(),
      });
    },
  });
  const recovery = useApprovalMutationRecovery(operator, (approval, command) => {
    if (command.kind === "CREATE_ORDER_CASH_REFUND" && command.request.orderId === order.id) {
      setCreatedApproval(approval);
      setOpen(false);
      previewMutation.reset();
    }
  });
  const ownedRecovery =
    recovery.recoveryView.status === "OWNED" &&
    recovery.recoveryView.recovery.command.kind === "CREATE_ORDER_CASH_REFUND" &&
    recovery.recoveryView.recovery.command.request.orderId === order.id
      ? recovery.recoveryView
      : recovery.recoveryView.status === "NONE"
        ? recovery.recoveryView
        : ({ status: "OTHER_OPERATOR" } as const);
  useEffect(() => {
    if (previewMutation.isError) previewErrorRef.current?.focus();
  }, [previewMutation.isError]);
  useEffect(() => {
    if (recovery.mutation.isError) mutationErrorRef.current?.focus();
  }, [recovery.mutation.isError]);
  const ownedRefundRequest =
    ownedRecovery.status === "OWNED" &&
    ownedRecovery.recovery.command.kind === "CREATE_ORDER_CASH_REFUND"
      ? ownedRecovery.recovery.command.request
      : null;
  useEffect(() => {
    if (!ownedRefundRequest) return;
    setAmountYuan((ownedRefundRequest.amountFen / 100).toFixed(2));
    setReason(ownedRefundRequest.reason);
    setOpen(true);
  }, [ownedRefundRequest]);
  if (!cashier || !relevantOrderStatus || remainingFen <= 0) return null;
  if (!onlyCash) {
    return (
      <section className="refund-approval-unavailable">
        <strong>这笔订单不能在这里申请现金退款</strong>
        <p>
          原单不是单笔现金收款，或包含多笔、混合收款。微信、支付宝、会员余额和拆分收款需要按原渠道核对，不能当成单笔现金退回。
        </p>
      </section>
    );
  }

  const preview = previewMutation.data;
  const amountError =
    amountFen === null
      ? "请输入大于 0 的金额，最多保留两位小数"
      : amountFen > remainingFen
        ? `不能超过本单尚可退款的 ${fee(remainingFen)}`
        : null;
  const reasonError = reason.trim().length < 2 ? "请填写至少 2 个字的退款原因" : null;
  const inputError = amountError ?? reasonError;
  const amountHelpId = `refund-remaining-${order.id}`;
  const amountErrorId = `refund-amount-error-${order.id}`;
  const reasonHelpId = `refund-reason-help-${order.id}`;
  const reasonErrorId = `refund-reason-error-${order.id}`;
  const lockedByRecovery = ownedRecovery.status === "OWNED";

  return (
    <section className="refund-approval-entry">
      {createdApproval?.snapshot.kind === "ORDER_CASH_REFUND" ? (
        <output className="approval-info">
          <div>
            <strong>
              {createdApproval.execution.status === "SUCCEEDED"
                ? "已查到这笔申请的退款完成记录"
                : "退款申请已提交，当前不能当成退款完成"}
            </strong>
            <p>
              {fee(createdApproval.snapshot.amountFen)} · {executionLabel(createdApproval)}
            </p>
          </div>
          <NavLink
            className="button secondary compact"
            to={"/review/" + encodeURIComponent(createdApproval.approvalId) + "?view=MINE"}
          >
            查看我的申请
          </NavLink>
        </output>
      ) : null}
      {!open ? (
        <button
          className="button secondary refund-button"
          type="button"
          onClick={() => {
            setAmountYuan((remainingFen / 100).toFixed(2));
            setReason("");
            setCreatedApproval(null);
            setOpen(true);
          }}
        >
          申请现金退款
        </button>
      ) : (
        <form
          className="refund-approval-form"
          onSubmit={(event) => {
            event.preventDefault();
            if (inputError || !preview?.canSubmit || !amountFen) return;
            recovery.submit({
              kind: "CREATE_ORDER_CASH_REFUND",
              request: {
                orderId: order.id,
                amountFen,
                reason: reason.trim(),
                idempotencyKey: crypto.randomUUID(),
              },
            });
          }}
        >
          <header>
            <div>
              <strong>申请现金退款</strong>
              <p>
                {orderReference.label} {orderReference.value}
                。先检查原单和影响，再提交给店长或老板；提交申请不会立即退款。
              </p>
            </div>
            <button
              className="button ghost compact"
              type="button"
              disabled={lockedByRecovery}
              onClick={() => {
                setOpen(false);
                previewMutation.reset();
              }}
            >
              {lockedByRecovery ? "结果待确认，不能收起" : "收起"}
            </button>
          </header>
          <label>
            退款金额（元）
            <input
              ref={amountInputRef}
              inputMode="decimal"
              name={"refund-amount-" + order.id}
              value={amountYuan}
              maxLength={16}
              disabled={lockedByRecovery || recovery.mutation.isPending}
              onChange={(event) => {
                setAmountYuan(event.target.value);
                setCreatedApproval(null);
                previewMutation.reset();
              }}
              aria-invalid={amountError ? true : undefined}
              aria-describedby={[amountHelpId, amountError ? amountErrorId : null]
                .filter(Boolean)
                .join(" ")}
            />
            <small id={amountHelpId}>
              本单已收 {fee(order.paidFen)}，此前已退 {fee(order.refundedFen)}，尚可申请{" "}
              {fee(remainingFen)}
            </small>
            {amountError ? (
              <small className="refund-field-error" id={amountErrorId} role="alert">
                金额还需修改：{amountError}
              </small>
            ) : null}
          </label>
          <label>
            退款原因
            <textarea
              ref={reasonInputRef}
              name={"refund-reason-" + order.id}
              value={reason}
              rows={3}
              maxLength={500}
              disabled={lockedByRecovery || recovery.mutation.isPending}
              onChange={(event) => {
                setReason(event.target.value);
                setCreatedApproval(null);
                previewMutation.reset();
              }}
              placeholder="例如：顾客提前结束，按门店规则退还未消费部分"
              aria-invalid={reasonError ? true : undefined}
              aria-describedby={[reasonHelpId, reasonError ? reasonErrorId : null]
                .filter(Boolean)
                .join(" ")}
            />
            <small id={reasonHelpId}>请写明实际原因，至少 2 个字，审批人会在原单中看到。</small>
            {reasonError ? (
              <small className="refund-field-error" id={reasonErrorId} role="alert">
                原因还需修改：{reasonError}
              </small>
            ) : null}
          </label>
          {previewMutation.isError ? (
            <div className="approval-warning" ref={previewErrorRef} role="alert" tabIndex={-1}>
              {failureMessage(previewMutation.error, "暂时无法检查退款申请")}
            </div>
          ) : null}
          {preview ? <RefundApprovalPreview preview={preview} /> : null}
          <ApprovalRecoveryNotice
            recoveryView={ownedRecovery}
            pending={recovery.mutation.isPending}
            onRetry={recovery.retry}
            onQuery={() => navigate("/review?view=MINE")}
          />
          {recovery.mutation.isError ? (
            <div className="approval-warning" ref={mutationErrorRef} role="alert" tabIndex={-1}>
              {failureMessage(recovery.mutation.error, "申请结果无法确认")}
            </div>
          ) : null}
          <div className="approval-actions">
            <button
              className="button secondary"
              type="button"
              disabled={lockedByRecovery || previewMutation.isPending}
              onClick={() => {
                if (amountError) {
                  amountInputRef.current?.focus();
                  return;
                }
                if (reasonError) {
                  reasonInputRef.current?.focus();
                  return;
                }
                previewMutation.mutate();
              }}
            >
              {previewMutation.isPending ? "正在检查…" : "检查原单和影响"}
            </button>
            <button
              className="button primary"
              type="submit"
              disabled={
                recovery.mutation.isPending ||
                lockedByRecovery ||
                Boolean(inputError) ||
                !preview?.canSubmit ||
                preview.amountFen !== amountFen ||
                preview.reason !== reason.trim()
              }
            >
              提交审批申请
            </button>
          </div>
        </form>
      )}
    </section>
  );
}
